import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ChevronRight, Download, FileText, Loader2, Pencil, Plus, Search, Settings, Trash2, Upload } from 'lucide-react';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { PdfDrawer } from '../../../../../../components/ui/PdfDrawer';
import type { PdfTarget } from '../../../../../../components/ui/PdfDrawer';
import { DeleteConfirmationDialog } from '../../../../../../components/ui/DeleteConfirmationDialog';
import { MaterialData } from '../../../../types/materials';
import { cn } from '../../../../../../lib/utils';
import { formatLabel, StatusBadge, valueOrDash, getMaterialPipeLengthM } from './pipeMillReportUtils';
import { ProjectLimitsModal } from './components/ProjectLimitsModal';
import { PROJECT_LIMIT_FIELDS } from './config/projectLimitsConfig';
import { usePipeMillReport } from '../../../../../../redux/Project/pipeMillReport/hooks/usePipeMillReport';
import {
  ReportAdjudicationContext,
  ReportDetail,
} from './components/ReportDetail';

type FullReportType =
  | 'CHEMICAL'
  | 'TENSILE'
  | 'CVN'
  | 'AUT'
  | 'HYDROSTATIC'
  | 'DIMENSIONAL'
  | 'HARDNESS'
  | 'FLATTENING'
  | 'BEND'
  | 'VISUAL'
  | 'RT'
  | 'MPT'
  | 'HEAT_TREAT'
  | 'PIPE_BODY_UT'
  | 'MFL_ET'
  | 'COATING'
  | 'THREAD'
  | 'WELD_MAP';

type MockReport = {
  status: string;
  updated?: string | null;
  source?: string | null;
  note?: string;
};

type ReportDefinition = {
  key: FullReportType;
  label: string;
  freq: string;
  psl2Mandatory?: boolean;
};

type ReportMetadataEntry = {
  id?: number;
  metadataId?: number;
  reportType: FullReportType;
  heatNumber?: string | null;
  lotNumber?: string | null;
  pipeNumber?: string | null;
  reportId: string;
  performedAt: string;
  frequency: string;
  lab: string;
  technician: string;
  source: string;
  sourceFile: string;
  note: string;
  extraDetails?: Record<string, unknown> | null;
};

type ProjectLimitFieldOption = {
  fieldPath: string;
  symbol?: string;
  label: string;
  unit: string;
  limit: string;
  min?: number | null;
  max?: number | null;
};

type MeasurementItem = {
  id: string;
  reportType: FullReportType;
  fieldPath: string;
  fieldLabel: string;
  fieldSymbol?: string;
  measuredValue: string;
  unit: string;
  frequency: string;
  limit: string;
  source: string;
  reference: string;
  status: string;
  tensileSpecimenId?: string;
  tensileLocation?: string;
  tensileOrientation?: string;
  tensileAreaMm2?: string;
  tensileYsMpa?: string;
  tensileUtsMpa?: string;
  tensileElongationPct?: string;
  tensileYsUtsRatio?: string;
  cvnSpecimenId?: string;
  cvnLocation?: string;
  cvnNotchOrientation?: string;
  cvnSpecimenSize?: string;
  cvnEnergyJ?: string;
  cvnShearPct?: string;
  cvnLateralExpansionMm?: string;
  autIndicationId?: string;
  autPositionMm?: string;
  autClock?: string;
  autFlawType?: string;
  autDepthPctWt?: string;
  autLengthMm?: string;
  autDisposition?: string;
  hydroTestPressureBar?: string;
  hydroHoldDurationS?: string;
  hydroPressureDropObserved?: string;
  hydroLeakObserved?: string;
  hydroMedium?: string;
  hydroSealingMethod?: string;
  hydroGaugeCalRef?: string;
  hydroDesignFactor?: string;
  dimOdAvgMm?: string;
  dimOdMinMm?: string;
  dimOdMaxMm?: string;
  dimOvalityPct?: string;
  dimWtEnd1N?: string;
  dimWtEnd1E?: string;
  dimWtEnd1S?: string;
  dimWtEnd1W?: string;
  dimWtEnd2N?: string;
  dimWtEnd2E?: string;
  dimWtEnd2S?: string;
  dimWtEnd2W?: string;
  dimLengthM?: string;
  dimStraightnessMm?: string;
  dimBevelAngleDeg?: string;
  dimEndSquarenessMm?: string;
  flatteningSpecimenId?: string;
  flatteningWeldPosition?: string;
  flatteningOdBeforeMm?: string;
  flatteningHdReached?: string;
  flatteningLimit?: string;
  flatteningCracksObserved?: string;
  bendSpecimenId?: string;
  bendType?: string;
  bendAngleDeg?: string;
  bendCrackLengthMm?: string;
  bendFracture?: string;
  bendWallThicknessMm?: string;
  bendMultiplier?: string;
  bendRequiredFormerMm?: string;
  bendMillFormerMm?: string;
  hardnessZone?: string;
  hardnessReadingId?: string;
  hardnessPosition?: string;
  hardnessValueHv10?: string;
  hardnessProjectLimitHv10?: string;
  hardnessFallbackLimitHv10?: string;
  visualDefectId?: string;
  visualDefectType?: string;
  visualPositionMm?: string;
  visualClock?: string;
  visualDepthPctWt?: string;
  visualDisposition?: string;
  visualLightingLux?: string;
  rtIndicationId?: string;
  rtPositionMm?: string;
  rtFlawType?: string;
  rtLengthMm?: string;
  rtAreaMm2?: string;
  rtDisposition?: string;
  rtIqiSensitivityPct?: string;
  rtExposureClass?: string;
  mptIndicationId?: string;
  mptEnd?: string;
  mptType?: string;
  mptLengthMm?: string;
  mptDisposition?: string;
  mptFieldStrengthOe?: string;
  mptUvIrradiance?: string;
  heatBatchId?: string;
  heatAustenitisingTempC?: string;
  heatAustenitisingHoldMin?: string;
  heatCoolingRateCps?: string;
  heatTemperingTempC?: string;
  heatTemperingHoldMin?: string;
  heatPipesInBatch?: string;
  pbutIndicationId?: string;
  pbutIndicationType?: string;
  pbutPositionMm?: string;
  pbutClock?: string;
  pbutAreaMm2?: string;
  pbutDepthPctWt?: string;
  pbutEndZone?: string;
  pbutDisposition?: string;
  mflIndicationId?: string;
  mflFlawType?: string;
  mflSignalPctRef?: string;
  mflDisposition?: string;
  mflCalNotchPctWt?: string;
  mflRoute?: string;
  coatingType?: string;
  coatingDftMinUm?: string;
  coatingDftAvgUm?: string;
  coatingAdhesionNcm?: string;
  coatingCdRadiusMm?: string;
  coatingAnchorProfileUm?: string;
  coatingHolidayVoltageKv?: string;
  coatingHolidaysFound?: string;
  coatingCutbackLengthMm?: string;
  coatingSurfacePrepGrade?: string;
  threadType?: string;
  threadStandard?: string;
  threadGaugeSetRef?: string;
  threadEndTested?: string;
  threadCouplingApplied?: string;
  threadVisualDamage?: string;
  threadTaperMmPerM?: string;
  threadLeadMmCum?: string;
  threadHeightMm?: string;
  threadPipeEndDiameterMm?: string;
  weldPassName?: string;
  weldProcess?: string;
  weldWireHeatNo?: string;
  weldFluxLotNo?: string;
  weldCurrentA?: string;
  weldVoltageV?: string;
  weldSpeedMmMin?: string;
  weldHeatInputKjMm?: string;
  weldPlateId?: string;
  weldPlateHeatNumber?: string;
  weldPipeHeatNumber?: string;
  weldWpsRef?: string;
};

type MaterialPdfDocument = {
  docRef?: number | string;
  fileName: string;
  fileUrl: string;
  uploadedAt?: string | null;
};

type SavedPipeMaterialReport = {
  id?: number | string;
  reportId?: number | string;
  projectId?: number;
  pipeId?: number | null;
  heatNumber?: string | null;
  pipeNumber?: string | null;
  reportType?: FullReportType;
  report_type?: FullReportType;
  reportDetails?: MeasurementItem[];
  report_details?: MeasurementItem[];
  overallStatus?: string;
  overall_status?: string;
  extraDetails?: Record<string, unknown> | null;
  extra_details?: Record<string, unknown> | null;
  updatedAt?: string | null;
  updated_at?: string | null;
};

type ProjectLimitConfig = {
  fieldKey: string;
  min?: number | null;
  max?: number | null;
  minRaw?: string | null;
  maxRaw?: string | null;
  unit?: string | null;
  text: string;
};

type ProjectLimitLookup = Partial<Record<FullReportType, Record<string, ProjectLimitConfig>>>;

const PROJECT_LIMIT_FIELD_KEYS: Partial<Record<FullReportType, string[]>> = {
  TENSILE: ['ys_mpa', 'uts_mpa', 'elongation_pct', 'ys_uts_ratio'],
  CVN: ['avg_energy_j', 'single_energy_j', 'avg_shear_pct', 'test_temperature'],
  HYDROSTATIC: ['test_pressure_bar', 'hold_duration_s', 'pressure_drop_mpa', 'leakage_events'],
  DIMENSIONAL: ['outer_diameter', 'wall_thickness', 'length_m', 'ovality_pct', 'straightness_mm_m', 'bevel_angle_deg', 'end_squareness_mm'],
  HARDNESS: ['overall_max', 'body_hv10', 'weld_hv10', 'haz_hv10'],
  FLATTENING: ['flattening_height_h', 'crack_length_mm', 'lamination_observed'],
  BEND: ['bend_angle_deg', 'open_defect_mm', 'mandrel_diameter_t'],
  AUT: ['indication_depth_pct', 'indication_length_mm'],
  COATING: ['dft_min_um', 'holiday_voltage_kv', 'adhesion_n_cm', 'cd_radius_mm', 'anchor_profile_um'],
  PIPE_BODY_UT: ['acceptance_level', 'pbut_lamination_area_mm2', 'pbut_end_zone_depth_pct_wt', 'pbut_inclusion_area_mm2'],
  VISUAL: ['visual_lighting_lux', 'visual_depth_pct_wt', 'visual_arc_strike', 'visual_crack'],
  RT: ['rt_iqi_sensitivity_pct', 'rt_exposure_class', 'rt_crack', 'rt_porosity_length_mm', 'rt_slag_length_mm'],
  MPT: ['mpt_field_strength_oe', 'mpt_uv_irradiance_uw_cm2', 'mpt_linear_length_mm', 'mpt_rounded_length_mm'],
  HEAT_TREAT: ['austenitising_temp_c', 'austenitising_hold_min', 'cooling_rate_cps', 'tempering_temp_c', 'tempering_hold_min'],
  MFL_ET: ['mfl_signal_pct_ref', 'mfl_cal_notch_pct_wt'],
  THREAD: ['thread_taper_mm_m', 'thread_lead_mm_cum', 'thread_height_mm', 'thread_od_gauge_mm'],
  WELD_MAP: ['weld_heat_input_kj_mm', 'weld_plate_heat_match', 'weld_wps_ref'],
};

type StandardCheck = {
  key: string;
  label: string;
  value: number;
  unit: string;
  min?: number;
  max?: number;
  status: string;
};

type StandardObservation = {
  id: string;
  area: string;
  finding: string;
  disposition: string;
  status: string;
};

type StandardReportDetailData = {
  reportId: string;
  title: string;
  subtitle: string;
  status: string;
  performedAt: string;
  owner: string;
  station: string;
  source: string;
  sourceFile: string;
  spec: string;
  summary: string;
  checks: StandardCheck[];
  observations: StandardObservation[];
  limits: Array<[string, string]>;
};

interface PipeMaterialReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMaterial: MaterialData;
  selectedPipe: any;
  unit: string;
  availableLotNumbers?: string[];
  onReportsChanged?: () => void | Promise<void>;
  initialReportType?: string | null;
}

const REPORT_TYPES: ReportDefinition[] = [
  { key: 'CHEMICAL', label: 'Chemical', freq: 'per_heat' },
  { key: 'TENSILE', label: 'Tensile', freq: 'per_lot' },
  { key: 'CVN', label: 'CVN Impact', freq: 'per_lot', psl2Mandatory: true },
  { key: 'AUT', label: 'AUT Weld NDT', freq: 'per_pipe' },
  { key: 'HYDROSTATIC', label: 'Hydrostatic', freq: 'per_pipe' },
  { key: 'DIMENSIONAL', label: 'Dimensional', freq: 'per_pipe' },
  { key: 'HARDNESS', label: 'Hardness', freq: 'per_lot', psl2Mandatory: true },
  { key: 'FLATTENING', label: 'Flattening', freq: 'per_lot' },
  { key: 'BEND', label: 'Bend', freq: 'per_lot' },
  { key: 'VISUAL', label: 'Visual', freq: 'per_pipe' },
  { key: 'RT', label: 'Radiographic', freq: 'per_pipe' },
  { key: 'MPT', label: 'Magnetic Particle', freq: 'per_pipe' },
  { key: 'HEAT_TREAT', label: 'Heat Treatment', freq: 'per_batch' },
  { key: 'PIPE_BODY_UT', label: 'Pipe Body UT', freq: 'per_pipe', psl2Mandatory: true },
  { key: 'MFL_ET', label: 'MFL / ET', freq: 'per_pipe' },
  { key: 'COATING', label: 'Coating', freq: 'per_pipe' },
  { key: 'THREAD', label: 'Threads', freq: 'per_pipe' },
  { key: 'WELD_MAP', label: 'Weld Map', freq: 'per_pipe' },
];

const MOCK_REPORTS: Record<FullReportType, MockReport> = {
  CHEMICAL: { status: 'PASS', updated: '2024-11-14', source: 'PDF_DIGITAL', note: 'Heat analysis' },
  TENSILE: { status: 'PASS', updated: '2024-11-14', source: 'SPREADSHEET', note: 'Tensile values checked against project limits' },
  CVN: { status: 'PASS', updated: '2024-11-14', source: 'PDF_DIGITAL' },
  AUT: { status: 'PASS', updated: '2024-11-14', source: 'API_FEED', note: '2 indications, both accepted' },
  HYDROSTATIC: { status: 'PASS', updated: '2024-11-14', source: 'API_FEED' },
  DIMENSIONAL: { status: 'PASS', updated: '2024-11-14', source: 'API_FEED' },
  HARDNESS: { status: 'PENDING_REVIEW', updated: '2024-11-14', source: 'PDF_DIGITAL', note: 'Sour service limit not entered in ITP' },
  FLATTENING: { status: 'PASS', updated: '2024-11-14', source: 'PDF_DIGITAL' },
  BEND: { status: 'PASS', updated: '2024-11-14', source: 'PDF_DIGITAL' },
  VISUAL: { status: 'PASS', updated: '2024-11-15', source: 'MANUAL' },
  RT: { status: 'PASS', updated: '2024-11-14', source: 'PDF_DIGITAL' },
  MPT: { status: 'PASS', updated: '2024-11-15', source: 'PDF_DIGITAL' },
  HEAT_TREAT: { status: 'PASS', updated: '2024-11-13', source: 'API_FEED' },
  PIPE_BODY_UT: { status: 'PASS', updated: '2024-11-14', source: 'API_FEED' },
  MFL_ET: { status: 'INCOMPLETE', updated: null, source: null, note: 'N/A - SAWL pipe' },
  COATING: { status: 'INCOMPLETE', updated: null, source: null, note: 'Awaiting coating-plant transfer' },
  THREAD: { status: 'INCOMPLETE', updated: null, source: null, note: 'N/A - bevelled-end pipe' },
  WELD_MAP: { status: 'PASS', updated: '2024-11-14', source: 'API_FEED' },
};

const CHEMICAL_DETAIL = {
  reportId: 'RPT-CHEM-J4K5272',
  performedAt: '2024-11-14 10:22',
  lab: 'Severstal Mill Lab - Spectrometer 2',
  technician: 'A. Sokolov',
  spec: 'API 5L Sec. 9.2 / ISO 14284',
  sourceFile: 'MTC-J4K5272-chemical.pdf',
  sample: {
    id: 'HEAT-J4K-5272-A',
    type: 'ladle_analysis',
    takenAt: '2024-11-14 09:58',
    furnace: 'BOF-3',
    castNo: 'C-2024-5272',
  },
  elements: [
    { symbol: 'C', name: 'Carbon', value: 0.084, unit: '%', max: 0.12, status: 'PASS' },
    { symbol: 'Mn', name: 'Manganese', value: 1.42, unit: '%', max: 1.65, status: 'PASS' },
    { symbol: 'P', name: 'Phosphorus', value: 0.018, unit: '%', max: 0.02, status: 'PASS' },
    { symbol: 'S', name: 'Sulfur', value: 0.006, unit: '%', max: 0.01, status: 'PASS' },
    { symbol: 'Si', name: 'Silicon', value: 0.24, unit: '%', max: 0.45, status: 'PASS' },
    { symbol: 'Nb', name: 'Niobium', value: 0.041, unit: '%', max: 0.06, status: 'PASS' },
    { symbol: 'V', name: 'Vanadium', value: 0.036, unit: '%', max: 0.08, status: 'PASS' },
    { symbol: 'Ti', name: 'Titanium', value: 0.014, unit: '%', max: 0.04, status: 'PASS' },
    { symbol: 'N', name: 'Nitrogen', value: 0.006, unit: '%', max: 0.012, status: 'PASS' },
  ],
  calculations: [
    { key: 'CE_IIW', label: 'Carbon equivalent IIW', value: 0.37, max: 0.42, formula: 'C + Mn/6 + (Cr+Mo+V)/5 + (Ni+Cu)/15', status: 'PASS' },
    { key: 'Pcm', label: 'Crack susceptibility', value: 0.18, max: 0.22, formula: 'C + Si/30 + Mn/20 + Cu/20 + Ni/60 + Cr/20 + Mo/15 + V/10 + 5B', status: 'PASS' },
  ],
};

const createEmptyDbReport = (): MockReport => ({
  status: 'INCOMPLETE',
  updated: null,
  source: null,
  note: '',
});

const TENSILE_DETAIL = {
  reportId: 'RPT-TEN-2024-11-1187',
  performedAt: '2024-11-14 14:08',
  lab: 'Severstal Mill Lab - Bay 3',
  technician: 'K. Volkov',
  spec: 'API 5L Sec. 9.3 / ISO 6892-1',
  sourceFile: 'tensile-results-2024-11-14.xlsx',
  testTempC: 22,
  specimens: [
    { id: 'S1', location: 'pipe_body', orientation: 'transverse', pieceType: 'strip', area: 128.5, ys: 489, uts: 612, elongation: 24.8, ratio: 0.799, fractureLoc: 'mid_metal', status: 'PASS' },
    { id: 'S2', location: 'weld_seam', orientation: 'transverse', pieceType: 'strip', area: 130.2, ys: 524, uts: 568, elongation: 22.1, ratio: 0.923, fractureLoc: 'weld_metal', status: 'PASS' },
    { id: 'S3', location: 'haz', orientation: 'transverse', pieceType: 'strip', area: 129.0, ys: 498, uts: 605, elongation: 23.4, ratio: 0.823, fractureLoc: 'haz', status: 'PASS' },
  ],
};

const CVN_DETAIL = {
  reportId: 'RPT-CVN-2024-11-LOT238',
  performedAt: '2024-11-14 16:35',
  lab: 'Severstal Mill Lab - Impact Bay',
  technician: 'N. Orlov',
  spec: 'API 5L Sec. 9.8 / ASTM E23',
  sourceFile: 'CVN-lot238.pdf',
  testTempC: -10,
  requirements: { avgEnergyJ: 60, singleEnergyJ: 45, shearPct: 75 },
  sets: [
    {
      id: 'CVN-BODY',
      location: 'pipe_body',
      specimens: [
        { id: 'B1', energy: 88, shear: 92, lateralExpansion: 1.18, status: 'PASS' },
        { id: 'B2', energy: 94, shear: 95, lateralExpansion: 1.24, status: 'PASS' },
        { id: 'B3', energy: 91, shear: 94, lateralExpansion: 1.21, status: 'PASS' },
      ],
    },
    {
      id: 'CVN-WELD',
      location: 'weld_seam',
      specimens: [
        { id: 'W1', energy: 76, shear: 84, lateralExpansion: 1.03, status: 'PASS' },
        { id: 'W2', energy: 72, shear: 82, lateralExpansion: 0.98, status: 'PASS' },
        { id: 'W3', energy: 79, shear: 86, lateralExpansion: 1.07, status: 'PASS' },
      ],
    },
    {
      id: 'CVN-HAZ',
      location: 'haz',
      specimens: [
        { id: 'H1', energy: 68, shear: 80, lateralExpansion: 0.93, status: 'PASS' },
        { id: 'H2', energy: 71, shear: 82, lateralExpansion: 0.96, status: 'PASS' },
        { id: 'H3', energy: 66, shear: 78, lateralExpansion: 0.91, status: 'PASS' },
      ],
    },
  ],
};

const BEND_DETAIL = {
  reportId: 'RPT-BEND-2024-11-LOT238',
  performedAt: '2024-11-14 17:05',
  lab: 'Severstal Mill Lab - Mechanical Bay',
  technician: 'K. Volkov',
  spec: 'API 5L Sec. 9.3 / ISO 5173',
  sourceFile: 'bend-results-2024-11-14.pdf',
  requirements: {
    bendAngleDeg: 180,
    maxOpenDefectMm: 3.0,
    mandrelDiameterT: 4,
  },
  specimens: [
    { id: 'FB-1', type: 'face_bend', rootOrFace: 'face', surface: 'weld face', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.0, status: 'PASS' },
    { id: 'FB-2', type: 'face_bend', rootOrFace: 'face', surface: 'weld face', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.2, status: 'PASS' },
    { id: 'FB-3', type: 'face_bend', rootOrFace: 'face', surface: 'weld face', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.0, status: 'PASS' },
    { id: 'FB-4', type: 'face_bend', rootOrFace: 'face', surface: 'weld face', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.4, status: 'PASS' },
    { id: 'RB-1', type: 'root_bend', rootOrFace: 'root', surface: 'weld root', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.0, status: 'PASS' },
    { id: 'RB-2', type: 'root_bend', rootOrFace: 'root', surface: 'weld root', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.1, status: 'PASS' },
    { id: 'RB-3', type: 'root_bend', rootOrFace: 'root', surface: 'weld root', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.0, status: 'PASS' },
    { id: 'RB-4', type: 'root_bend', rootOrFace: 'root', surface: 'weld root', angleDeg: 180, mandrelDiameterT: 4, maxOpenDefectMm: 0.3, status: 'PASS' },
  ],
};

const HARDNESS_DETAIL = {
  reportId: 'RPT-HARD-2024-11-LOT238',
  performedAt: '2024-11-14 17:40',
  lab: 'Severstal Mill Lab - Mechanical Bay',
  technician: 'N. Orlov',
  spec: 'API 5L Annex H / ISO 6507-1',
  sourceFile: 'hardness-map-2024-11-14.pdf',
  method: 'HV10',
  requirements: {
    sourService: true,
    overallMaxHv10: 220,
    standardMaxHv10: 250,
  },
  locations: [
    {
      id: 'HB-BODY',
      location: 'pipe_body',
      readings: [
        { id: 'B1', position: 'body 3 o clock', valueHv10: 179, status: 'PASS' },
        { id: 'B2', position: 'body 6 o clock', valueHv10: 188, status: 'PASS' },
        { id: 'B3', position: 'body 9 o clock', valueHv10: 196, status: 'PASS' },
      ],
    },
    {
      id: 'HB-WELD',
      location: 'weld_seam',
      readings: [
        { id: 'W1', position: 'weld cap', valueHv10: 218, status: 'PASS' },
        { id: 'W2', position: 'weld root', valueHv10: 224, status: 'PENDING_REVIEW' },
        { id: 'W3', position: 'weld mid-wall', valueHv10: 238, status: 'PENDING_REVIEW' },
      ],
    },
    {
      id: 'HB-HAZ',
      location: 'haz',
      readings: [
        { id: 'H1', position: 'HAZ left', valueHv10: 229, status: 'PENDING_REVIEW' },
        { id: 'H2', position: 'HAZ right', valueHv10: 216, status: 'PASS' },
        { id: 'H3', position: 'HAZ toe', valueHv10: 221, status: 'PENDING_REVIEW' },
      ],
    },
  ],
};

const AUT_DETAIL = {
  reportId: 'RPT-AUT-2024-11-LOT238',
  performedAt: '2024-11-14 18:10',
  station: 'AUT Station 4',
  operator: 'M. Petrov',
  spec: 'API 5L Annex E / DNV-ST-F101',
  sourceFile: 'aut-weld-scan-2024-11-14.pdf',
  acceptanceLevel: 'N2',
  calibration: {
    block: 'CRC-AUT-BLK-12',
    referenceReflector: '3.0 mm SDH',
    sensitivityDb: 6,
    lastVerified: '2024-11-14 08:15',
    status: 'PASS',
  },
  limits: {
    maxDepthPct: 8,
    maxLengthMm: 25,
    maxAmplitudePct: 60,
  },
  coverage: [
    { channel: 'CH-01', zone: 'weld cap', coveragePct: 100, status: 'PASS' },
    { channel: 'CH-02', zone: 'weld root', coveragePct: 100, status: 'PASS' },
    { channel: 'CH-03', zone: 'HAZ left/right', coveragePct: 100, status: 'PASS' },
  ],
  indications: [
    { id: 'IND-01', flawType: 'porosity', posMm: 2440, clock: '3:00', depthPct: 4.2, lengthMm: 12, amplitudePct: 42, status: 'PASS' },
    { id: 'IND-02', flawType: 'slag line', posMm: 6090, clock: '9:30', depthPct: 5.8, lengthMm: 18, amplitudePct: 48, status: 'PASS' },
  ],
};

const DIMENSIONAL_DETAIL = {
  reportId: 'RPT-DIM-2024-11-PIPE9904',
  performedAt: '2024-11-14 09:15',
  station: 'Dimensional Bench 2',
  inspector: 'A. Morozov',
  spec: 'API 5L Sec. 9.11 / Project ITP',
  sourceFile: 'dimensional-checks-R9904.pdf',
  checks: [
    { key: 'OD', label: 'Outside diameter', location: 'pipe body', value: 660.6, unit: 'mm', min: 658.8, max: 662.0, status: 'PASS' },
    { key: 'WT', label: 'Wall thickness', location: 'min scan', value: 22.4, unit: 'mm', min: 21.5, max: 23.1, status: 'PASS' },
    { key: 'LEN', label: 'Pipe length', location: 'end-to-end', value: 12.18, unit: 'm', min: 12.0, max: 12.4, status: 'PASS' },
    { key: 'OVAL', label: 'Ovality', location: 'pipe body', value: 0.42, unit: '%', max: 1.0, status: 'PASS' },
    { key: 'STR', label: 'Straightness', location: 'full pipe', value: 1.8, unit: 'mm/m', max: 3.0, status: 'PASS' },
    { key: 'BEV', label: 'Bevel angle', location: 'end prep', value: 30.2, unit: 'deg', min: 29.5, max: 30.5, status: 'PASS' },
  ],
};

const FLATTENING_DETAIL = {
  reportId: 'RPT-FLAT-2024-11-LOT238',
  performedAt: '2024-11-14 18:45',
  lab: 'Severstal Mill Lab - Mechanical Bay',
  technician: 'K. Volkov',
  spec: 'API 5L Sec. 9.3 / ISO 8492',
  sourceFile: 'flattening-results-2024-11-14.pdf',
  requirements: {
    position1DistanceHMm: 410,
    position2DistanceHMm: 235,
    crackMaxMm: 0,
    laminationAllowed: false,
  },
  specimens: [
    { id: 'FL-1', orientation: 'weld_0_deg', position: 'first close', surface: 'weld at 0 deg', hMm: 410.0, crackMm: 0.0, lamination: false, status: 'PASS' },
    { id: 'FL-2', orientation: 'weld_90_deg', position: 'first close', surface: 'weld at 90 deg', hMm: 408.5, crackMm: 0.0, lamination: false, status: 'PASS' },
    { id: 'FL-3', orientation: 'weld_0_deg', position: 'final close', surface: 'weld at 0 deg', hMm: 235.0, crackMm: 0.0, lamination: false, status: 'PASS' },
    { id: 'FL-4', orientation: 'weld_90_deg', position: 'final close', surface: 'weld at 90 deg', hMm: 236.2, crackMm: 0.0, lamination: false, status: 'PASS' },
  ],
};

const HYDROSTATIC_DETAIL = {
  reportId: 'RPT-HYDRO-2024-11-PIPE9904',
  performedAt: '2024-11-14 11:20',
  station: 'Hydro Bay 1',
  operator: 'I. Smirnov',
  spec: 'API 5L Sec. 9.4 / Project ITP',
  sourceFile: 'hydrostatic-pressure-hold-R9904.pdf',
  testMedium: 'treated water',
  requirements: {
    minPressureMpa: 18.6,
    holdDurationS: 30,
    maxPressureDropMpa: 0.2,
    leakageAllowed: false,
  },
  readings: [
    { phase: 'Fill / vent', timeS: 0, pressureMpa: 0.4, status: 'PASS' },
    { phase: 'Ramp up', timeS: 18, pressureMpa: 12.5, status: 'PASS' },
    { phase: 'Peak pressure', timeS: 32, pressureMpa: 19.2, status: 'PASS' },
    { phase: 'Hold start', timeS: 35, pressureMpa: 19.1, status: 'PASS' },
    { phase: 'Hold end', timeS: 65, pressureMpa: 19.0, status: 'PASS' },
    { phase: 'Depressurize', timeS: 90, pressureMpa: 0.0, status: 'PASS' },
  ],
  checks: [
    { key: 'TEST_PRESSURE', label: 'Test pressure', value: 19.2, unit: 'MPa', min: 18.6, status: 'PASS' },
    { key: 'HOLD_TIME', label: 'Hold duration', value: 30, unit: 's', min: 30, status: 'PASS' },
    { key: 'PRESSURE_DROP', label: 'Pressure drop during hold', value: 0.1, unit: 'MPa', max: 0.2, status: 'PASS' },
    { key: 'LEAKAGE', label: 'Leakage events', value: 0, unit: 'events', max: 0, status: 'PASS' },
  ],
};

const STANDARD_REPORT_DETAILS: Partial<Record<FullReportType, StandardReportDetailData>> = {
  VISUAL: {
    reportId: 'RPT-VIS-2024-11-1187',
    title: 'Visual',
    subtitle: 'external / internal visual inspection',
    status: 'PASS',
    performedAt: '2024-11-15 09:10',
    owner: 'QA Inspector L. Moroz',
    station: 'Final Inspection Line',
    source: 'MANUAL',
    sourceFile: 'visual-inspection-30858.pdf',
    spec: 'API 5L Sec. 9.10 / ITP 8',
    summary: 'No dents, arc burns, laminations, bevel damage, or marking defects observed.',
    checks: [
      { key: 'SURFACE', label: 'External surface condition', value: 0, unit: 'defects', max: 0, status: 'PASS' },
      { key: 'ENDS', label: 'End face / bevel condition', value: 0, unit: 'defects', max: 0, status: 'PASS' },
      { key: 'MARKING', label: 'Stencil and traceability marking', value: 1, unit: 'verified', min: 1, status: 'PASS' },
      { key: 'CLEANLINESS', label: 'Internal cleanliness', value: 1, unit: 'verified', min: 1, status: 'PASS' },
    ],
    observations: [
      { id: 'VIS-1', area: 'Body', finding: 'No surface discontinuities', disposition: 'Accept', status: 'PASS' },
      { id: 'VIS-2', area: 'Bevel end 1', finding: 'Bevel clean, no handling damage', disposition: 'Accept', status: 'PASS' },
      { id: 'VIS-3', area: 'Marking', finding: 'Pipe number, heat, grade readable', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['Surface defects', '0 rejectable'], ['Traceability marking', 'required'], ['End preparation', 'no visible damage']],
  },
  RT: {
    reportId: 'RPT-RT-2024-11-1187',
    title: 'Radiographic',
    subtitle: 'weld seam radiography',
    status: 'PASS',
    performedAt: '2024-11-14 18:25',
    owner: 'RT Level II A. Belov',
    station: 'RT Bunker 3',
    source: 'PDF_DIGITAL',
    sourceFile: 'RT-30858-weld.pdf',
    spec: 'API 5L Annex E / ISO 10893-6',
    summary: 'Radiographic shots accepted. No rejectable lack of fusion, cracks, or clustered porosity.',
    checks: [
      { key: 'SENSITIVITY', label: 'Image quality sensitivity', value: 1.6, unit: '%', max: 2.0, status: 'PASS' },
      { key: 'DENSITY', label: 'Film density', value: 2.8, unit: 'D', min: 2.0, max: 4.0, status: 'PASS' },
      { key: 'REJECTS', label: 'Rejectable indications', value: 0, unit: 'count', max: 0, status: 'PASS' },
    ],
    observations: [
      { id: 'RT-01', area: 'Weld 0-120 deg', finding: 'No relevant indication', disposition: 'Accept', status: 'PASS' },
      { id: 'RT-02', area: 'Weld 120-240 deg', finding: 'Isolated pore below limit', disposition: 'Accept', status: 'PASS' },
      { id: 'RT-03', area: 'Weld 240-360 deg', finding: 'No relevant indication', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['IQI sensitivity', '<= 2.0 %'], ['Film density', '2.0-4.0 D'], ['Rejectable indications', '0']],
  },
  MPT: {
    reportId: 'RPT-MPT-2024-11-1187',
    title: 'Magnetic Particle',
    subtitle: 'surface crack detection',
    status: 'PASS',
    performedAt: '2024-11-15 08:40',
    owner: 'NDT Level II S. Antonov',
    station: 'MPI Bench 1',
    source: 'PDF_DIGITAL',
    sourceFile: 'MPT-30858.pdf',
    spec: 'ISO 10893-5 / ASTM E709',
    summary: 'Magnetic particle examination found no relevant linear indications.',
    checks: [
      { key: 'FIELD', label: 'Magnetic field strength', value: 3.2, unit: 'kA/m', min: 2.4, max: 4.0, status: 'PASS' },
      { key: 'LIFT', label: 'Yoke lift test', value: 4.6, unit: 'kg', min: 4.5, status: 'PASS' },
      { key: 'INDICATIONS', label: 'Relevant indications', value: 0, unit: 'count', max: 0, status: 'PASS' },
    ],
    observations: [
      { id: 'MPT-1', area: 'Weld cap', finding: 'No linear indication', disposition: 'Accept', status: 'PASS' },
      { id: 'MPT-2', area: 'HAZ left', finding: 'No relevant indication', disposition: 'Accept', status: 'PASS' },
      { id: 'MPT-3', area: 'HAZ right', finding: 'No relevant indication', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['Linear indications', '0 rejectable'], ['Yoke lift', '>= 4.5 kg'], ['Field strength', '2.4-4.0 kA/m']],
  },
  HEAT_TREAT: {
    reportId: 'RPT-HT-2024-11-FB014',
    title: 'Heat Treatment',
    subtitle: 'furnace batch cycle verification',
    status: 'PASS',
    performedAt: '2024-11-13 23:30',
    owner: 'Furnace Operator V. Smirnov',
    station: 'Furnace FB-014',
    source: 'API_FEED',
    sourceFile: 'heat-treatment-FB014.json',
    spec: 'ITP 5 / TMCP route card',
    summary: 'Furnace batch FB-014 completed within qualified thermal cycle window.',
    checks: [
      { key: 'SOAK_TEMP', label: 'Soak temperature', value: 612, unit: 'C', min: 600, max: 625, status: 'PASS' },
      { key: 'SOAK_TIME', label: 'Soak duration', value: 42, unit: 'min', min: 35, max: 55, status: 'PASS' },
      { key: 'COOL_RATE', label: 'Cooling rate', value: 18, unit: 'C/min', min: 12, max: 25, status: 'PASS' },
    ],
    observations: [
      { id: 'HT-1', area: 'Zone 1', finding: 'Within thermal envelope', disposition: 'Accept', status: 'PASS' },
      { id: 'HT-2', area: 'Zone 2', finding: 'Within thermal envelope', disposition: 'Accept', status: 'PASS' },
      { id: 'HT-3', area: 'Quench exit', finding: 'Cooling rate in range', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['Soak temperature', '600-625 C'], ['Soak duration', '35-55 min'], ['Cooling rate', '12-25 C/min']],
  },
  PIPE_BODY_UT: {
    reportId: 'RPT-PBUT-2024-11-1187',
    title: 'Pipe Body UT',
    subtitle: 'lamination and body wall ultrasonic scan',
    status: 'PASS',
    performedAt: '2024-11-14 12:55',
    owner: 'UT Level II P. Fedorov',
    station: 'Body UT Line 1',
    source: 'API_FEED',
    sourceFile: 'pipe-body-ut-2024-11-14.csv',
    spec: 'API 5L PSL2 / ISO 10893-8',
    summary: '100% body UT completed. No lamination indications above U2 acceptance level.',
    checks: [
      { key: 'COVERAGE', label: 'Scan coverage', value: 100, unit: '%', min: 100, status: 'PASS' },
      { key: 'MIN_WT', label: 'Minimum verified wall', value: 22.1, unit: 'mm', min: 21.5, status: 'PASS' },
      { key: 'REJECTS', label: 'Rejectable laminations', value: 0, unit: 'count', max: 0, status: 'PASS' },
    ],
    observations: [
      { id: 'PBUT-1', area: 'Body strip A', finding: 'No relevant lamination', disposition: 'Accept', status: 'PASS' },
      { id: 'PBUT-2', area: 'Body strip B', finding: 'No relevant lamination', disposition: 'Accept', status: 'PASS' },
      { id: 'PBUT-3', area: 'End zones', finding: 'Coverage complete', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['Acceptance level', 'U2'], ['Coverage', '100 %'], ['Rejectable laminations', '0']],
  },
  MFL_ET: {
    reportId: 'RPT-MFLET-2024-11-1187',
    title: 'MFL / ET',
    subtitle: 'flux leakage / eddy-current applicability record',
    status: 'INCOMPLETE',
    performedAt: 'not scheduled',
    owner: 'NDT Planning',
    station: 'N/A for SAWL route',
    source: 'MANUAL',
    sourceFile: 'route-applicability-30858.json',
    spec: 'ITP applicability matrix',
    summary: 'MFL / ET is configured for ERW route only. Current pipe type is SAWL, so this report is not applicable.',
    checks: [
      { key: 'APPLIES', label: 'Applies to pipe route', value: 0, unit: 'flag', max: 0, status: 'INCOMPLETE' },
      { key: 'ROUTE', label: 'Pipe route SAWL', value: 1, unit: 'verified', min: 1, status: 'PASS' },
    ],
    observations: [
      { id: 'MFL-NA', area: 'Route matrix', finding: 'SAWL pipe does not require MFL / ET', disposition: 'N/A', status: 'INCOMPLETE' },
    ],
    limits: [['Applicability', 'ERW only'], ['Current route', 'SAWL'], ['Clearance impact', 'not required']],
  },
  COATING: {
    reportId: 'RPT-COAT-2024-11-1187',
    title: 'Coating',
    subtitle: 'downstream coating transfer placeholder',
    status: 'INCOMPLETE',
    performedAt: 'awaiting coating plant',
    owner: 'Coating Plant Interface',
    station: 'External applicator',
    source: 'MANUAL',
    sourceFile: 'coating-transfer-pending.json',
    spec: 'ITP 11.1 / project coating spec',
    summary: 'Coating record is expected from downstream applicator. Current pipe is blocked until transfer arrives.',
    checks: [
      { key: 'TRANSFER', label: 'Coating record received', value: 0, unit: 'flag', min: 1, status: 'INCOMPLETE' },
      { key: 'DFT_MIN', label: 'Minimum DFT', value: 0, unit: 'um', min: 450, status: 'INCOMPLETE' },
      { key: 'HOLIDAY', label: 'Holiday test defects', value: 0, unit: 'count', max: 0, status: 'INCOMPLETE' },
    ],
    observations: [
      { id: 'COAT-1', area: 'Applicator transfer', finding: 'Record not yet received', disposition: 'Awaiting transfer', status: 'INCOMPLETE' },
    ],
    limits: [['Minimum DFT', '>= 450 um'], ['Holiday defects', '0'], ['Record source', 'coating plant']],
  },
  THREAD: {
    reportId: 'RPT-THD-2024-11-1187',
    title: 'Threads',
    subtitle: 'end connection applicability record',
    status: 'INCOMPLETE',
    performedAt: 'not scheduled',
    owner: 'Finishing Planning',
    station: 'N/A for bevelled-end SAWL',
    source: 'MANUAL',
    sourceFile: 'thread-applicability-30858.json',
    spec: 'ITP end finish matrix',
    summary: 'Thread inspection is not applicable because this project uses bevelled-end SAWL line pipe.',
    checks: [
      { key: 'END_TYPE', label: 'Bevelled end selected', value: 1, unit: 'verified', min: 1, status: 'PASS' },
      { key: 'THREAD_REQ', label: 'Threading required', value: 0, unit: 'flag', max: 0, status: 'INCOMPLETE' },
    ],
    observations: [
      { id: 'THD-NA', area: 'End finish', finding: 'No threaded connection required', disposition: 'N/A', status: 'INCOMPLETE' },
    ],
    limits: [['Project end type', 'bevelled'], ['Thread inspection', 'not applicable'], ['Clearance impact', 'not required']],
  },
  WELD_MAP: {
    reportId: 'RPT-WMAP-2024-11-1187',
    title: 'Weld Map',
    subtitle: 'weld seam traceability and repair map',
    status: 'PASS',
    performedAt: '2024-11-14 09:20',
    owner: 'Weld Data System',
    station: 'SAWL Line 2',
    source: 'API_FEED',
    sourceFile: 'weld-map-30858.json',
    spec: 'ITP 6.2 / weld traceability procedure',
    summary: 'Weld seam, coil/plate traceability, WPS, and repair status linked for this pipe.',
    checks: [
      { key: 'WPS', label: 'Approved WPS linked', value: 1, unit: 'verified', min: 1, status: 'PASS' },
      { key: 'SEAM_LEN', label: 'Mapped weld length', value: 12.18, unit: 'm', min: 12.0, status: 'PASS' },
      { key: 'REPAIRS', label: 'Open weld repairs', value: 0, unit: 'count', max: 0, status: 'PASS' },
      { key: 'TRACE', label: 'Plate-to-pipe trace link', value: 1, unit: 'verified', min: 1, status: 'PASS' },
    ],
    observations: [
      { id: 'WM-1', area: 'Longitudinal seam', finding: 'WPS-SAWL-X65-22 linked', disposition: 'Accept', status: 'PASS' },
      { id: 'WM-2', area: 'Repair register', finding: 'No open repairs', disposition: 'Accept', status: 'PASS' },
      { id: 'WM-3', area: 'Traceability', finding: 'Plate P-5272-44 linked to pipe', disposition: 'Accept', status: 'PASS' },
    ],
    limits: [['Approved WPS', 'required'], ['Open repairs', '0'], ['Traceability link', 'required']],
  },
};

const getFirstMaterialPdfDocument = (selectedMaterial: MaterialData): MaterialPdfDocument | null => {
  const documents = (selectedMaterial as any)?.documents;
  if (!Array.isArray(documents)) return null;

  for (const documentGroup of documents) {
    const nestedDocuments = documentGroup?.materials;
    if (Array.isArray(nestedDocuments)) {
      const match = nestedDocuments.find((document: any) => document?.fileUrl);
      if (match) {
        return {
          docRef: documentGroup?.docRef ?? match.docRef ?? match.materialTraceabilityId,
          fileName: match.fileName || 'Material document',
          fileUrl: match.fileUrl,
          uploadedAt: match.uploadedAt || match.createdAt || null,
        };
      }
    }

    if (documentGroup?.fileUrl) {
      return {
        docRef: documentGroup.docRef ?? documentGroup.id,
        fileName: documentGroup.fileName || 'Material document',
        fileUrl: documentGroup.fileUrl,
        uploadedAt: documentGroup.uploadedAt || null,
      };
    }
  }

  return null;
};

const FREQUENCY_OPTIONS = ['per_pipe', 'per_heat', 'per_lot', 'per_shift', 'per_batch'];
const FREQUENCY_LABELS: Record<string, string> = {
  per_pipe: 'Per pipe',
  per_heat: 'Per heat',
  per_lot: 'Per lot',
  per_shift: 'Per shift',
  per_batch: 'Per batch',
};

const formatFrequencyLabel = (frequency: string): string =>
  FREQUENCY_LABELS[frequency] || frequency.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const REPORT_SCOPE_LABELS: Record<string, string> = {
  per_pipe: 'pipe',
  per_heat: 'heat',
  per_lot: 'lot',
  per_shift: 'shift',
  per_batch: 'batch',
};

const getReportScopeLabel = (frequency?: string): string =>
  REPORT_SCOPE_LABELS[frequency || ''] || 'pipe';

const getExtraDetails = (value: { extraDetails?: Record<string, unknown> | null; extra_details?: Record<string, unknown> | null }): Record<string, unknown> =>
  (value.extraDetails || value.extra_details || {}) as Record<string, unknown>;

const getLotNumberFromExtraDetails = (extraDetails: Record<string, unknown>): string =>
  String(
    extraDetails.pipeLotNumber ??
    extraDetails.lotNumber ??
    extraDetails.lotNo ??
    extraDetails.lot_no ??
    extraDetails.batchNumber ??
    ''
  ).trim();

const getFieldOptionsForReport = (): ProjectLimitFieldOption[] => [
  { fieldPath: 'measured_value', label: 'Measured value', unit: '', limit: 'Project limit not configured' },
];

const CHEMICAL_DERIVED_FIELD_PATHS = new Set(['ceq_iiw', 'ce_iiw', 'pcm', 'v_nb_ti', 'microalloy_sum']);

const isChemicalDerivedField = (field?: ProjectLimitFieldOption): boolean =>
  Boolean(field && CHEMICAL_DERIVED_FIELD_PATHS.has(field.fieldPath.toLowerCase()));

const getDefaultProjectLimitFieldOptions = (reportType: FullReportType): ProjectLimitFieldOption[] =>
  ((PROJECT_LIMIT_FIELDS as Partial<Record<FullReportType, Array<{ fieldPath: string; symbol?: string; label: string; unit: string; fallback: string }>>>)[reportType] || [])
    .map((field) => ({
      fieldPath: field.fieldPath,
      symbol: field.symbol,
      label: field.label,
      unit: field.unit,
      limit: field.fallback,
      max: getLimitNumber(field.fallback.match(/(?:<=|≤)\s*([0-9.]+)/)?.[1]),
    }));

const mergeProjectLimitFieldOptions = (reportType: FullReportType, fields: ProjectLimitFieldOption[] = []): ProjectLimitFieldOption[] => {
  const merged = [...fields];
  getDefaultProjectLimitFieldOptions(reportType).forEach((field) => {
    if (!merged.some((item) => item.fieldPath === field.fieldPath)) merged.push(field);
  });
  return merged;
};

const formatProjectFieldOption = (field: ProjectLimitFieldOption): string =>
  field.symbol ? `${field.symbol} - ${field.label} (${field.fieldPath})` : `${field.label} (${field.fieldPath})`;

const createFieldPathFromLabel = (reportType: FullReportType, label: string, symbol: string): string => {
  if (reportType === 'CHEMICAL' && symbol.trim()) return `elements.${symbol.trim()}`;
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'custom_field';
};

const createReportMetadataForm = (reportType: FullReportType, frequency?: string): ReportMetadataEntry => ({
  reportType,
  reportId: `RPT-${reportType}-${Date.now().toString().slice(-5)}`,
  performedAt: new Date().toISOString().slice(0, 16),
  frequency: frequency || 'per_pipe',
  lab: '',
  technician: '',
  source: 'MANUAL',
  sourceFile: '',
  note: '',
});

const normalizeReportMetadata = (metadata: any): ReportMetadataEntry | null => {
  const reportType = metadata?.reportType || metadata?.report_type;
  if (!reportType) return null;
  const extraDetails = metadata.extraDetails || metadata.extra_details || {};
  return {
    id: metadata.id ?? metadata.metadataId,
    metadataId: metadata.metadataId ?? metadata.id,
    reportType,
    heatNumber: metadata.heatNumber || metadata.heat_number || null,
    lotNumber: getLotNumberFromExtraDetails(extraDetails),
    pipeNumber: metadata.pipeNumber || metadata.pipe_number || null,
    reportId: metadata.reportId || metadata.report_id || '',
    performedAt: metadata.performedAt || metadata.performed_at || '',
    frequency: metadata.frequency || 'per_pipe',
    lab: metadata.lab || '',
    technician: metadata.technician || '',
    source: metadata.source || 'MANUAL',
    sourceFile: metadata.sourceFile || metadata.source_file || '',
    note: metadata.note || '',
    extraDetails,
  };
};

const createMeasurementForm = (reportType: FullReportType, frequency?: string): MeasurementItem => {
  const field = getFieldOptionsForReport()[0];
  const suffix = Date.now().toString().slice(-3);
  return {
    id: `measure-${Date.now()}`,
    reportType,
    fieldPath: field.fieldPath,
    fieldLabel: field.label,
    fieldSymbol: field.symbol,
    measuredValue: '',
    unit: field.unit,
    frequency: frequency || 'per_pipe',
    limit: field.limit,
    source: 'manual entry',
    reference: 'project_limits',
    status: 'PENDING_REVIEW',
    tensileSpecimenId: reportType === 'TENSILE' ? `S-${suffix}` : undefined,
    tensileLocation: reportType === 'TENSILE' ? 'pipe_body' : undefined,
    tensileOrientation: reportType === 'TENSILE' ? 'transverse' : undefined,
    tensileAreaMm2: '',
    tensileYsMpa: '',
    tensileUtsMpa: '',
    tensileElongationPct: '',
    tensileYsUtsRatio: '',
    cvnSpecimenId: reportType === 'CVN' ? `C-${suffix}` : undefined,
    cvnLocation: reportType === 'CVN' ? 'pipe_body' : undefined,
    cvnNotchOrientation: reportType === 'CVN' ? 'transverse' : undefined,
    cvnSpecimenSize: reportType === 'CVN' ? 'full' : undefined,
    cvnEnergyJ: '',
    cvnShearPct: '',
    cvnLateralExpansionMm: '',
    autIndicationId: reportType === 'AUT' ? `I-${suffix}` : undefined,
    autPositionMm: '',
    autClock: reportType === 'AUT' ? '3:00' : undefined,
    autFlawType: reportType === 'AUT' ? 'inclusion' : undefined,
    autDepthPctWt: '',
    autLengthMm: '',
    autDisposition: reportType === 'AUT' ? 'accept' : undefined,
    hydroTestPressureBar: reportType === 'HYDROSTATIC' ? '' : undefined,
    hydroHoldDurationS: reportType === 'HYDROSTATIC' ? '' : undefined,
    hydroPressureDropObserved: reportType === 'HYDROSTATIC' ? 'no' : undefined,
    hydroLeakObserved: reportType === 'HYDROSTATIC' ? 'no' : undefined,
    hydroMedium: reportType === 'HYDROSTATIC' ? '' : undefined,
    hydroSealingMethod: reportType === 'HYDROSTATIC' ? '' : undefined,
    hydroGaugeCalRef: reportType === 'HYDROSTATIC' ? '' : undefined,
    hydroDesignFactor: reportType === 'HYDROSTATIC' ? '' : undefined,
    dimOdAvgMm: reportType === 'DIMENSIONAL' ? '660.7' : undefined,
    dimOdMinMm: reportType === 'DIMENSIONAL' ? '658.9' : undefined,
    dimOdMaxMm: reportType === 'DIMENSIONAL' ? '662.1' : undefined,
    dimOvalityPct: reportType === 'DIMENSIONAL' ? '0.48' : undefined,
    dimWtEnd1N: reportType === 'DIMENSIONAL' ? '22.1' : undefined,
    dimWtEnd1E: reportType === 'DIMENSIONAL' ? '22.4' : undefined,
    dimWtEnd1S: reportType === 'DIMENSIONAL' ? '22.3' : undefined,
    dimWtEnd1W: reportType === 'DIMENSIONAL' ? '22.5' : undefined,
    dimWtEnd2N: reportType === 'DIMENSIONAL' ? '22.2' : undefined,
    dimWtEnd2E: reportType === 'DIMENSIONAL' ? '22.1' : undefined,
    dimWtEnd2S: reportType === 'DIMENSIONAL' ? '22.3' : undefined,
    dimWtEnd2W: reportType === 'DIMENSIONAL' ? '22.4' : undefined,
    dimLengthM: reportType === 'DIMENSIONAL' ? '12.18' : undefined,
    dimStraightnessMm: reportType === 'DIMENSIONAL' ? '14.2' : undefined,
    dimBevelAngleDeg: reportType === 'DIMENSIONAL' ? '30.2' : undefined,
    dimEndSquarenessMm: reportType === 'DIMENSIONAL' ? '0.8' : undefined,
    flatteningSpecimenId: reportType === 'FLATTENING' ? `F-${suffix}` : undefined,
    flatteningWeldPosition: reportType === 'FLATTENING' ? '0deg' : undefined,
    flatteningOdBeforeMm: reportType === 'FLATTENING' ? '660.4' : undefined,
    flatteningHdReached: reportType === 'FLATTENING' ? '0.85' : undefined,
    flatteningLimit: reportType === 'FLATTENING' ? '' : undefined,
    flatteningCracksObserved: reportType === 'FLATTENING' ? 'no' : undefined,
    bendSpecimenId: reportType === 'BEND' ? `B-${suffix}` : undefined,
    bendType: reportType === 'BEND' ? 'face_bend' : undefined,
    bendAngleDeg: reportType === 'BEND' ? '180' : undefined,
    bendCrackLengthMm: reportType === 'BEND' ? '0.0' : undefined,
    bendFracture: reportType === 'BEND' ? 'no' : undefined,
    bendWallThicknessMm: reportType === 'BEND' ? '22.2' : undefined,
    bendMultiplier: reportType === 'BEND' ? '6' : undefined,
    bendRequiredFormerMm: reportType === 'BEND' ? '133.2' : undefined,
    bendMillFormerMm: reportType === 'BEND' ? '133.2' : undefined,
    hardnessZone: reportType === 'HARDNESS' ? 'weld cap' : undefined,
    hardnessReadingId: reportType === 'HARDNESS' ? `H-${suffix}` : undefined,
    hardnessPosition: reportType === 'HARDNESS' ? 'indent 1' : undefined,
    hardnessValueHv10: reportType === 'HARDNESS' ? '218' : undefined,
    hardnessProjectLimitHv10: reportType === 'HARDNESS' ? '220' : undefined,
    hardnessFallbackLimitHv10: reportType === 'HARDNESS' ? '250' : undefined,
    visualDefectId: reportType === 'VISUAL' ? `V-${suffix}` : undefined,
    visualDefectType: reportType === 'VISUAL' ? 'scale' : undefined,
    visualPositionMm: '',
    visualClock: reportType === 'VISUAL' ? '5:00' : undefined,
    visualDepthPctWt: '',
    visualDisposition: reportType === 'VISUAL' ? 'accept' : undefined,
    visualLightingLux: reportType === 'VISUAL' ? '612' : undefined,
    rtIndicationId: reportType === 'RT' ? `rt-${suffix}` : undefined,
    rtPositionMm: '',
    rtFlawType: reportType === 'RT' ? 'porosity' : undefined,
    rtLengthMm: '',
    rtAreaMm2: '',
    rtDisposition: reportType === 'RT' ? 'accept' : undefined,
    rtIqiSensitivityPct: reportType === 'RT' ? '1.8' : undefined,
    rtExposureClass: reportType === 'RT' ? 'B' : undefined,
    mptIndicationId: reportType === 'MPT' ? `m${suffix}` : undefined,
    mptEnd: reportType === 'MPT' ? 'End 1 - PE1' : undefined,
    mptType: reportType === 'MPT' ? 'rounded' : undefined,
    mptLengthMm: '',
    mptDisposition: reportType === 'MPT' ? 'accept' : undefined,
    mptFieldStrengthOe: reportType === 'MPT' ? '32' : undefined,
    mptUvIrradiance: reportType === 'MPT' ? '1180' : undefined,
    heatBatchId: reportType === 'HEAT_TREAT' ? `heat-${suffix}` : undefined,
    heatAustenitisingTempC: '',
    heatAustenitisingHoldMin: '',
    heatCoolingRateCps: '',
    heatTemperingTempC: '',
    heatTemperingHoldMin: '',
    heatPipesInBatch: '',
    pbutIndicationId: reportType === 'PIPE_BODY_UT' ? `L${suffix}` : undefined,
    pbutIndicationType: reportType === 'PIPE_BODY_UT' ? 'lamination' : undefined,
    pbutPositionMm: '',
    pbutClock: reportType === 'PIPE_BODY_UT' ? '2:30' : undefined,
    pbutAreaMm2: '',
    pbutDepthPctWt: '',
    pbutEndZone: reportType === 'PIPE_BODY_UT' ? 'no' : undefined,
    pbutDisposition: reportType === 'PIPE_BODY_UT' ? 'accept' : undefined,
    mflIndicationId: reportType === 'MFL_ET' ? `e${suffix}` : undefined,
    mflFlawType: reportType === 'MFL_ET' ? 'hook_crack' : undefined,
    mflSignalPctRef: '',
    mflDisposition: reportType === 'MFL_ET' ? 'accept' : undefined,
    mflCalNotchPctWt: reportType === 'MFL_ET' ? '12.5' : undefined,
    mflRoute: reportType === 'MFL_ET' ? 'ERW/HFW' : undefined,
    coatingType: reportType === 'COATING' ? '3LPE' : undefined,
    coatingDftMinUm: '',
    coatingDftAvgUm: '',
    coatingAdhesionNcm: '',
    coatingCdRadiusMm: '',
    coatingAnchorProfileUm: '',
    coatingHolidayVoltageKv: '',
    coatingHolidaysFound: '',
    coatingCutbackLengthMm: '',
    coatingSurfacePrepGrade: '',
    threadType: '',
    threadStandard: '',
    threadGaugeSetRef: '',
    threadEndTested: reportType === 'THREAD' ? 'both' : undefined,
    threadCouplingApplied: reportType === 'THREAD' ? 'no' : undefined,
    threadVisualDamage: reportType === 'THREAD' ? 'no' : undefined,
    threadTaperMmPerM: '',
    threadLeadMmCum: '',
    threadHeightMm: '',
    threadPipeEndDiameterMm: '',
    weldPassName: '',
    weldProcess: reportType === 'WELD_MAP' ? 'SAW' : undefined,
    weldWireHeatNo: '',
    weldFluxLotNo: '',
    weldCurrentA: '',
    weldVoltageV: '',
    weldSpeedMmMin: '',
    weldHeatInputKjMm: '',
    weldPlateId: '',
    weldPlateHeatNumber: '',
    weldPipeHeatNumber: '',
    weldWpsRef: '',
  };
};

const parseLimitNumber = (value?: string): number | null => {
  if (!value?.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getLimitNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const getLimitString = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text || null;
};

const formatProjectLimitText = (limit: ProjectLimitConfig): string => {
  if (limit.minRaw && limit.maxRaw && limit.minRaw === limit.maxRaw) return `= ${limit.minRaw}${limit.unit ? ` ${limit.unit}` : ''}`;
  if (limit.minRaw && limit.maxRaw) return `${limit.minRaw} - ${limit.maxRaw}${limit.unit ? ` ${limit.unit}` : ''}`;
  if (limit.minRaw) return `>= ${limit.minRaw}${limit.unit ? ` ${limit.unit}` : ''}`;
  if (limit.maxRaw) return `<= ${limit.maxRaw}${limit.unit ? ` ${limit.unit}` : ''}`;
  return limit.text || 'Project limit configured';
};

const buildProjectLimitLookup = (limits: any[] = []): ProjectLimitLookup =>
  limits.reduce((acc, limit) => {
    const reportType = limit.reportType || limit.report_type || limit.report;
    const fieldKey = limit.fieldKey || limit.field_key || limit.field;
    if (!reportType || !fieldKey) return acc;

    const minRaw = getLimitString(limit.minValue ?? limit.min_value);
    const maxRaw = getLimitString(limit.maxValue ?? limit.max_value);
    const config: ProjectLimitConfig = {
      fieldKey,
      min: getLimitNumber(minRaw),
      max: getLimitNumber(maxRaw),
      minRaw,
      maxRaw,
      unit: getLimitString(limit.unit),
      text: getLimitString(limit.std ?? limit.standardFallback ?? limit.detailsJson?.standardFallback ?? limit.details_json?.standardFallback) || '',
    };

    return {
      ...acc,
      [reportType]: {
        ...(acc[reportType as FullReportType] || {}),
        [fieldKey]: config,
      },
    };
  }, {} as ProjectLimitLookup);

const getProjectLimit = (limits: ProjectLimitLookup, reportType: FullReportType, fieldKey: string): ProjectLimitConfig | undefined =>
  limits[reportType]?.[fieldKey];

const getProjectLimitText = (limits: ProjectLimitLookup, reportType: FullReportType, fieldKeys: string[]): string => {
  const texts = fieldKeys
    .map((fieldKey) => {
      const limit = getProjectLimit(limits, reportType, fieldKey);
      return limit ? `${getProjectLimitDisplayLabel(reportType, fieldKey, limit)}: ${formatProjectLimitText(limit)}` : null;
    })
    .filter(Boolean);
  return texts.length ? texts.join('; ') : 'Project limit not configured';
};

const getReportProjectLimitText = (limits: ProjectLimitLookup, reportType: FullReportType, fallback = 'Project limit not configured'): string => {
  const fieldKeys = PROJECT_LIMIT_FIELD_KEYS[reportType];
  if (!fieldKeys?.length) return fallback;
  const text = getProjectLimitText(limits, reportType, fieldKeys);
  return text === 'Project limit not configured' ? fallback : text;
};

const getProjectLimitReportType = (limit: any): FullReportType | undefined =>
  limit.reportType || limit.report_type || limit.report;

const getProjectLimitFieldKey = (limit: any): string | undefined =>
  limit.fieldKey || limit.field_key || limit.field;

const CHEMICAL_LIMIT_DISPLAY_NAMES: Record<string, string> = {
  ceq_iiw: 'Carbon equivalent',
  microalloy_sum: 'V + Nb + Ti sum',
  pcm: 'Weld cracking index',
  'elements.b': 'Boron',
  'elements.c': 'Carbon',
  'elements.cr': 'Chromium',
  'elements.cu': 'Copper',
  'elements.mn': 'Manganese',
  'elements.mo': 'Molybdenum',
  'elements.n': 'Nitrogen',
  'elements.nb': 'Niobium',
  'elements.ni': 'Nickel',
  'elements.p': 'Phosphorus',
  'elements.s': 'Sulfur',
  'elements.si': 'Silicon',
  'elements.ti': 'Titanium',
  'elements.v': 'Vanadium',
};

const getProjectLimitDisplayLabel = (reportType: FullReportType, fieldKey: string, limit: any): string => {
  const standardField = (PROJECT_LIMIT_FIELDS as Partial<Record<FullReportType, Array<{ fieldPath: string; label: string }>>>)[reportType]
    ?.find((field) => field.fieldPath.toLowerCase() === fieldKey.toLowerCase());
  if (standardField?.label) return standardField.label;

  if (reportType === 'CHEMICAL') {
    const chemicalName = CHEMICAL_LIMIT_DISPLAY_NAMES[fieldKey.toLowerCase()];
    if (chemicalName) return chemicalName;
  }

  return limit.fieldLabel || limit.field_label || fieldKey
    .replace(/^elements\./i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char: string) => char.toUpperCase());
};

const buildProjectLimitFieldOptions = (limits: any[] = []): Partial<Record<FullReportType, ProjectLimitFieldOption[]>> =>
  limits.reduce((acc, limit) => {
    const reportType = getProjectLimitReportType(limit);
    const fieldKey = getProjectLimitFieldKey(limit);
    if (!reportType || !fieldKey) return acc;

    const minRaw = getLimitString(limit.minValue ?? limit.min_value);
    const maxRaw = getLimitString(limit.maxValue ?? limit.max_value);
    const config: ProjectLimitConfig = {
      fieldKey,
      min: getLimitNumber(minRaw),
      max: getLimitNumber(maxRaw),
      minRaw,
      maxRaw,
      unit: getLimitString(limit.unit),
      text: getLimitString(limit.std ?? limit.standardFallback ?? limit.detailsJson?.standardFallback ?? limit.details_json?.standardFallback) || '',
    };
    const symbol = fieldKey.startsWith('elements.') ? fieldKey.replace('elements.', '') : undefined;
    const option: ProjectLimitFieldOption = {
      fieldPath: fieldKey,
      symbol,
      label: getProjectLimitDisplayLabel(reportType, fieldKey, limit),
      unit: config.unit || '',
      limit: formatProjectLimitText(config),
      min: config.min,
      max: config.max,
    };

    return {
      ...acc,
      [reportType]: [
        ...(acc[reportType] || []),
        option,
      ],
    };
  }, {} as Partial<Record<FullReportType, ProjectLimitFieldOption[]>>);

const evaluateProjectLimit = (
  limits: ProjectLimitLookup,
  reportType: FullReportType,
  fieldKey: string,
  value: number | null,
): string => {
  const limit = getProjectLimit(limits, reportType, fieldKey);
  if (!limit || value == null) return 'PENDING_REVIEW';
  if (limit.min != null && value < limit.min) return 'FAIL';
  if (limit.max != null && value > limit.max) return 'FAIL';
  return 'PASS';
};

const combineStatuses = (statuses: string[]): string => {
  if (statuses.includes('FAIL')) return 'FAIL';
  if (statuses.includes('PENDING_REVIEW')) return 'PENDING_REVIEW';
  return 'PASS';
};

const calculateChemicalStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const measuredValue = parseLimitNumber(measurement.measuredValue);
  return evaluateProjectLimit(limits, 'CHEMICAL', measurement.fieldPath, measuredValue);
};

const getNumericId = (value: unknown): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const getSavedReportId = (report?: SavedPipeMaterialReport): number | undefined =>
  getNumericId(report?.id ?? report?.reportId);

const normalizeScopeValue = (value: unknown): string => String(value ?? '').trim().toLowerCase();

type ReportScope = {
  frequency: string;
  heatNumber: string | null;
  lotNumber: string | null;
  pipeNumber: string | null;
};

const getReportLotNumber = (report: SavedPipeMaterialReport | ReportMetadataEntry): string => {
  const extraDetails = getExtraDetails(report as SavedPipeMaterialReport);
  return getLotNumberFromExtraDetails(extraDetails);
};

const getReportScope = (
  frequency: string | undefined,
  heatNumber: unknown,
  lotNumber: unknown,
  pipeNumber: unknown
): ReportScope => {
  const normalizedFrequency = frequency || 'per_pipe';
  const nextHeatNumber = String(heatNumber ?? '').trim() || null;
  const nextLotNumber = String(lotNumber ?? '').trim() || null;
  const nextPipeNumber = String(pipeNumber ?? '').trim() || null;

  if (normalizedFrequency === 'per_heat') {
    return {
      frequency: normalizedFrequency,
      heatNumber: nextHeatNumber,
      lotNumber: null,
      pipeNumber: null,
    };
  }

  if (normalizedFrequency === 'per_lot' || normalizedFrequency === 'per_batch' || normalizedFrequency === 'per_shift') {
    return {
      frequency: normalizedFrequency,
      heatNumber: nextHeatNumber,
      lotNumber: nextLotNumber,
      pipeNumber: null,
    };
  }

  return {
    frequency: normalizedFrequency,
    heatNumber: nextHeatNumber,
    lotNumber: nextLotNumber,
    pipeNumber: nextPipeNumber,
  };
};

const reportMatchesScope = (
  report: SavedPipeMaterialReport | ReportMetadataEntry,
  reportType: FullReportType,
  scope: ReportScope
): boolean => {
  const rawReport = report as SavedPipeMaterialReport & { heat_number?: string | null; pipe_number?: string | null };
  const reportTypeValue = (rawReport.reportType || rawReport.report_type) as FullReportType | undefined;
  if (reportTypeValue !== reportType) return false;

  const reportHeat = normalizeScopeValue(rawReport.heatNumber ?? rawReport.heat_number);
  const reportPipe = normalizeScopeValue(rawReport.pipeNumber ?? rawReport.pipe_number);
  const reportLot = normalizeScopeValue(getReportLotNumber(report));
  const currentHeat = normalizeScopeValue(scope.heatNumber);
  const currentLot = normalizeScopeValue(scope.lotNumber);
  const currentPipe = normalizeScopeValue(scope.pipeNumber);

  if (reportHeat !== currentHeat) return false;
  if (scope.frequency === 'per_heat') return !reportLot && !reportPipe;
  if (scope.frequency === 'per_lot' || scope.frequency === 'per_batch' || scope.frequency === 'per_shift') {
    return reportLot === currentLot && !reportPipe;
  }
  return reportPipe === currentPipe;
};

const savedReportMatchesScope = (
  report: SavedPipeMaterialReport | ReportMetadataEntry,
  heatNumber: unknown,
  lotNumber: unknown,
  pipeNumber: unknown
): boolean => {
  const rawReport = report as SavedPipeMaterialReport & { heat_number?: string | null; pipe_number?: string | null };
  const reportHeat = normalizeScopeValue(rawReport.heatNumber ?? rawReport.heat_number);
  const reportLot = normalizeScopeValue(getReportLotNumber(report));
  const reportPipe = normalizeScopeValue(rawReport.pipeNumber ?? rawReport.pipe_number);
  const currentHeat = normalizeScopeValue(heatNumber);
  const currentLot = normalizeScopeValue(lotNumber);
  const currentPipe = normalizeScopeValue(pipeNumber);

  if (reportHeat && currentHeat && reportHeat !== currentHeat) return false;
  if (reportPipe && currentPipe && reportPipe !== currentPipe) return false;
  if (reportLot && currentLot && reportLot !== currentLot) return false;
  return true;
};

const getReportTypeFromSavedReport = (report: SavedPipeMaterialReport): FullReportType | undefined =>
  report.reportType || report.report_type;

const getReportDetailsFromSavedReport = (report: SavedPipeMaterialReport): MeasurementItem[] =>
  Array.isArray(report.reportDetails)
    ? report.reportDetails
    : Array.isArray(report.report_details)
      ? report.report_details
      : [];

const calculateTensileRatio = (measurement: MeasurementItem): string => {
  const manualRatio = parseLimitNumber(measurement.tensileYsUtsRatio);
  if (manualRatio != null) return manualRatio.toFixed(3);

  const ys = parseLimitNumber(measurement.tensileYsMpa);
  const uts = parseLimitNumber(measurement.tensileUtsMpa);
  if (ys == null || uts == null || uts === 0) return '';

  return (ys / uts).toFixed(3);
};

const calculateTensileStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const ys = parseLimitNumber(measurement.tensileYsMpa);
  const uts = parseLimitNumber(measurement.tensileUtsMpa);
  const elongation = parseLimitNumber(measurement.tensileElongationPct);
  const ratio = parseLimitNumber(calculateTensileRatio(measurement));

  if (ys == null || uts == null || elongation == null || ratio == null) return 'INCOMPLETE';

  const statuses = [
    evaluateProjectLimit(limits, 'TENSILE', 'ys_mpa', ys),
    evaluateProjectLimit(limits, 'TENSILE', 'uts_mpa', uts),
    evaluateProjectLimit(limits, 'TENSILE', 'elongation_pct', elongation),
    evaluateProjectLimit(limits, 'TENSILE', 'ys_uts_ratio', ratio),
  ];

  return statuses.includes('FAIL') ? 'FAIL' : 'PASS';
};

const calculateCvnStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const energy = parseLimitNumber(measurement.cvnEnergyJ);
  const shear = parseLimitNumber(measurement.cvnShearPct);

  return combineStatuses([
    evaluateProjectLimit(limits, 'CVN', 'single_energy_j', energy),
    evaluateProjectLimit(limits, 'CVN', 'avg_shear_pct', shear),
  ]);
};

const calculateAutStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const depth = parseLimitNumber(measurement.autDepthPctWt);
  const length = parseLimitNumber(measurement.autLengthMm);

  return combineStatuses([
    evaluateProjectLimit(limits, 'AUT', 'indication_depth_pct', depth),
    evaluateProjectLimit(limits, 'AUT', 'indication_length_mm', length),
  ]);
};

const calculateHydrostaticStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const pressure = parseLimitNumber(measurement.hydroTestPressureBar);
  const hold = parseLimitNumber(measurement.hydroHoldDurationS);
  const pressureDropObserved = measurement.hydroPressureDropObserved === 'yes';
  const leakObserved = measurement.hydroLeakObserved === 'yes';

  if (pressureDropObserved || leakObserved) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'HYDROSTATIC', 'test_pressure_bar', pressure),
    evaluateProjectLimit(limits, 'HYDROSTATIC', 'hold_duration_s', hold),
  ]);
};

const calculateDimensionalStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const requiredValues = [
    measurement.dimOdAvgMm,
    measurement.dimOdMinMm,
    measurement.dimOdMaxMm,
    measurement.dimOvalityPct,
    measurement.dimLengthM,
    measurement.dimStraightnessMm,
    measurement.dimBevelAngleDeg,
    measurement.dimEndSquarenessMm,
  ];
  const wallValues = [
    measurement.dimWtEnd1N,
    measurement.dimWtEnd1E,
    measurement.dimWtEnd1S,
    measurement.dimWtEnd1W,
    measurement.dimWtEnd2N,
    measurement.dimWtEnd2E,
    measurement.dimWtEnd2S,
    measurement.dimWtEnd2W,
  ];

  if ([...requiredValues, ...wallValues].some((value) => parseLimitNumber(value) == null)) return 'PENDING_REVIEW';

  const statuses = [
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'outer_diameter', parseLimitNumber(measurement.dimOdAvgMm)),
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'outer_diameter', parseLimitNumber(measurement.dimOdMinMm)),
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'outer_diameter', parseLimitNumber(measurement.dimOdMaxMm)),
    ...wallValues.map((value) => evaluateProjectLimit(limits, 'DIMENSIONAL', 'wall_thickness', parseLimitNumber(value))),
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'ovality_pct', parseLimitNumber(measurement.dimOvalityPct)),
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'length_m', parseLimitNumber(measurement.dimLengthM)),
    evaluateProjectLimit(limits, 'DIMENSIONAL', 'straightness_mm_m', parseLimitNumber(measurement.dimStraightnessMm)),
  ];
  if (getProjectLimit(limits, 'DIMENSIONAL', 'bevel_angle_deg')) {
    statuses.push(evaluateProjectLimit(limits, 'DIMENSIONAL', 'bevel_angle_deg', parseLimitNumber(measurement.dimBevelAngleDeg)));
  }
  if (getProjectLimit(limits, 'DIMENSIONAL', 'end_squareness_mm')) {
    statuses.push(evaluateProjectLimit(limits, 'DIMENSIONAL', 'end_squareness_mm', parseLimitNumber(measurement.dimEndSquarenessMm)));
  }

  return combineStatuses(statuses);
};

const getDimensionalFailureMessages = (measurement: MeasurementItem, limits: ProjectLimitLookup): string[] => {
  const messages: string[] = [];
  const addFailure = (label: string, fieldKey: string, rawValue?: string) => {
    const value = parseLimitNumber(rawValue);
    const limit = getProjectLimit(limits, 'DIMENSIONAL', fieldKey);
    if (!limit || value == null) return;
    const unit = limit.unit ? ` ${limit.unit}` : '';
    if (limit.min != null && value < limit.min) messages.push(`${label}: ${value} < ${limit.min}${unit}`);
    if (limit.max != null && value > limit.max) messages.push(`${label}: ${value} > ${limit.max}${unit}`);
  };

  addFailure('OD Avg', 'outer_diameter', measurement.dimOdAvgMm);
  addFailure('OD Min', 'outer_diameter', measurement.dimOdMinMm);
  addFailure('OD Max', 'outer_diameter', measurement.dimOdMaxMm);
  ([
    ['WT End 1 - N', measurement.dimWtEnd1N],
    ['WT End 1 - E', measurement.dimWtEnd1E],
    ['WT End 1 - S', measurement.dimWtEnd1S],
    ['WT End 1 - W', measurement.dimWtEnd1W],
    ['WT End 2 - N', measurement.dimWtEnd2N],
    ['WT End 2 - E', measurement.dimWtEnd2E],
    ['WT End 2 - S', measurement.dimWtEnd2S],
    ['WT End 2 - W', measurement.dimWtEnd2W],
  ] as const).forEach(([label, value]) => addFailure(label, 'wall_thickness', value));
  addFailure('Ovality', 'ovality_pct', measurement.dimOvalityPct);
  addFailure('Length', 'length_m', measurement.dimLengthM);
  addFailure('Straightness', 'straightness_mm_m', measurement.dimStraightnessMm);
  addFailure('Bevel angle', 'bevel_angle_deg', measurement.dimBevelAngleDeg);
  addFailure('End squareness', 'end_squareness_mm', measurement.dimEndSquarenessMm);

  return messages;
};

const calculateFlatteningStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const hdReached = parseLimitNumber(measurement.flatteningHdReached);
  const cracksObserved = measurement.flatteningCracksObserved === 'yes';
  const crackLengthLimit = getProjectLimit(limits, 'FLATTENING', 'crack_length_mm');
  const crackLengthValue = cracksObserved ? (crackLengthLimit?.max ?? 0) + 1 : 0;

  return combineStatuses([
    evaluateProjectLimit(limits, 'FLATTENING', 'flattening_height_h', hdReached),
    crackLengthLimit ? evaluateProjectLimit(limits, 'FLATTENING', 'crack_length_mm', crackLengthValue) : cracksObserved ? 'FAIL' : 'PASS',
  ]);
};

const calculateBendStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const angle = parseLimitNumber(measurement.bendAngleDeg);
  const crack = parseLimitNumber(measurement.bendCrackLengthMm);
  const fracture = measurement.bendFracture === 'yes';

  if (fracture) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'BEND', 'bend_angle_deg', angle),
    evaluateProjectLimit(limits, 'BEND', 'open_defect_mm', crack),
  ]);
};

const calculateHardnessStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const hardness = parseLimitNumber(measurement.hardnessValueHv10);
  const zone = (measurement.hardnessZone || '').toLowerCase();
  const zoneLimitKey = zone.includes('haz')
    ? 'haz_hv10'
    : zone.includes('base') || zone.includes('body')
      ? 'body_hv10'
      : zone.includes('weld')
        ? 'weld_hv10'
        : 'overall_max';
  const specificStatus = evaluateProjectLimit(limits, 'HARDNESS', zoneLimitKey, hardness);
  return specificStatus === 'PENDING_REVIEW'
    ? evaluateProjectLimit(limits, 'HARDNESS', 'overall_max', hardness)
    : specificStatus;
};

const calculateVisualStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const defectType = measurement.visualDefectType || '';
  const depth = parseLimitNumber(measurement.visualDepthPctWt);
  const lighting = parseLimitNumber(measurement.visualLightingLux);
  const repaired = measurement.visualDisposition === 'repaired';

  if (defectType === 'arc_strike' || defectType === 'crack') return 'FAIL';
  if (repaired) return 'PENDING_REVIEW';
  return combineStatuses([
    evaluateProjectLimit(limits, 'VISUAL', 'visual_depth_pct_wt', depth),
    evaluateProjectLimit(limits, 'VISUAL', 'visual_lighting_lux', lighting),
  ]);
};

const getVisualStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const defectType = measurement.visualDefectType || '';
  const depth = parseLimitNumber(measurement.visualDepthPctWt);
  const lighting = parseLimitNumber(measurement.visualLightingLux);
  const disposition = measurement.visualDisposition || 'accept';
  const depthLimit = getProjectLimit(limits, 'VISUAL', 'visual_depth_pct_wt');
  const lightingLimit = getProjectLimit(limits, 'VISUAL', 'visual_lighting_lux');

  if (defectType === 'arc_strike') return 'Failing: Arc strike is an auto-fail defect type';
  if (defectType === 'crack') return 'Failing: Crack is an auto-fail defect type';
  if (disposition === 'repaired') return 'Pending review: repaired defect needs reviewer acceptance';
  if (depthLimit && depth != null && depthLimit.min != null && depth < depthLimit.min) return `Failing: Depth ${depth} < ${depthLimit.min}${depthLimit.unit ? ` ${depthLimit.unit}` : ''}`;
  if (depthLimit && depth != null && depthLimit.max != null && depth > depthLimit.max) return `Failing: Depth ${depth} > ${depthLimit.max}${depthLimit.unit ? ` ${depthLimit.unit}` : ''}`;
  if (lightingLimit && lighting != null && lightingLimit.min != null && lighting < lightingLimit.min) return `Failing: Lighting ${lighting} < ${lightingLimit.min}${lightingLimit.unit ? ` ${lightingLimit.unit}` : ''}`;
  if (lightingLimit && lighting != null && lightingLimit.max != null && lighting > lightingLimit.max) return `Failing: Lighting ${lighting} > ${lightingLimit.max}${lightingLimit.unit ? ` ${lightingLimit.unit}` : ''}`;
  return null;
};

const calculateRtStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const length = parseLimitNumber(measurement.rtLengthMm);
  const flawType = measurement.rtFlawType || 'porosity';
  const iqiSensitivity = parseLimitNumber(measurement.rtIqiSensitivityPct);
  const accepted = measurement.rtDisposition === 'accept';
  const exposureClass = measurement.rtExposureClass || '';

  if (flawType === 'crack' || flawType === 'lack_of_fusion' || !accepted) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'RT', 'rt_iqi_sensitivity_pct', iqiSensitivity),
    flawType === 'slag'
      ? evaluateProjectLimit(limits, 'RT', 'rt_slag_length_mm', length)
      : evaluateProjectLimit(limits, 'RT', 'rt_porosity_length_mm', length),
    getProjectLimit(limits, 'RT', 'rt_exposure_class') && exposureClass !== 'B' ? 'FAIL' : 'PASS',
  ]);
};

const getRtStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const length = parseLimitNumber(measurement.rtLengthMm);
  const flawType = measurement.rtFlawType || 'porosity';
  const iqiSensitivity = parseLimitNumber(measurement.rtIqiSensitivityPct);
  const disposition = measurement.rtDisposition || 'accept';
  const exposureClass = measurement.rtExposureClass || '';
  const iqiLimit = getProjectLimit(limits, 'RT', 'rt_iqi_sensitivity_pct');
  const porosityLimit = getProjectLimit(limits, 'RT', 'rt_porosity_length_mm');
  const slagLimit = getProjectLimit(limits, 'RT', 'rt_slag_length_mm');
  const requiredClass = getProjectLimit(limits, 'RT', 'rt_exposure_class');

  if (flawType === 'crack') return 'Failing: Crack is an auto-fail RT indication';
  if (flawType === 'lack_of_fusion') return 'Failing: Lack of fusion is an auto-fail RT indication';
  if (disposition !== 'accept') return `Failing: Disposition is ${formatLabel(disposition)}`;
  if (iqiLimit && iqiSensitivity != null && iqiLimit.max != null && iqiSensitivity > iqiLimit.max) return `Failing: IQI sensitivity ${iqiSensitivity} > ${iqiLimit.max}${iqiLimit.unit ? ` ${iqiLimit.unit}` : ''}`;
  if (requiredClass && exposureClass && exposureClass !== 'B') return `Failing: Exposure class ${exposureClass} does not match B`;
  if (flawType === 'slag' && slagLimit && length != null && slagLimit.max != null && length > slagLimit.max) return `Failing: Slag length ${length} > ${slagLimit.max}${slagLimit.unit ? ` ${slagLimit.unit}` : ''}`;
  if (flawType !== 'slag' && porosityLimit && length != null && porosityLimit.max != null && length > porosityLimit.max) return `Failing: Porosity length ${length} > ${porosityLimit.max}${porosityLimit.unit ? ` ${porosityLimit.unit}` : ''}`;
  return null;
};

const calculateMptStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const length = parseLimitNumber(measurement.mptLengthMm);
  const fieldStrength = parseLimitNumber(measurement.mptFieldStrengthOe);
  const uvIrradiance = parseLimitNumber(measurement.mptUvIrradiance);
  const indicationType = measurement.mptType || 'rounded';
  const accepted = measurement.mptDisposition === 'accept';

  if (!accepted) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'MPT', 'mpt_field_strength_oe', fieldStrength),
    evaluateProjectLimit(limits, 'MPT', 'mpt_uv_irradiance_uw_cm2', uvIrradiance),
    indicationType === 'linear'
      ? evaluateProjectLimit(limits, 'MPT', 'mpt_linear_length_mm', length)
      : evaluateProjectLimit(limits, 'MPT', 'mpt_rounded_length_mm', length),
  ]);
};

const getMptStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const length = parseLimitNumber(measurement.mptLengthMm);
  const fieldStrength = parseLimitNumber(measurement.mptFieldStrengthOe);
  const uvIrradiance = parseLimitNumber(measurement.mptUvIrradiance);
  const indicationType = measurement.mptType || 'rounded';
  const disposition = measurement.mptDisposition || 'accept';
  const fieldLimit = getProjectLimit(limits, 'MPT', 'mpt_field_strength_oe');
  const uvLimit = getProjectLimit(limits, 'MPT', 'mpt_uv_irradiance_uw_cm2');
  const linearLimit = getProjectLimit(limits, 'MPT', 'mpt_linear_length_mm');
  const roundedLimit = getProjectLimit(limits, 'MPT', 'mpt_rounded_length_mm');

  if (disposition !== 'accept') return `Failing: Disposition is ${formatLabel(disposition)}`;
  if (fieldLimit && fieldStrength != null && fieldLimit.min != null && fieldStrength < fieldLimit.min) return `Failing: Field strength ${fieldStrength} < ${fieldLimit.min}${fieldLimit.unit ? ` ${fieldLimit.unit}` : ''}`;
  if (uvLimit && uvIrradiance != null && uvLimit.min != null && uvIrradiance < uvLimit.min) return `Failing: UV irradiance ${uvIrradiance} < ${uvLimit.min}${uvLimit.unit ? ` ${uvLimit.unit}` : ''}`;
  if (indicationType === 'linear' && linearLimit && length != null && linearLimit.max != null && length > linearLimit.max) return `Failing: Linear indication ${length} > ${linearLimit.max}${linearLimit.unit ? ` ${linearLimit.unit}` : ''}`;
  if (indicationType !== 'linear' && roundedLimit && length != null && roundedLimit.max != null && length > roundedLimit.max) return `Failing: Rounded indication ${length} > ${roundedLimit.max}${roundedLimit.unit ? ` ${roundedLimit.unit}` : ''}`;
  return null;
};

const calculateHeatTreatStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const ausTemp = parseLimitNumber(measurement.heatAustenitisingTempC);
  const ausHold = parseLimitNumber(measurement.heatAustenitisingHoldMin);
  const coolingRate = parseLimitNumber(measurement.heatCoolingRateCps);
  const temperTemp = parseLimitNumber(measurement.heatTemperingTempC);
  const temperHold = parseLimitNumber(measurement.heatTemperingHoldMin);

  return combineStatuses([
    evaluateProjectLimit(limits, 'HEAT_TREAT', 'austenitising_temp_c', ausTemp),
    evaluateProjectLimit(limits, 'HEAT_TREAT', 'austenitising_hold_min', ausHold),
    evaluateProjectLimit(limits, 'HEAT_TREAT', 'cooling_rate_cps', coolingRate),
    evaluateProjectLimit(limits, 'HEAT_TREAT', 'tempering_temp_c', temperTemp),
    evaluateProjectLimit(limits, 'HEAT_TREAT', 'tempering_hold_min', temperHold),
  ]);
};

const getHeatTreatStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const checks: Array<[string, string, number | null]> = [
    ['Austenitising temperature', 'austenitising_temp_c', parseLimitNumber(measurement.heatAustenitisingTempC)],
    ['Austenitising hold', 'austenitising_hold_min', parseLimitNumber(measurement.heatAustenitisingHoldMin)],
    ['Cooling rate', 'cooling_rate_cps', parseLimitNumber(measurement.heatCoolingRateCps)],
    ['Tempering temperature', 'tempering_temp_c', parseLimitNumber(measurement.heatTemperingTempC)],
    ['Tempering hold', 'tempering_hold_min', parseLimitNumber(measurement.heatTemperingHoldMin)],
  ];

  for (const [label, fieldKey, value] of checks) {
    const limit = getProjectLimit(limits, 'HEAT_TREAT', fieldKey);
    if (!limit || value == null) continue;
    if (limit.min != null && value < limit.min) return `Failing: ${label} ${value} < ${limit.min}${limit.unit ? ` ${limit.unit}` : ''}`;
    if (limit.max != null && value > limit.max) return `Failing: ${label} ${value} > ${limit.max}${limit.unit ? ` ${limit.unit}` : ''}`;
  }

  return null;
};

const calculatePipeBodyUtStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const area = parseLimitNumber(measurement.pbutAreaMm2);
  const depthPctWt = parseLimitNumber(measurement.pbutDepthPctWt);
  const indicationType = measurement.pbutIndicationType || 'lamination';
  const endZone = measurement.pbutEndZone === 'yes';
  const accepted = measurement.pbutDisposition === 'accept';

  if (!accepted) return 'FAIL';
  return combineStatuses([
    indicationType === 'inclusion'
      ? evaluateProjectLimit(limits, 'PIPE_BODY_UT', 'pbut_inclusion_area_mm2', area)
      : evaluateProjectLimit(limits, 'PIPE_BODY_UT', 'pbut_lamination_area_mm2', area),
    endZone ? evaluateProjectLimit(limits, 'PIPE_BODY_UT', 'pbut_end_zone_depth_pct_wt', depthPctWt) : 'PASS',
  ]);
};

const getPipeBodyUtStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const area = parseLimitNumber(measurement.pbutAreaMm2);
  const depthPctWt = parseLimitNumber(measurement.pbutDepthPctWt);
  const indicationType = measurement.pbutIndicationType || 'lamination';
  const endZone = measurement.pbutEndZone === 'yes';
  const disposition = measurement.pbutDisposition || 'accept';
  const areaLimit = getProjectLimit(limits, 'PIPE_BODY_UT', indicationType === 'inclusion' ? 'pbut_inclusion_area_mm2' : 'pbut_lamination_area_mm2');
  const depthLimit = getProjectLimit(limits, 'PIPE_BODY_UT', 'pbut_end_zone_depth_pct_wt');

  if (disposition !== 'accept') return `Failing: Disposition is ${formatLabel(disposition)}`;
  if (areaLimit && area != null && areaLimit.max != null && area > areaLimit.max) return `Failing: ${formatLabel(indicationType)} area ${area} > ${areaLimit.max}${areaLimit.unit ? ` ${areaLimit.unit}` : ''}`;
  if (endZone && depthLimit && depthPctWt != null && depthLimit.max != null && depthPctWt > depthLimit.max) return `Failing: End-zone depth ${depthPctWt} > ${depthLimit.max}${depthLimit.unit ? ` ${depthLimit.unit}` : ''}`;
  return null;
};

const calculateMflEtStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const signal = parseLimitNumber(measurement.mflSignalPctRef);
  const calNotch = parseLimitNumber(measurement.mflCalNotchPctWt);
  const disposition = measurement.mflDisposition || 'accept';

  if (disposition === 'reject') return 'FAIL';
  if (disposition === 'confirmed_by_AUT' || disposition === 'repaired') return 'PENDING_REVIEW';
  return combineStatuses([
    evaluateProjectLimit(limits, 'MFL_ET', 'mfl_signal_pct_ref', signal),
    evaluateProjectLimit(limits, 'MFL_ET', 'mfl_cal_notch_pct_wt', calNotch),
  ]);
};

const getMflEtStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const signal = parseLimitNumber(measurement.mflSignalPctRef);
  const calNotch = parseLimitNumber(measurement.mflCalNotchPctWt);
  const disposition = measurement.mflDisposition || 'accept';
  const signalLimit = getProjectLimit(limits, 'MFL_ET', 'mfl_signal_pct_ref');
  const calNotchLimit = getProjectLimit(limits, 'MFL_ET', 'mfl_cal_notch_pct_wt');

  if (disposition === 'reject') return 'Failing: Disposition is Reject';
  if (disposition === 'confirmed_by_AUT') return 'Pending review: AUT confirmation required';
  if (disposition === 'repaired') return 'Pending review: repaired indication needs reviewer acceptance';
  if (signalLimit && signal != null && signalLimit.max != null && signal > signalLimit.max) return `Failing: Signal ${signal} > ${signalLimit.max}${signalLimit.unit ? ` ${signalLimit.unit}` : ''}`;
  if (calNotchLimit && calNotch != null && calNotchLimit.min != null && calNotch < calNotchLimit.min) return `Failing: Cal notch ${calNotch} < ${calNotchLimit.min}${calNotchLimit.unit ? ` ${calNotchLimit.unit}` : ''}`;
  if (calNotchLimit && calNotch != null && calNotchLimit.max != null && calNotch > calNotchLimit.max) return `Failing: Cal notch ${calNotch} > ${calNotchLimit.max}${calNotchLimit.unit ? ` ${calNotchLimit.unit}` : ''}`;
  return null;
};

const calculateCoatingStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const dftMin = parseLimitNumber(measurement.coatingDftMinUm);
  const holidayVoltage = parseLimitNumber(measurement.coatingHolidayVoltageKv);
  const adhesion = parseLimitNumber(measurement.coatingAdhesionNcm);
  const cdRadius = parseLimitNumber(measurement.coatingCdRadiusMm);
  const anchorProfile = parseLimitNumber(measurement.coatingAnchorProfileUm);
  const holidays = parseLimitNumber(measurement.coatingHolidaysFound);

  if (holidays != null && holidays > 0) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'COATING', 'dft_min_um', dftMin),
    evaluateProjectLimit(limits, 'COATING', 'holiday_voltage_kv', holidayVoltage),
    evaluateProjectLimit(limits, 'COATING', 'adhesion_n_cm', adhesion),
    evaluateProjectLimit(limits, 'COATING', 'cd_radius_mm', cdRadius),
    evaluateProjectLimit(limits, 'COATING', 'anchor_profile_um', anchorProfile),
  ]);
};

const getCoatingStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const checks: Array<[string, string, string | undefined]> = [
    ['DFT minimum', 'dft_min_um', measurement.coatingDftMinUm],
    ['Holiday voltage', 'holiday_voltage_kv', measurement.coatingHolidayVoltageKv],
    ['Adhesion', 'adhesion_n_cm', measurement.coatingAdhesionNcm],
    ['CD radius', 'cd_radius_mm', measurement.coatingCdRadiusMm],
    ['Anchor profile', 'anchor_profile_um', measurement.coatingAnchorProfileUm],
  ];
  const holidays = parseLimitNumber(measurement.coatingHolidaysFound);

  if ((measurement.coatingHolidaysFound || '').trim() && holidays == null) return 'Pending review: Holidays found must be numeric';
  if (holidays != null && holidays > 0) return `Failing: Holidays found ${holidays} > 0`;
  for (const [label, fieldKey, rawValue] of checks) {
    const limit = getProjectLimit(limits, 'COATING', fieldKey);
    if (!limit) continue;
    const trimmedValue = (rawValue || '').trim();
    if (!trimmedValue) return `Pending review: ${label} is missing`;
    const value = parseLimitNumber(rawValue);
    if (value == null) return `Pending review: ${label} must be numeric`;
    if (limit.min != null && value < limit.min) return `Failing: ${label} ${value} < ${limit.min}${limit.unit ? ` ${limit.unit}` : ''}`;
    if (limit.max != null && value > limit.max) return `Failing: ${label} ${value} > ${limit.max}${limit.unit ? ` ${limit.unit}` : ''}`;
  }

  return null;
};

const calculateThreadStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const taper = parseLimitNumber(measurement.threadTaperMmPerM);
  const lead = parseLimitNumber(measurement.threadLeadMmCum);
  const height = parseLimitNumber(measurement.threadHeightMm);
  const odGauge = parseLimitNumber(measurement.threadPipeEndDiameterMm);
  const visualDamage = measurement.threadVisualDamage === 'yes';

  if (visualDamage) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'THREAD', 'thread_taper_mm_m', taper),
    evaluateProjectLimit(limits, 'THREAD', 'thread_lead_mm_cum', lead),
    evaluateProjectLimit(limits, 'THREAD', 'thread_height_mm', height),
    evaluateProjectLimit(limits, 'THREAD', 'thread_od_gauge_mm', odGauge),
  ]);
};

const getThreadStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const checks: Array<[string, string, string | undefined]> = [
    ['Taper', 'thread_taper_mm_m', measurement.threadTaperMmPerM],
    ['Lead', 'thread_lead_mm_cum', measurement.threadLeadMmCum],
    ['Height', 'thread_height_mm', measurement.threadHeightMm],
    ['OD@gauge', 'thread_od_gauge_mm', measurement.threadPipeEndDiameterMm],
  ];

  if (measurement.threadVisualDamage === 'yes') return 'Failing: Visual damage is marked Yes';
  for (const [label, fieldKey, rawValue] of checks) {
    const limit = getProjectLimit(limits, 'THREAD', fieldKey);
    if (!limit) continue;
    const trimmedValue = (rawValue || '').trim();
    if (!trimmedValue) return `Pending review: ${label} is missing`;
    const value = parseLimitNumber(rawValue);
    if (value == null) return `Pending review: ${label} must be numeric`;
    if (limit.min != null && value < limit.min) return `Failing: ${label} ${value} < ${limit.min}${limit.unit ? ` ${limit.unit}` : ''}`;
    if (limit.max != null && value > limit.max) return `Failing: ${label} ${value} > ${limit.max}${limit.unit ? ` ${limit.unit}` : ''}`;
  }

  return null;
};

const calculateWeldHeatInput = (measurement: MeasurementItem): string => {
  const manual = parseLimitNumber(measurement.weldHeatInputKjMm);
  if (manual != null) return manual.toFixed(2);

  const current = parseLimitNumber(measurement.weldCurrentA);
  const voltage = parseLimitNumber(measurement.weldVoltageV);
  const speed = parseLimitNumber(measurement.weldSpeedMmMin);
  if (current == null || voltage == null || speed == null || speed === 0) return '';

  return ((current * voltage * 60) / (speed * 1000)).toFixed(2);
};

const calculateWeldMapStatus = (measurement: MeasurementItem, limits: ProjectLimitLookup): string => {
  const heatInput = parseLimitNumber(calculateWeldHeatInput(measurement));
  const traceMatches = (measurement.weldPlateHeatNumber || '').trim() === (measurement.weldPipeHeatNumber || '').trim();
  const wpsLimit = getProjectLimit(limits, 'WELD_MAP', 'weld_wps_ref');
  const expectedWps = (wpsLimit?.minRaw || wpsLimit?.maxRaw || '').trim();
  const actualWps = (measurement.weldWpsRef || '').trim();

  if (heatInput == null || !measurement.weldPlateHeatNumber || !measurement.weldPipeHeatNumber || !actualWps) return 'PENDING_REVIEW';
  if (!traceMatches) return 'FAIL';
  if (expectedWps && actualWps && actualWps !== expectedWps) return 'FAIL';
  return combineStatuses([
    evaluateProjectLimit(limits, 'WELD_MAP', 'weld_heat_input_kj_mm', heatInput),
    getProjectLimit(limits, 'WELD_MAP', 'weld_plate_heat_match') ? 'PASS' : 'PENDING_REVIEW',
    getProjectLimit(limits, 'WELD_MAP', 'weld_wps_ref') ? 'PASS' : 'PENDING_REVIEW',
  ]);
};

const getWeldMapStatusReason = (measurement: MeasurementItem, limits: ProjectLimitLookup): string | null => {
  const heatInput = parseLimitNumber(calculateWeldHeatInput(measurement));
  const heatInputLimit = getProjectLimit(limits, 'WELD_MAP', 'weld_heat_input_kj_mm');
  const wpsLimit = getProjectLimit(limits, 'WELD_MAP', 'weld_wps_ref');
  const expectedWps = (wpsLimit?.minRaw || wpsLimit?.maxRaw || '').trim();
  const actualWps = (measurement.weldWpsRef || '').trim();
  const plateHeat = (measurement.weldPlateHeatNumber || '').trim();
  const pipeHeat = (measurement.weldPipeHeatNumber || '').trim();

  if (heatInput == null) return 'Pending review: Heat input is missing or cannot be calculated';
  if (heatInputLimit && heatInputLimit.min != null && heatInput < heatInputLimit.min) return `Failing: Heat input ${heatInput} < ${heatInputLimit.min}${heatInputLimit.unit ? ` ${heatInputLimit.unit}` : ''}`;
  if (heatInputLimit && heatInputLimit.max != null && heatInput > heatInputLimit.max) return `Failing: Heat input ${heatInput} > ${heatInputLimit.max}${heatInputLimit.unit ? ` ${heatInputLimit.unit}` : ''}`;
  if (!plateHeat) return 'Pending review: Plate heat is missing';
  if (!pipeHeat) return 'Pending review: Pipe heat is missing';
  if (plateHeat !== pipeHeat) return `Failing: Plate heat ${plateHeat} does not match pipe heat ${pipeHeat}`;
  if (!actualWps) return 'Pending review: WPS is missing';
  if (expectedWps && actualWps !== expectedWps) return `Failing: WPS ${actualWps} does not match ${expectedWps}`;
  return null;
};

const prepareMeasurementForSave = (measurement: MeasurementItem, limits: ProjectLimitLookup): MeasurementItem => {
  if (measurement.reportType === 'CHEMICAL') {
    const fieldLimit = getProjectLimit(limits, 'CHEMICAL', measurement.fieldPath);
    return {
      ...measurement,
      limit: fieldLimit ? formatProjectLimitText(fieldLimit) : measurement.limit,
      status: calculateChemicalStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'CVN') {
    return {
      ...measurement,
      fieldPath: 'cvn_specimen',
      fieldLabel: 'CVN impact specimen',
      measuredValue: measurement.cvnEnergyJ || '',
      unit: 'J',
      limit: getProjectLimitText(limits, 'CVN', ['avg_energy_j', 'single_energy_j', 'avg_shear_pct', 'test_temperature']),
      status: calculateCvnStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'AUT') {
    return {
      ...measurement,
      fieldPath: 'aut_indication',
      fieldLabel: 'AUT indication',
      measuredValue: measurement.autLengthMm || '',
      unit: 'mm',
      limit: getProjectLimitText(limits, 'AUT', ['indication_depth_pct', 'indication_length_mm']),
      status: calculateAutStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'HYDROSTATIC') {
    return {
      ...measurement,
      fieldPath: 'hydrostatic_test',
      fieldLabel: 'Hydrostatic test result',
      measuredValue: measurement.hydroTestPressureBar || '',
      unit: 'bar',
      limit: getProjectLimitText(limits, 'HYDROSTATIC', ['test_pressure_bar', 'hold_duration_s', 'pressure_drop_mpa', 'leakage_events']),
      status: calculateHydrostaticStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'DIMENSIONAL') {
    return {
      ...measurement,
      fieldPath: 'dimensional_inspection',
      fieldLabel: 'Dimensional inspection',
      measuredValue: measurement.dimOdAvgMm || '',
      unit: 'mm',
      limit: getProjectLimitText(limits, 'DIMENSIONAL', ['outer_diameter', 'wall_thickness', 'length_m', 'ovality_pct', 'straightness_mm_m']),
      status: calculateDimensionalStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'FLATTENING') {
    return {
      ...measurement,
      fieldPath: 'flattening_specimen',
      fieldLabel: 'Flattening specimen',
      measuredValue: measurement.flatteningHdReached || '',
      unit: 'H/D',
      limit: getProjectLimitText(limits, 'FLATTENING', ['flattening_height_h', 'crack_length_mm', 'lamination_observed']),
      status: calculateFlatteningStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'BEND') {
    return {
      ...measurement,
      fieldPath: 'bend_specimen',
      fieldLabel: 'Bend specimen',
      measuredValue: measurement.bendAngleDeg || '',
      unit: 'deg',
      limit: getProjectLimitText(limits, 'BEND', ['bend_angle_deg', 'open_defect_mm', 'mandrel_diameter_t']),
      status: calculateBendStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'HARDNESS') {
    return {
      ...measurement,
      fieldPath: 'hardness_reading',
      fieldLabel: 'Hardness reading',
      measuredValue: measurement.hardnessValueHv10 || '',
      unit: 'HV10',
      limit: getProjectLimitText(limits, 'HARDNESS', ['overall_max', 'body_hv10', 'weld_hv10', 'haz_hv10']),
      status: calculateHardnessStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'VISUAL') {
    return {
      ...measurement,
      fieldPath: 'visual_defect',
      fieldLabel: 'Visual defect',
      measuredValue: measurement.visualDepthPctWt || '',
      unit: '% WT',
      limit: getProjectLimitText(limits, 'VISUAL', ['visual_lighting_lux', 'visual_depth_pct_wt', 'visual_arc_strike', 'visual_crack']),
      status: calculateVisualStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'RT') {
    return {
      ...measurement,
      fieldPath: 'rt_indication',
      fieldLabel: 'RT indication',
      measuredValue: measurement.rtLengthMm || '',
      unit: 'mm',
      limit: getProjectLimitText(limits, 'RT', ['rt_iqi_sensitivity_pct', 'rt_exposure_class', 'rt_crack', 'rt_porosity_length_mm', 'rt_slag_length_mm']),
      status: calculateRtStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'MPT') {
    return {
      ...measurement,
      fieldPath: 'mpt_indication',
      fieldLabel: 'MPT indication',
      measuredValue: measurement.mptLengthMm || '',
      unit: 'mm',
      limit: getProjectLimitText(limits, 'MPT', ['mpt_field_strength_oe', 'mpt_uv_irradiance_uw_cm2', 'mpt_linear_length_mm', 'mpt_rounded_length_mm']),
      status: calculateMptStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'HEAT_TREAT') {
    return {
      ...measurement,
      fieldPath: 'heat_treat_cycle',
      fieldLabel: 'Heat treatment cycle',
      measuredValue: measurement.heatAustenitisingTempC || '',
      unit: 'C',
      limit: getProjectLimitText(limits, 'HEAT_TREAT', ['austenitising_temp_c', 'austenitising_hold_min', 'cooling_rate_cps', 'tempering_temp_c', 'tempering_hold_min']),
      status: calculateHeatTreatStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'PIPE_BODY_UT') {
    return {
      ...measurement,
      fieldPath: 'pipe_body_ut_indication',
      fieldLabel: 'Pipe body UT indication',
      measuredValue: measurement.pbutAreaMm2 || '',
      unit: 'mm2',
      limit: getProjectLimitText(limits, 'PIPE_BODY_UT', ['acceptance_level', 'pbut_lamination_area_mm2', 'pbut_end_zone_depth_pct_wt', 'pbut_inclusion_area_mm2']),
      status: calculatePipeBodyUtStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'MFL_ET') {
    return {
      ...measurement,
      fieldPath: 'mfl_et_indication',
      fieldLabel: 'MFL / ET indication',
      measuredValue: measurement.mflSignalPctRef || '',
      unit: '% ref',
      limit: getProjectLimitText(limits, 'MFL_ET', ['mfl_signal_pct_ref', 'mfl_cal_notch_pct_wt']),
      status: calculateMflEtStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'COATING') {
    return {
      ...measurement,
      fieldPath: 'coating_result',
      fieldLabel: 'Coating result',
      measuredValue: measurement.coatingDftMinUm || '',
      unit: 'um',
      limit: getProjectLimitText(limits, 'COATING', ['dft_min_um', 'holiday_voltage_kv', 'adhesion_n_cm', 'cd_radius_mm', 'anchor_profile_um']),
      status: calculateCoatingStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'THREAD') {
    return {
      ...measurement,
      fieldPath: 'thread_gauge_result',
      fieldLabel: 'Thread gauge result',
      measuredValue: measurement.threadTaperMmPerM || '',
      unit: 'mm/m',
      limit: getProjectLimitText(limits, 'THREAD', ['thread_taper_mm_m', 'thread_lead_mm_cum', 'thread_height_mm', 'thread_od_gauge_mm']),
      status: calculateThreadStatus(measurement, limits),
    };
  }

  if (measurement.reportType === 'WELD_MAP') {
    const heatInput = calculateWeldHeatInput(measurement);
    return {
      ...measurement,
      fieldPath: 'weld_map_pass',
      fieldLabel: 'Weld map pass',
      measuredValue: heatInput || measurement.weldHeatInputKjMm || '',
      unit: 'kJ/mm',
      limit: getProjectLimitText(limits, 'WELD_MAP', ['weld_heat_input_kj_mm', 'weld_plate_heat_match', 'weld_wps_ref']),
      status: calculateWeldMapStatus(measurement, limits),
      weldHeatInputKjMm: heatInput || measurement.weldHeatInputKjMm,
    };
  }

  if (measurement.reportType !== 'TENSILE') return measurement;

  const ratio = calculateTensileRatio(measurement);
  return {
    ...measurement,
    fieldPath: 'tensile_specimen',
    fieldLabel: 'Tensile specimen',
    measuredValue: ratio || measurement.tensileYsMpa || '',
    unit: '',
    limit: getProjectLimitText(limits, 'TENSILE', ['ys_mpa', 'uts_mpa', 'elongation_pct', 'ys_uts_ratio']),
    status: calculateTensileStatus(measurement, limits),
    tensileYsUtsRatio: ratio,
  };
};

const prepareImportedMeasurementForSave = (measurement: MeasurementItem, limits: ProjectLimitLookup): MeasurementItem => {
  const fieldLimit = getProjectLimit(limits, measurement.reportType, measurement.fieldPath);
  return {
    ...measurement,
    limit: fieldLimit ? formatProjectLimitText(fieldLimit) : measurement.limit,
    status: fieldLimit ? evaluateProjectLimit(limits, measurement.reportType, measurement.fieldPath, parseLimitNumber(measurement.measuredValue)) : 'PENDING_REVIEW',
  };
};

type ReportExcelColumn = {
  header: string;
  key: keyof MeasurementItem;
  width?: number;
  options?: string[];
  primary?: boolean;
  hidden?: boolean;
};

const EXCEL_ITEM_ID_COLUMN: ReportExcelColumn = { header: 'Item ID', key: 'id', width: 28, hidden: true };
const yesNoOptions = ['no', 'yes'];
const dispositionOptions = ['accept', 'repair', 'reject'];

const REPORT_EXCEL_COLUMNS: Partial<Record<FullReportType, ReportExcelColumn[]>> = {
  CHEMICAL: [
    { header: 'Chemical Element', key: 'fieldLabel', width: 32 },
    { header: 'Symbol', key: 'fieldSymbol', width: 14 },
    { header: 'Measured Value', key: 'measuredValue', width: 18, primary: true },
    { header: 'Unit', key: 'unit', width: 14 },
  ],
  TENSILE: [
    { header: 'Specimen ID', key: 'tensileSpecimenId', width: 16 },
    { header: 'Location', key: 'tensileLocation', width: 18, options: ['pipe_body', 'weld_seam', 'haz'] },
    { header: 'Orientation', key: 'tensileOrientation', width: 18, options: ['transverse', 'longitudinal'] },
    { header: 'Area mm2', key: 'tensileAreaMm2', width: 14 },
    { header: 'YS MPa', key: 'tensileYsMpa', width: 14, primary: true },
    { header: 'UTS MPa', key: 'tensileUtsMpa', width: 14 },
    { header: 'Elongation %', key: 'tensileElongationPct', width: 16 },
    { header: 'YS/UTS Ratio', key: 'tensileYsUtsRatio', width: 16 },
  ],
  CVN: [
    { header: 'Specimen ID', key: 'cvnSpecimenId', width: 16 },
    { header: 'Location', key: 'cvnLocation', width: 18, options: ['pipe_body', 'weld_seam', 'haz'] },
    { header: 'Notch Orientation', key: 'cvnNotchOrientation', width: 20, options: ['transverse', 'longitudinal'] },
    { header: 'Specimen Size', key: 'cvnSpecimenSize', width: 18, options: ['full', '3/4', '2/3', '1/2'] },
    { header: 'Energy J', key: 'cvnEnergyJ', width: 14, primary: true },
    { header: 'Shear %', key: 'cvnShearPct', width: 14 },
    { header: 'Lateral Expansion mm', key: 'cvnLateralExpansionMm', width: 22 },
  ],
  AUT: [
    { header: 'Indication ID', key: 'autIndicationId', width: 18 },
    { header: 'Position mm', key: 'autPositionMm', width: 16 },
    { header: 'Clock', key: 'autClock', width: 12 },
    { header: 'Flaw Type', key: 'autFlawType', width: 18, options: ['inclusion', 'porosity', 'slag line', 'lack of fusion', 'crack'] },
    { header: 'Depth % WT', key: 'autDepthPctWt', width: 14 },
    { header: 'Length mm', key: 'autLengthMm', width: 14, primary: true },
    { header: 'Disposition', key: 'autDisposition', width: 16, options: ['accept', 'repair', 'reject', 'monitor'] },
  ],
  HYDROSTATIC: [
    { header: 'Frequency Scope', key: 'frequency', width: 18, options: FREQUENCY_OPTIONS },
    { header: 'Test Pressure bar', key: 'hydroTestPressureBar', width: 20, primary: true },
    { header: 'Hold Duration s', key: 'hydroHoldDurationS', width: 18 },
    { header: 'Pressure Drop Observed', key: 'hydroPressureDropObserved', width: 24, options: yesNoOptions },
    { header: 'Leak Observed', key: 'hydroLeakObserved', width: 16, options: yesNoOptions },
    { header: 'Medium', key: 'hydroMedium', width: 16 },
    { header: 'Sealing', key: 'hydroSealingMethod', width: 18 },
    { header: 'Gauge Cal.', key: 'hydroGaugeCalRef', width: 20 },
    { header: 'Design Factor', key: 'hydroDesignFactor', width: 16 },
  ],
  DIMENSIONAL: [
    { header: 'OD Avg mm', key: 'dimOdAvgMm', width: 14, primary: true },
    { header: 'OD Min mm', key: 'dimOdMinMm', width: 14 },
    { header: 'OD Max mm', key: 'dimOdMaxMm', width: 14 },
    { header: 'Ovality %', key: 'dimOvalityPct', width: 14 },
    { header: 'WT End 1 N', key: 'dimWtEnd1N', width: 14 },
    { header: 'WT End 1 E', key: 'dimWtEnd1E', width: 14 },
    { header: 'WT End 1 S', key: 'dimWtEnd1S', width: 14 },
    { header: 'WT End 1 W', key: 'dimWtEnd1W', width: 14 },
    { header: 'WT End 2 N', key: 'dimWtEnd2N', width: 14 },
    { header: 'WT End 2 E', key: 'dimWtEnd2E', width: 14 },
    { header: 'WT End 2 S', key: 'dimWtEnd2S', width: 14 },
    { header: 'WT End 2 W', key: 'dimWtEnd2W', width: 14 },
    { header: 'Length m', key: 'dimLengthM', width: 14 },
    { header: 'Straightness mm', key: 'dimStraightnessMm', width: 18 },
    { header: 'Bevel Angle deg', key: 'dimBevelAngleDeg', width: 18 },
    { header: 'End Squareness mm', key: 'dimEndSquarenessMm', width: 20 },
  ],
  FLATTENING: [
    { header: 'Specimen ID', key: 'flatteningSpecimenId', width: 16 },
    { header: 'Weld Position', key: 'flatteningWeldPosition', width: 18, options: ['0deg', '90deg'] },
    { header: 'OD Before mm', key: 'flatteningOdBeforeMm', width: 16 },
    { header: 'H/D Reached', key: 'flatteningHdReached', width: 16, primary: true },
    { header: 'Cracks Observed', key: 'flatteningCracksObserved', width: 18, options: yesNoOptions },
  ],
  BEND: [
    { header: 'Specimen ID', key: 'bendSpecimenId', width: 16 },
    { header: 'Bend Type', key: 'bendType', width: 16, options: ['face_bend', 'root_bend'] },
    { header: 'Bend Angle deg', key: 'bendAngleDeg', width: 18, primary: true },
    { header: 'Crack Length mm', key: 'bendCrackLengthMm', width: 18 },
    { header: 'Fracture', key: 'bendFracture', width: 14, options: yesNoOptions },
    { header: 'Wall Thickness mm', key: 'bendWallThicknessMm', width: 20 },
    { header: 'Multiplier', key: 'bendMultiplier', width: 14 },
    { header: 'Mill Former mm', key: 'bendMillFormerMm', width: 18 },
  ],
  HARDNESS: [
    { header: 'Zone', key: 'hardnessZone', width: 18, options: ['weld cap', 'weld root', 'haz outer', 'haz inner', 'base metal'] },
    { header: 'Reading ID', key: 'hardnessReadingId', width: 16 },
    { header: 'Position', key: 'hardnessPosition', width: 16 },
    { header: 'Measured HV10', key: 'hardnessValueHv10', width: 18, primary: true },
    { header: 'Project Max HV10', key: 'hardnessProjectLimitHv10', width: 20 },
    { header: 'Fallback Max HV10', key: 'hardnessFallbackLimitHv10', width: 22 },
  ],
  VISUAL: [
    { header: 'Defect ID', key: 'visualDefectId', width: 16 },
    { header: 'Type', key: 'visualDefectType', width: 16, options: ['scale', 'groove', 'pit', 'arc_strike', 'crack'] },
    { header: 'Position mm', key: 'visualPositionMm', width: 16 },
    { header: 'Clock', key: 'visualClock', width: 12 },
    { header: 'Depth % WT', key: 'visualDepthPctWt', width: 16, primary: true },
    { header: 'Disposition', key: 'visualDisposition', width: 16, options: ['accept', 'repaired', 'reject', 'monitor'] },
    { header: 'Lighting lux', key: 'visualLightingLux', width: 16 },
  ],
  RT: [
    { header: 'Indication ID', key: 'rtIndicationId', width: 18 },
    { header: 'Position mm', key: 'rtPositionMm', width: 16 },
    { header: 'Flaw Type', key: 'rtFlawType', width: 18, options: ['porosity', 'slag', 'lack_of_fusion', 'crack'] },
    { header: 'Length mm', key: 'rtLengthMm', width: 14, primary: true },
    { header: 'Area mm2', key: 'rtAreaMm2', width: 14 },
    { header: 'Disposition', key: 'rtDisposition', width: 16, options: dispositionOptions },
    { header: 'Exposure Class', key: 'rtExposureClass', width: 16, options: ['B', 'A'] },
    { header: 'IQI Sensitivity %', key: 'rtIqiSensitivityPct', width: 20 },
  ],
  MPT: [
    { header: 'Indication ID', key: 'mptIndicationId', width: 18 },
    { header: 'End Face', key: 'mptEnd', width: 18, options: ['End 1 - PE1', 'End 2 - PE2'] },
    { header: 'Type', key: 'mptType', width: 16, options: ['rounded', 'linear', 'non_relevant'] },
    { header: 'Length mm', key: 'mptLengthMm', width: 14, primary: true },
    { header: 'Disposition', key: 'mptDisposition', width: 16, options: dispositionOptions },
    { header: 'Field Strength Oe', key: 'mptFieldStrengthOe', width: 20 },
    { header: 'UV-A uW/cm2', key: 'mptUvIrradiance', width: 18 },
  ],
  HEAT_TREAT: [
    { header: 'Batch ID', key: 'heatBatchId', width: 16 },
    { header: 'Austenitising Temp C', key: 'heatAustenitisingTempC', width: 24, primary: true },
    { header: 'Austenitising Hold min', key: 'heatAustenitisingHoldMin', width: 24 },
    { header: 'Cooling Rate C/s', key: 'heatCoolingRateCps', width: 20 },
    { header: 'Tempering Temp C', key: 'heatTemperingTempC', width: 20 },
    { header: 'Tempering Hold min', key: 'heatTemperingHoldMin', width: 22 },
    { header: 'Pipes in Batch', key: 'heatPipesInBatch', width: 18 },
  ],
  PIPE_BODY_UT: [
    { header: 'Indication ID', key: 'pbutIndicationId', width: 18 },
    { header: 'Type', key: 'pbutIndicationType', width: 16, options: ['lamination', 'inclusion'] },
    { header: 'Position mm', key: 'pbutPositionMm', width: 16 },
    { header: 'Clock', key: 'pbutClock', width: 12 },
    { header: 'Area mm2', key: 'pbutAreaMm2', width: 14, primary: true },
    { header: 'Depth % WT', key: 'pbutDepthPctWt', width: 16 },
    { header: 'End Zone', key: 'pbutEndZone', width: 14, options: yesNoOptions },
    { header: 'Disposition', key: 'pbutDisposition', width: 16, options: dispositionOptions },
  ],
  MFL_ET: [
    { header: 'Indication ID', key: 'mflIndicationId', width: 18 },
    { header: 'Flaw Type', key: 'mflFlawType', width: 18, options: ['hook_crack', 'seam_crack', 'lamination', 'wall_loss'] },
    { header: 'Signal % Ref', key: 'mflSignalPctRef', width: 16, primary: true },
    { header: 'Disposition', key: 'mflDisposition', width: 20, options: ['accept', 'confirmed_by_AUT', 'repaired', 'reject'] },
    { header: 'Cal Notch % WT', key: 'mflCalNotchPctWt', width: 18 },
    { header: 'Route', key: 'mflRoute', width: 14, options: ['ERW/HFW', 'SAWL'] },
  ],
  COATING: [
    { header: 'Coating Type', key: 'coatingType', width: 16, options: ['3LPE', 'FBE'] },
    { header: 'DFT Min um', key: 'coatingDftMinUm', width: 16, primary: true },
    { header: 'DFT Avg um', key: 'coatingDftAvgUm', width: 16 },
    { header: 'Adhesion N/cm', key: 'coatingAdhesionNcm', width: 18 },
    { header: 'CD Radius mm', key: 'coatingCdRadiusMm', width: 16 },
    { header: 'Anchor Profile um', key: 'coatingAnchorProfileUm', width: 20 },
    { header: 'Holiday Voltage kV', key: 'coatingHolidayVoltageKv', width: 22 },
    { header: 'Holidays Found', key: 'coatingHolidaysFound', width: 18 },
    { header: 'Cutback Length mm', key: 'coatingCutbackLengthMm', width: 22 },
    { header: 'Surface Prep', key: 'coatingSurfacePrepGrade', width: 18 },
  ],
  THREAD: [
    { header: 'Thread Type', key: 'threadType', width: 18 },
    { header: 'Standard', key: 'threadStandard', width: 16 },
    { header: 'Gauge Set', key: 'threadGaugeSetRef', width: 18 },
    { header: 'End Tested', key: 'threadEndTested', width: 16, options: ['both', 'end_1', 'end_2'] },
    { header: 'Taper mm/m', key: 'threadTaperMmPerM', width: 16, primary: true },
    { header: 'Lead mm cum', key: 'threadLeadMmCum', width: 16 },
    { header: 'Height mm', key: 'threadHeightMm', width: 14 },
    { header: 'Pipe End Diameter mm', key: 'threadPipeEndDiameterMm', width: 24 },
    { header: 'Coupling Applied', key: 'threadCouplingApplied', width: 20, options: yesNoOptions },
    { header: 'Visual Damage', key: 'threadVisualDamage', width: 18, options: yesNoOptions },
  ],
  WELD_MAP: [
    { header: 'Pass', key: 'weldPassName', width: 16 },
    { header: 'Process', key: 'weldProcess', width: 14, options: ['GMAW', 'SAW'] },
    { header: 'Wire Heat', key: 'weldWireHeatNo', width: 18 },
    { header: 'Flux Lot', key: 'weldFluxLotNo', width: 18 },
    { header: 'Current A', key: 'weldCurrentA', width: 14 },
    { header: 'Voltage V', key: 'weldVoltageV', width: 14 },
    { header: 'Speed mm/min', key: 'weldSpeedMmMin', width: 18 },
    { header: 'Heat Input kJ/mm', key: 'weldHeatInputKjMm', width: 20, primary: true },
    { header: 'Plate ID', key: 'weldPlateId', width: 20 },
    { header: 'Plate Heat', key: 'weldPlateHeatNumber', width: 18 },
    { header: 'Pipe Heat', key: 'weldPipeHeatNumber', width: 18 },
    { header: 'WPS / PQR', key: 'weldWpsRef', width: 22 },
  ],
};

const getReportExcelColumns = (reportType: FullReportType): ReportExcelColumn[] =>
  REPORT_EXCEL_COLUMNS[reportType] || [];

const createExcelMeasurementBase = (reportType: FullReportType, id: string, frequency?: string, reference?: string): MeasurementItem => ({
  id,
  reportType,
  fieldPath: 'excel_import',
  fieldLabel: 'Excel import',
  measuredValue: '',
  unit: '',
  frequency: frequency || 'per_pipe',
  limit: '',
  source: 'EXCEL_UPLOAD',
  reference: reference || '',
  status: 'PENDING_REVIEW',
});

const normalizeExcelKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const getExcelValue = (row: Record<string, unknown>, labels: string[]): string => {
  const normalizedLabels = labels.map(normalizeExcelKey);
  const match = Object.entries(row).find(([key]) => normalizedLabels.includes(normalizeExcelKey(key)));
  const value = match?.[1];
  return value == null ? '' : String(value).trim();
};

const getTemplateFields = (projectLimitFields?: ProjectLimitFieldOption[]): ProjectLimitFieldOption[] =>
  projectLimitFields?.length ? projectLimitFields : getFieldOptionsForReport();

const getMeasurementTemplateFields = (reportType: FullReportType, projectLimitFields?: ProjectLimitFieldOption[]): ProjectLimitFieldOption[] =>
  mergeProjectLimitFieldOptions(reportType, getTemplateFields(projectLimitFields))
    .filter((field) => reportType !== 'CHEMICAL' || !isChemicalDerivedField(field));

const CHEMICAL_ELEMENT_NAMES: Record<string, string> = {
  B: 'Boron',
  C: 'Carbon',
  CE: 'Carbon Equivalent',
  Cr: 'Chromium',
  Cu: 'Copper',
  Mn: 'Manganese',
  Mo: 'Molybdenum',
  Nb: 'Niobium',
  Ni: 'Nickel',
  P: 'Phosphorus',
  S: 'Sulfur',
  Si: 'Silicon',
  Ti: 'Titanium',
  V: 'Vanadium',
};

const getChemicalElementName = (field: ProjectLimitFieldOption): string => {
  const symbol = field.symbol || field.fieldPath.replace(/^elements\./, '');
  return CHEMICAL_ELEMENT_NAMES[symbol] || field.label;
};

const downloadReportExcelTemplate = async (
  reportType: FullReportType,
  reportLabel: string,
  projectLimitFields?: ProjectLimitFieldOption[],
  existingMeasurements: MeasurementItem[] = []
) => {
  const fields = getMeasurementTemplateFields(reportType, projectLimitFields);
  const reportColumns = getReportExcelColumns(reportType);
  const exportColumns = [EXCEL_ITEM_ID_COLUMN, ...reportColumns];
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Report Items');
  const optionsWorksheet = workbook.addWorksheet('Field Options');
  const templateRows = existingMeasurements.length || (reportType === 'CHEMICAL' ? Math.max(fields.length, 10) : 10);

  worksheet.columns = exportColumns.map((column) => ({
    header: column.header,
    key: String(column.key),
    width: column.width || Math.max(column.header.length + 4, 16),
    hidden: column.hidden,
  }));

  exportColumns.forEach((column, columnIndex) => {
    const options = reportType === 'CHEMICAL' && column.key === 'fieldLabel'
      ? fields.map(getChemicalElementName)
      : column.options;
    options?.forEach((option, optionIndex) => {
      optionsWorksheet.getCell(optionIndex + 1, columnIndex + 1).value = option;
    });
  });
  optionsWorksheet.state = 'veryHidden';

  for (let index = 0; index < templateRows; index += 1) {
    const existingMeasurement = existingMeasurements[index];
    const field = reportType === 'CHEMICAL' ? fields[index] : undefined;
    const baseRow = existingMeasurement
      ? exportColumns.reduce((row, column) => ({
        ...row,
        [column.key]: String(existingMeasurement[column.key] ?? ''),
      }), {} as Record<string, string>)
      : reportType === 'CHEMICAL'
        ? {
          fieldLabel: field ? getChemicalElementName(field) : '',
          fieldSymbol: field?.symbol || '',
          measuredValue: '',
          unit: field?.unit || '',
        }
        : {};
    worksheet.addRow(baseRow);
    if (!existingMeasurement && reportType === 'CHEMICAL') {
      worksheet.getCell(index + 2, 2).value = field ? getChemicalElementName(field) : '';
      worksheet.getCell(index + 2, 3).value = field?.symbol || '';
      worksheet.getCell(index + 2, 5).value = field?.unit || '';
    }
    exportColumns.forEach((column, columnIndex) => {
      const optionCount = reportType === 'CHEMICAL' && column.key === 'fieldLabel'
        ? fields.length
        : column.options?.length || 0;
      if (!optionCount) return;
      worksheet.getCell(index + 2, columnIndex + 1).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`'Field Options'!$${String.fromCharCode(65 + columnIndex)}$1:$${String.fromCharCode(65 + columnIndex)}$${optionCount}`],
        showErrorMessage: true,
        errorTitle: 'Invalid value',
        error: `Select ${column.header} from the dropdown list.`,
      };
    });
  }

  const instructionSheet = workbook.addWorksheet('Instructions');
  instructionSheet.addRows([
    ['Instruction'],
    [`Fill one ${reportLabel} item per row using this report-specific template.`],
    ['Existing rows include a hidden Item ID column. Keep it unchanged when editing saved rows so upload updates the same DB item.'],
    ['Project limits and frequency rules are not saved in this file; the UI reads them from the project-limit APIs.'],
  ]);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${reportType}_${reportLabel.replace(/[^a-z0-9]+/gi, '_')}_${existingMeasurements.length ? 'export' : 'template'}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
};

const parseReportExcelItems = async (
  file: File,
  reportType: FullReportType,
  frequency?: string,
  projectLimitFields?: ProjectLimitFieldOption[]
): Promise<MeasurementItem[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = firstSheetName ? workbook.Sheets[firstSheetName] : undefined;
  if (!worksheet) return [];

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
  const fields = getMeasurementTemplateFields(reportType, projectLimitFields);
  const reportColumns = getReportExcelColumns(reportType);
  if (!reportColumns.length) return [];

  return rows
    .map((row, index) => {
      const hasValue = reportColumns.some((column) => getExcelValue(row, [column.header]));
      if (!hasValue) return null;

      const existingItemId = getExcelValue(row, [EXCEL_ITEM_ID_COLUMN.header, 'ID', 'Item Id']);
      const measurement = createExcelMeasurementBase(reportType, existingItemId || `excel-${reportType}-${Date.now()}-${index}`, frequency, file.name);
      reportColumns.forEach((column) => {
        (measurement as Record<string, unknown>)[column.key] = getExcelValue(row, [column.header]);
      });

      if (reportType === 'CHEMICAL') {
        const chemicalField = fields.find((field) =>
          getChemicalElementName(field) === measurement.fieldLabel && (!measurement.fieldSymbol || field.symbol === measurement.fieldSymbol)
        ) || fields.find((field) => getChemicalElementName(field) === measurement.fieldLabel) || fields[index] || fields[0];
        measurement.fieldPath = chemicalField.fieldPath;
        measurement.fieldLabel = getChemicalElementName(chemicalField);
        measurement.fieldSymbol = chemicalField.symbol;
        measurement.unit = measurement.unit || chemicalField.unit || '';
      }

      const primaryColumn = reportColumns.find((column) => column.primary);
      const primaryValue = primaryColumn ? getExcelValue(row, [primaryColumn.header]) : '';
      return {
        ...measurement,
        measuredValue: primaryValue || measurement.measuredValue || '',
      } as MeasurementItem;
    })
    .filter(Boolean) as MeasurementItem[];
};

const statusNoteClassName = (status?: string) => cn(
  'rounded-md px-2 py-1 text-[11px]',
  status === 'FAIL' && 'bg-[rgba(196,0,31,0.06)] text-readable-red',
  status === 'PENDING_REVIEW' && 'bg-[rgba(230,161,0,0.10)] text-[#8A6500]',
  status !== 'FAIL' && status !== 'PENDING_REVIEW' && 'bg-page-bg text-heritage-grey'
);

const Detail: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
    <div className={cn('mt-1 text-[12px] font-medium text-text-grey', mono && 'font-mono')}>{value}</div>
  </div>
);

const inputClassName =
  'h-10 w-full rounded-lg border border-[#E2E5EA] bg-white px-3 text-[13px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block space-y-1">
    <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-heritage-grey">{label}</span>
    {children}
  </label>
);

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDisplayDate = (value?: string | null): string => {
  if (!value) return '-';
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!isoMatch) return value;

  const [, year, month, day] = isoMatch;
  return `${day}-${MONTH_LABELS[Number(month) - 1] || month}-${year}`;
};

const getMeasurementSymbol = (measurement: MeasurementItem): string =>
  measurement.fieldSymbol || measurement.fieldPath.split('.').pop() || measurement.fieldLabel;

const resolveReportStatusFromMeasurements = (report: MockReport, measurements: MeasurementItem[]): string => {
  if (!measurements.length) return report.status;
  if (measurements.some((item) => item.status === 'FAIL')) return 'FAIL';
  if (measurements.some((item) => item.status === 'PENDING_REVIEW')) return 'PENDING_REVIEW';
  if (measurements.some((item) => item.status === 'INCOMPLETE')) return 'INCOMPLETE';
  return 'PASS';
};

const getReportCardSummary = (reportType: FullReportType, report: MockReport, measurements: MeasurementItem[] = []) => {
  const reportMeasurements = measurements.filter((item) => item.reportType === reportType);
  const measurementStats = reportMeasurements.length
    ? reportMeasurements.slice(-3).reverse().map((measurement) => ({
      label: getMeasurementSymbol(measurement),
      value: `${measurement.measuredValue || '-'}${measurement.unit ? ` ${measurement.unit}` : ''}`,
      sub: measurement.limit,
    }))
    : null;

  if (measurementStats) {
    return {
      eyebrow: reportType === 'CHEMICAL' ? 'Heat chemistry' : report.source ? formatLabel(report.source) : 'Saved report items',
      stats: measurementStats,
      note: `${reportMeasurements.length} saved items`,
      chart: true,
    };
  }

  return {
    eyebrow: report.source ? formatLabel(report.source) : 'No DB report items',
    stats: [
      { label: 'Items', value: '0', sub: 'saved in DB' },
      { label: 'Status', value: formatLabel(report.status || 'INCOMPLETE'), sub: 'DB' },
      { label: 'Freq.', value: formatLabel(REPORT_TYPES.find((item) => item.key === reportType)?.freq || '-') },
    ],
    note: report.note || 'No report items saved in DB yet.',
    chart: false,
  };

};

const getStandardReportSummaryStats = (
  reportType: FullReportType,
  detail: StandardReportDetailData,
  passCount: number,
  issueCount: number
): Array<{ label: string; value: React.ReactNode; sub?: string }> => {
  if (reportType === 'VISUAL') {
    return [
      { label: 'Surface', value: '0', sub: 'rejectable' },
      { label: 'End prep', value: 'OK', sub: 'bevel/end face' },
      { label: 'Marking', value: 'OK', sub: 'trace readable' },
    ];
  }

  if (reportType === 'THREAD') {
    return [
      { label: 'End type', value: 'Bevelled', sub: 'project' },
      { label: 'Threading', value: 'No', sub: 'required' },
      { label: 'Impact', value: 'N/A', sub: 'not required' },
    ];
  }

  if (reportType === 'PIPE_BODY_UT') {
    return [
      { label: 'Coverage', value: '100%', sub: 'scan' },
      { label: 'Min WT', value: '22.1 mm', sub: 'verified' },
      { label: 'Rejects', value: '0', sub: 'laminations' },
    ];
  }

  if (reportType === 'COATING') {
    return [
      { label: 'Transfer', value: 'No', sub: 'awaiting' },
      { label: 'DFT min', value: '0 um', sub: 'not received' },
      { label: 'Holiday', value: '0', sub: 'pending' },
    ];
  }

  if (reportType === 'WELD_MAP') {
    return [
      { label: 'WPS', value: 'Linked', sub: 'approved' },
      { label: 'Seam', value: '12.18 m', sub: 'mapped' },
      { label: 'Repairs', value: '0', sub: 'open' },
    ];
  }

  return [
    { label: 'Checks', value: detail.checks.length, sub: 'configured' },
    { label: 'Passing', value: passCount, sub: 'checks' },
    { label: 'Issues', value: issueCount, sub: 'open' },
  ];
};

export const PipeMaterialReportModal: React.FC<PipeMaterialReportModalProps> = ({
  isOpen,
  onClose,
  selectedMaterial,
  selectedPipe,
  availableLotNumbers = [],
  onReportsChanged,
  initialReportType,
}) => {
  const {
    reports,
    metadataByType: savedMetadataByType = {},
    metadataLoadingByType = {},
    projectLimits,
    reportsLoading,
    projectLimitsLoading,
    frequencyRules,
    frequencyRulesLoading,
    saving,
    savingScope,
    saveSuccess,
    lastSaved,
    fetchReports,
    fetchMetadata,
    fetchProjectLimits,
    fetchFrequencyRules,
    saveBulkReports,
    saveReportItem,
    saveReportStatus,
    deleteReportItem,
    clearSaveStatus,
  } = usePipeMillReport();
  const details = selectedMaterial?.details || {};
  const extraDetails = selectedMaterial?.materialExtraDetails || {};
  const materialAny = selectedMaterial as any;
  const heatNumber = details.pipeHeatNumber || details.heatNumber || extraDetails.heatNumber || 'J4K-5272';
  const lotNumber = details.pipeLotNumber || details.lotNumber || details.lotNo || extraDetails.pipeLotNumber || extraDetails.lotNumber || extraDetails.lotNo || '';
  const pipeNumber = details.pipeNumber || details.pipeNo || extraDetails.pipeNumber || selectedMaterial?.materialId || '30858';
  const pipeNri = details.pipeNRI || details.pipeNri || extraDetails.pipeNRI || 'NRI-U-11874';
  const projectId = getNumericId(materialAny?.projectId ?? materialAny?.project_id ?? selectedPipe?.projectId ?? selectedPipe?.project_id);
  const pipeId = getNumericId(materialAny?.pipeId ?? materialAny?.pipe_id ?? selectedPipe?.pipeId ?? selectedPipe?.pipe_id ?? selectedPipe?.id);
  const pipeSizeLabel =
    selectedPipe?.displayName ||
    `${valueOrDash(selectedPipe?.pipeName)} (${valueOrDash(selectedPipe?.size)}" x ${valueOrDash(selectedPipe?.wallThickness)}")`;

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReportType, setSelectedReportType] = useState<FullReportType>('CHEMICAL');
  const [addReportType, setAddReportType] = useState<FullReportType>('HYDROSTATIC');
  const [detailReportType, setDetailReportType] = useState<FullReportType | null>(null);
  const [isProjectLimitsModalOpen, setIsProjectLimitsModalOpen] = useState(false);
  const [isReportMetadataModalOpen, setIsReportMetadataModalOpen] = useState(false);
  const [reportMetadataForm, setReportMetadataForm] = useState<ReportMetadataEntry>(() => createReportMetadataForm('HYDROSTATIC'));
  const [reportMetadataByType, setReportMetadataByType] = useState<Partial<Record<FullReportType, ReportMetadataEntry>>>({});
  const [measurements, setMeasurements] = useState<MeasurementItem[]>([]);
  const [deletedMeasurementIds, setDeletedMeasurementIds] = useState<string[]>([]);
  const [isMeasurementModalOpen, setIsMeasurementModalOpen] = useState(false);
  const [measurementForm, setMeasurementForm] = useState<MeasurementItem>(() => createMeasurementForm('HYDROSTATIC'));
  const [editingMeasurementId, setEditingMeasurementId] = useState<string | null>(null);
  const [measurementToDelete, setMeasurementToDelete] = useState<MeasurementItem | null>(null);
  const [customFieldOptionsByType, setCustomFieldOptionsByType] = useState<Partial<Record<FullReportType, ProjectLimitFieldOption[]>>>({});
  const [isFieldOptionModalOpen, setIsFieldOptionModalOpen] = useState(false);
  const [pdfTarget, setPdfTarget] = useState<PdfTarget | null>(null);
  const [pdfWidthPercent, setPdfWidthPercent] = useState(55);
  const lastHandledSaveAtRef = useRef<number | null>(null);
  const [fieldOptionForm, setFieldOptionForm] = useState<ProjectLimitFieldOption>({
    fieldPath: '',
    symbol: '',
    label: '',
    unit: '',
    limit: '',
  });
  const materialPdfDocument = useMemo(() => getFirstMaterialPdfDocument(selectedMaterial), [selectedMaterial]);
  const projectLimitLookup = useMemo(
    () => buildProjectLimitLookup(Array.isArray(projectLimits) ? projectLimits : []),
    [projectLimits]
  );
  const projectLimitFieldOptionsByReport = useMemo(
    () => buildProjectLimitFieldOptions(Array.isArray(projectLimits) ? projectLimits : []),
    [projectLimits]
  );
  const frequencyByReport = useMemo(() => (
    Array.isArray(frequencyRules)
      ? frequencyRules.reduce((acc, rule: any) => {
        const projectLimitId = rule.projectLimitId || rule.project_limit_id;
        const matchingLimit = Array.isArray(projectLimits)
          ? projectLimits.find((limit: any) => String(limit.id) === String(projectLimitId))
          : undefined;
        const reportType =
          rule.projectLimit?.reportType ||
          rule.projectLimit?.report_type ||
          rule.reportType ||
          rule.report_type ||
          rule.report ||
          matchingLimit?.reportType ||
          matchingLimit?.report_type;
        const frequency = rule.frequency || rule.frequencyType || rule.frequency_type;
        if (reportType && frequency) acc[reportType as FullReportType] = String(frequency);
        return acc;
      }, {} as Partial<Record<FullReportType, string>>)
      : {}
  ), [frequencyRules, projectLimits]);
  const savedReports = useMemo(
    () => (Array.isArray(reports)
      ? (reports as SavedPipeMaterialReport[]).filter((report) => savedReportMatchesScope(report, heatNumber, lotNumber, pipeNumber))
      : []),
    [heatNumber, lotNumber, pipeNumber, reports]
  );
  const isReportDataLoading = Boolean(
    reportsLoading ||
    projectLimitsLoading ||
    frequencyRulesLoading ||
    Object.values(metadataLoadingByType || {}).some(Boolean)
  );

  useEffect(() => {
    if (!isOpen || !projectId) return;
    setMeasurements([]);
    setDeletedMeasurementIds([]);
    setReportMetadataByType({});
    const scope = {
      projectId,
      ...(pipeId ? { pipeId } : {}),
    };
    fetchReports({
      ...scope,
      heatNumber,
    });
    fetchMetadata({
      ...scope,
      heatNumber,
    });
    fetchProjectLimits(scope);
    fetchFrequencyRules(scope);
    // Redux hook action creators are not memoized; reload only when modal scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatNumber, isOpen, pipeId, pipeNumber, projectId]);

  useEffect(() => {
    const savedAt = lastSaved?.at;
    const savedScope = lastSaved?.scope;
    const refreshScopes = new Set(['report', 'bulkReports', 'reportItem', 'reportStatus', 'reportDelete', 'reportItemDelete']);
    if (!isOpen || !saveSuccess || !savedAt || lastHandledSaveAtRef.current === savedAt || !refreshScopes.has(savedScope)) return;

    lastHandledSaveAtRef.current = savedAt;
    onReportsChanged?.();
    clearSaveStatus();
  }, [clearSaveStatus, isOpen, lastSaved?.at, lastSaved?.scope, onReportsChanged, saveSuccess]);

  useEffect(() => {
    if (!isOpen || !initialReportType) return;
    const normalizedReportType = String(initialReportType).toUpperCase().replace(/\s+/g, '_') as FullReportType;
    if (!REPORT_TYPES.some((reportType) => reportType.key === normalizedReportType)) return;

    setSelectedReportType(normalizedReportType);
    setStatusFilter('all');
    setQuery('');
    setDetailReportType(normalizedReportType);
  }, [initialReportType, isOpen]);

  useEffect(() => {
    const savedMeasurements = savedReports.flatMap((report) => {
      const reportType = getReportTypeFromSavedReport(report);
      if (!reportType) return [];
      return getReportDetailsFromSavedReport(report).map((item) => ({
        ...item,
        reportType: item.reportType || reportType,
      }));
    });

    if (savedMeasurements.length > 0) {
      setMeasurements(savedMeasurements);
      setDeletedMeasurementIds([]);
    } else {
      setMeasurements([]);
      setDeletedMeasurementIds([]);
    }
  }, [savedReports]);

  useEffect(() => {
    const normalized = Object.values(savedMetadataByType)
      .map(normalizeReportMetadata)
      .filter((metadata) => metadata && savedReportMatchesScope(metadata, heatNumber, lotNumber, pipeNumber))
      .filter(Boolean) as ReportMetadataEntry[];
    setReportMetadataByType((previous) => {
      const scopedPrevious = Object.fromEntries(Object.entries(previous).filter(([, metadata]) => (
        metadata && savedReportMatchesScope(metadata, heatNumber, lotNumber, pipeNumber)
      ))) as Partial<Record<FullReportType, ReportMetadataEntry>>;

      return normalized.reduce((acc, metadata) => ({
        ...acc,
      [metadata.reportType]: metadata,
      }), scopedPrevious);
    });
  }, [heatNumber, lotNumber, pipeNumber, savedMetadataByType]);

  const getAvailableFieldOptions = (reportType: FullReportType): ProjectLimitFieldOption[] => [
    ...mergeProjectLimitFieldOptions(
      reportType,
      projectLimitFieldOptionsByReport[reportType]?.length ? projectLimitFieldOptionsByReport[reportType]! : getFieldOptionsForReport()
    ).filter((field) => reportType !== 'CHEMICAL' || !isChemicalDerivedField(field)),
    ...(customFieldOptionsByType[reportType] || []),
  ];

  const isLegacyDefaultSavedReport = (report?: SavedPipeMaterialReport): boolean => {
    const details = report?.extraDetails || report?.extra_details || {};
    const note = String((details as any)?.note || '').toLowerCase();
    const restoredFrom = String((details as any)?.restoredFrom || '').toLowerCase();
    return note.includes('default report restored') || restoredFrom.includes('mock');
  };

  const reportRows = useMemo(() => REPORT_TYPES.filter((reportType) => {
    const reportMeasurements = measurements.filter((item) => item.reportType === reportType.key);
    const savedReport = savedReports.find((report) => getReportTypeFromSavedReport(report) === reportType.key);
    const metadata = reportMetadataByType[reportType.key];
    const hasLocalDraftMetadata = Boolean(metadata && !metadata.id && !metadata.metadataId);
    return Boolean((savedReport && !isLegacyDefaultSavedReport(savedReport)) || reportMeasurements.length || hasLocalDraftMetadata);
  }).map((reportType) => {
    const reportMeasurements = measurements.filter((item) => item.reportType === reportType.key);
    const savedReport = savedReports.find((report) => getReportTypeFromSavedReport(report) === reportType.key);
    const savedReportStatus = savedReport?.overallStatus || savedReport?.overall_status;
    const savedReportExtraDetails = savedReport?.extraDetails || savedReport?.extra_details || {};
    const savedReportNote =
      (savedReportExtraDetails as any)?.adjudication?.reviewerNote ||
      (savedReportExtraDetails as any)?.reviewerNote ||
      (savedReportExtraDetails as any)?.note;
    const baseReport = savedReport
      ? {
        status: savedReportStatus || 'INCOMPLETE',
        updated: savedReport?.updatedAt || savedReport?.updated_at || null,
        source: (savedReportExtraDetails as any)?.source || null,
        note: savedReportNote || '',
      }
      : reportMetadataByType[reportType.key]
      ? {
        status: reportMeasurements.length ? 'PENDING_REVIEW' : 'INCOMPLETE',
        updated: reportMetadataByType[reportType.key]?.performedAt || null,
        source: reportMetadataByType[reportType.key]?.source || null,
        note: reportMetadataByType[reportType.key]?.note || 'User-created report metadata. Add measured fields from project limits.',
      }
      : createEmptyDbReport();

    return {
      ...reportType,
      freq: frequencyByReport[reportType.key] || reportType.freq,
      report: {
        ...baseReport,
        status: savedReportStatus || resolveReportStatusFromMeasurements(baseReport, reportMeasurements),
        note: savedReportNote || (reportMeasurements.length ? `${reportMeasurements.length} saved items` : baseReport.note),
      },
    };
  }), [frequencyByReport, reportMetadataByType, measurements, savedReports]);

  const counts = useMemo(() => reportRows.reduce(
    (acc, row) => ({
      ...acc,
      [row.report.status]: (acc[row.report.status] || 0) + 1,
    }),
    { all: reportRows.length } as Record<string, number>
  ), [reportRows]);

  const filteredReports = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return reportRows.filter((row) => {
      const matchesStatus = statusFilter === 'all' || row.report.status === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        row.key.toLowerCase().includes(normalizedQuery) ||
        row.label.toLowerCase().includes(normalizedQuery) ||
        (row.report.source || '').toLowerCase().includes(normalizedQuery) ||
        (row.report.note || '').toLowerCase().includes(normalizedQuery);
      return matchesStatus && matchesQuery;
    });
  }, [reportRows, query, statusFilter]);

  const getCurrentReportScope = (reportType: FullReportType): ReportScope => {
    const metadata = reportMetadataByType[reportType];
    const frequency = metadata?.frequency || frequencyByReport[reportType] || REPORT_TYPES.find((report) => report.key === reportType)?.freq || 'per_pipe';
    return getReportScope(
      frequency,
      metadata?.heatNumber ?? heatNumber,
      metadata?.lotNumber ?? lotNumber,
      metadata?.pipeNumber ?? pipeNumber
    );
  };

  const findSavedReport = (reportType: FullReportType): SavedPipeMaterialReport | undefined => {
    const scope = getCurrentReportScope(reportType);
    return savedReports.find((report) => reportMatchesScope(report, reportType, scope));
  };

  const availableReportLotNumbers = useMemo(() => {
    const baseOptions = Array.from(new Set([
      ...availableLotNumbers,
      lotNumber,
      reportMetadataForm.lotNumber || '',
    ]
      .map((value) => String(value || '').trim())
      .filter((value) => value && value !== '-')));

    if (!(reportMetadataForm.frequency === 'per_lot' || reportMetadataForm.frequency === 'per_batch' || reportMetadataForm.frequency === 'per_shift')) {
      return baseOptions;
    }

    return baseOptions.filter((option) => {
      if (option === reportMetadataForm.lotNumber) return true;
      const scope = getReportScope(reportMetadataForm.frequency, reportMetadataForm.heatNumber ?? heatNumber, option, pipeNumber);
      return !savedReports.some((report) => reportMatchesScope(report, reportMetadataForm.reportType, scope));
    });
  }, [availableLotNumbers, heatNumber, lotNumber, pipeNumber, reportMetadataForm.frequency, reportMetadataForm.heatNumber, reportMetadataForm.lotNumber, reportMetadataForm.reportType, savedReports]);

  const saveAdjudication = (reportType: FullReportType, request: { status: string; action: string; note?: string }) => {
    if (!projectId) return;
    const savedReport = findSavedReport(reportType);
    const reportId = getSavedReportId(savedReport);
    saveReportStatus({
      ...(reportId ? { reportId } : {}),
      projectId,
      ...(pipeId ? { pipeId } : {}),
      heatNumber,
      pipeNumber,
      reportType,
      overallStatus: request.status,
      adjudication: request.action,
      reviewerNote: request.note,
      extraDetails: {
        ...((savedReport?.extraDetails || savedReport?.extra_details || {}) as Record<string, unknown>),
        pipeNri,
        reviewerNote: request.note,
        adjudicationAction: request.action,
      },
    });
  };

  const handleAddReport = () => {
    const frequency = frequencyByReport[addReportType] || REPORT_TYPES.find((report) => report.key === addReportType)?.freq || 'per_pipe';
    const scope = getReportScope(frequency, heatNumber, lotNumber, pipeNumber);
    setReportMetadataForm({
      ...createReportMetadataForm(addReportType, frequency),
      heatNumber: scope.heatNumber,
      lotNumber: scope.lotNumber,
      pipeNumber: scope.pipeNumber,
      extraDetails: { pipeNri, lotNumber: scope.lotNumber },
    });
    setIsReportMetadataModalOpen(true);
  };

  const openReportDetail = (reportType: FullReportType) => {
    setSelectedReportType(reportType);
    setDetailReportType(reportType);
  };

  const openMaterialPdf = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    if (!materialPdfDocument) return;

    setPdfTarget({
      reportId: Number(materialPdfDocument.docRef) || 0,
      weldId: String(pipeNumber),
      reportNo: materialPdfDocument.fileName,
      rev: null,
      reportDate: materialPdfDocument.uploadedAt || null,
    });
  };

  const saveReportMetadata = () => {
    const scope = getReportScope(
      reportMetadataForm.frequency,
      reportMetadataForm.heatNumber ?? heatNumber,
      reportMetadataForm.lotNumber ?? lotNumber,
      reportMetadataForm.pipeNumber ?? pipeNumber
    );
    const scopeLabel = getReportScopeLabel(scope.frequency);
    if (!scope.heatNumber) {
      window.alert('Heat number is required before adding this report.');
      return;
    }
    if ((scope.frequency === 'per_lot' || scope.frequency === 'per_batch' || scope.frequency === 'per_shift') && !scope.lotNumber) {
      window.alert(`${formatFrequencyLabel(scope.frequency)} reports require a lot, batch, or shift number.`);
      return;
    }
    if (scope.frequency === 'per_pipe' && !scope.pipeNumber) {
      window.alert('Pipe number is required for per pipe reports.');
      return;
    }
    const isDuplicate = savedReports.some((report) => reportMatchesScope(report, reportMetadataForm.reportType, scope));
    if (isDuplicate) {
      window.alert(`Only one ${REPORT_TYPES.find((report) => report.key === reportMetadataForm.reportType)?.label || reportMetadataForm.reportType} report can be added for this ${scopeLabel}.`);
      return;
    }

    const metadataExtraDetails = {
      ...(reportMetadataForm.extraDetails || {}),
      pipeNri,
      lotNumber: scope.lotNumber,
      frequencyScope: scope.frequency,
    };

    if (projectId) {
      saveBulkReports({
        projectId,
        ...(pipeId ? { pipeId } : {}),
        heatNumber: scope.heatNumber,
        ...(scope.pipeNumber ? { pipeNumber: scope.pipeNumber } : {}),
        extraDetails: metadataExtraDetails,
        reports: [{
          reportType: reportMetadataForm.reportType,
          reportId: reportMetadataForm.reportId,
          performedAt: reportMetadataForm.performedAt,
          frequency: scope.frequency,
          lab: reportMetadataForm.lab,
          technician: reportMetadataForm.technician,
          source: reportMetadataForm.source,
          sourceFile: reportMetadataForm.sourceFile,
          note: reportMetadataForm.note,
          overallStatus: 'PENDING_REVIEW',
          extraDetails: metadataExtraDetails,
          reportDetails: [],
        }],
      });
    }
    setReportMetadataByType((previous) => ({
      ...previous,
      [reportMetadataForm.reportType]: {
        ...reportMetadataForm,
        frequency: scope.frequency,
        heatNumber: scope.heatNumber,
        lotNumber: scope.lotNumber,
        pipeNumber: scope.pipeNumber,
        extraDetails: metadataExtraDetails,
      },
    }));
    setSelectedReportType(reportMetadataForm.reportType);
    setStatusFilter('all');
    setQuery('');
    setDetailReportType(reportMetadataForm.reportType);
    setIsReportMetadataModalOpen(false);
  };

  const openAddMeasurement = (reportType: FullReportType) => {
    const field = getAvailableFieldOptions(reportType)[0];
    const draft = createMeasurementForm(reportType, reportMetadataByType[reportType]?.frequency || frequencyByReport[reportType]);
    setMeasurementForm({
      ...draft,
      fieldPath: field.fieldPath,
      fieldLabel: field.label,
      fieldSymbol: field.symbol,
      unit: field.unit,
      limit: getReportProjectLimitText(projectLimitLookup, reportType, field.limit),
    });
    setEditingMeasurementId(null);
    setIsMeasurementModalOpen(true);
  };

  const openEditMeasurement = (measurement: MeasurementItem) => {
    setMeasurementForm(measurement);
    setEditingMeasurementId(measurement.id);
    setIsMeasurementModalOpen(true);
  };

  const deleteMeasurement = (measurement: MeasurementItem) => {
    setMeasurementToDelete(measurement);
  };

  const confirmDeleteMeasurement = () => {
    if (!measurementToDelete) return;
    const measurement = measurementToDelete;
    const savedReportId = getSavedReportId(findSavedReport(measurement.reportType));
    if (savedReportId) {
      deleteReportItem({
        reportId: savedReportId,
        itemId: measurement.id,
      });
    }
    setMeasurements((previous) => previous.filter((item) => item.id !== measurement.id));
    setDeletedMeasurementIds((previous) => previous.includes(measurement.id) ? previous : [...previous, measurement.id]);
    setMeasurementToDelete(null);
  };

  const updateMeasurementField = (fieldPath: string) => {
    const selectedField = getAvailableFieldOptions(measurementForm.reportType).find((field) => field.fieldPath === fieldPath);
    setMeasurementForm((previous) => ({
      ...previous,
      fieldPath,
      fieldLabel: selectedField?.label || previous.fieldLabel,
      fieldSymbol: selectedField?.symbol || previous.fieldSymbol,
      unit: selectedField?.unit || previous.unit,
      limit: selectedField?.limit || previous.limit,
    }));
  };

  const openAddFieldOption = () => {
    setFieldOptionForm({
      fieldPath: '',
      symbol: '',
      label: '',
      unit: measurementForm.unit,
      limit: '',
    });
    setIsFieldOptionModalOpen(true);
  };

  const saveFieldOption = () => {
    const label = fieldOptionForm.label.trim();
    if (!label) return;
    const symbol = fieldOptionForm.symbol?.trim();
    const fieldPath = fieldOptionForm.fieldPath.trim() || createFieldPathFromLabel(measurementForm.reportType, label, symbol || '');
    const newField: ProjectLimitFieldOption = {
      fieldPath,
      symbol,
      label,
      unit: fieldOptionForm.unit.trim(),
      limit: fieldOptionForm.limit.trim() || 'Project limit not configured',
    };
    setCustomFieldOptionsByType((previous) => ({
      ...previous,
      [measurementForm.reportType]: [
        ...(previous[measurementForm.reportType] || []),
        newField,
      ],
    }));
    setMeasurementForm((previous) => ({
      ...previous,
      fieldPath: newField.fieldPath,
      fieldLabel: newField.label,
      fieldSymbol: newField.symbol,
      unit: newField.unit,
      limit: newField.limit,
    }));
    setIsFieldOptionModalOpen(false);
  };

  const saveMeasurement = () => {
    const measurementToSave = prepareMeasurementForSave(measurementForm, projectLimitLookup);
    const savedReportId = getSavedReportId(findSavedReport(measurementToSave.reportType));
    const metadata = reportMetadataByType[measurementToSave.reportType];
    const metadataId = metadata?.id || metadata?.metadataId;
    const scope = getCurrentReportScope(measurementToSave.reportType);
    if (projectId) {
      saveReportItem({
        ...(savedReportId ? { reportId: savedReportId } : {}),
        ...(metadataId ? { pipeMillReportMasterId: metadataId } : {}),
        projectId,
        ...(pipeId ? { pipeId } : {}),
        heatNumber: scope.heatNumber,
        ...(scope.pipeNumber ? { pipeNumber: scope.pipeNumber } : {}),
        reportType: measurementToSave.reportType,
        itemId: measurementToSave.id,
        item: measurementToSave,
        extraDetails: {
          ...(metadata?.extraDetails || {}),
          pipeNri,
          lotNumber: scope.lotNumber,
          frequencyScope: scope.frequency,
          source: measurementToSave.source,
          reference: measurementToSave.reference,
        },
      });
    }
    setMeasurements((previous) => {
      if (editingMeasurementId) {
        const existingIndex = previous.findIndex((item) => item.id === editingMeasurementId);
        if (existingIndex >= 0) {
          return previous.map((item) => item.id === editingMeasurementId ? measurementToSave : item);
        }

        return [measurementToSave, ...previous];
      }
      return [measurementToSave, ...previous];
    });
    setDeletedMeasurementIds((previous) => previous.filter((id) => id !== measurementToSave.id));
    setEditingMeasurementId(null);
    setIsMeasurementModalOpen(false);
  };

  const importReportMeasurements = (reportType: FullReportType, importedMeasurements: MeasurementItem[]) => {
    if (!importedMeasurements.length) return;

    const preparedImports = importedMeasurements.map((measurement) => (
      getReportExcelColumns(measurement.reportType).length
        ? prepareMeasurementForSave(measurement, projectLimitLookup)
        : prepareImportedMeasurementForSave(measurement, projectLimitLookup)
    ));
    const existingReportMeasurements = measurements.filter((item) => item.reportType === reportType);
    const otherMeasurements = measurements.filter((item) => item.reportType !== reportType);
    const importedById = new Map(preparedImports.map((item) => [item.id, item]));
    const updatedExistingMeasurements = existingReportMeasurements.map((item) => importedById.get(item.id) || item);
    const newImportedMeasurements = preparedImports.filter((item) => !existingReportMeasurements.some((existing) => existing.id === item.id));
    const mergedReportMeasurements = [...updatedExistingMeasurements, ...newImportedMeasurements];
    const metadata = reportMetadataByType[reportType] || createReportMetadataForm(reportType, frequencyByReport[reportType]);
    const sourceFile = preparedImports[0]?.reference || metadata.sourceFile;
    const scope = getReportScope(metadata.frequency || frequencyByReport[reportType], metadata.heatNumber ?? heatNumber, metadata.lotNumber ?? lotNumber, metadata.pipeNumber ?? pipeNumber);
    const metadataExtraDetails = {
      ...(metadata.extraDetails || {}),
      pipeNri,
      lotNumber: scope.lotNumber,
      frequencyScope: scope.frequency,
      source: 'EXCEL_UPLOAD',
      sourceFile,
    };

    if (projectId) {
      saveBulkReports({
        projectId,
        ...(pipeId ? { pipeId } : {}),
        heatNumber: scope.heatNumber,
        ...(scope.pipeNumber ? { pipeNumber: scope.pipeNumber } : {}),
        extraDetails: metadataExtraDetails,
        reports: [{
          reportType,
          reportId: metadata.reportId,
          performedAt: metadata.performedAt,
          frequency: scope.frequency,
          lab: metadata.lab,
          technician: metadata.technician,
          source: 'EXCEL_UPLOAD',
          sourceFile,
          note: metadata.note || 'Imported from Excel template',
          extraDetails: metadataExtraDetails,
          reportDetails: mergedReportMeasurements,
        }],
      });
    }

    setReportMetadataByType((previous) => ({
      ...previous,
      [reportType]: {
        ...metadata,
        frequency: scope.frequency,
        heatNumber: scope.heatNumber,
        lotNumber: scope.lotNumber,
        pipeNumber: scope.pipeNumber,
        source: 'EXCEL_UPLOAD',
        sourceFile,
        extraDetails: metadataExtraDetails,
      },
    }));
    setMeasurements([...otherMeasurements, ...mergedReportMeasurements]);
    setDeletedMeasurementIds((previous) => previous.filter((id) => !preparedImports.some((item) => item.id === id)));
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent
          onInteractOutside={(event) => {
            if ((event.target as HTMLElement).closest('[data-pdf-drawer="true"]')) {
              event.preventDefault();
            }
          }}
          className="!left-0 !top-0 !h-screen !max-h-screen !w-screen !max-w-none !translate-x-0 !translate-y-0 m-0 gap-0 rounded-none border-0 bg-page-bg p-0 shadow-none [&>button]:hidden"
        >
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-start gap-4">
                <Button onClick={onClose} variant="outline" className="h-9 w-9 p-0 rounded-lg border-[#E2E5EA]">
                  <ArrowLeft className="h-5 w-5 text-text-grey" />
                </Button>
                <div>
                  <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue">
                    Pipe {pipeNumber}
                  </DialogTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-heritage-grey">
                    <span className="font-mono text-blueprint-blue">{pipeNri}</span>
                    <span>-</span>
                    <span>Heat {heatNumber}</span>
                    <span>-</span>
                    <span>{pipeSizeLabel}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-heritage-grey" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search report, source, note..."
                    className="h-10 w-[260px] rounded-lg border border-[#E2E5EA] bg-white pl-9 pr-3 text-[13px] text-text-grey outline-none focus:ring-2 focus:ring-hero-red-10"
                  />
                </div> */}
                
                <Button type="button" onClick={handleAddReport} className="bg-hero-red text-white hover:bg-hero-red-60">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Report
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsProjectLimitsModalOpen(true)}
                  className="border-[#E2E5EA] bg-white text-text-grey hover:bg-page-bg"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Project Limits
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {[
                { key: 'all', label: `All ${counts.all} reports`, count: counts.all },
                { key: 'PASS', label: 'Cleared', count: counts.PASS || 0 },
                { key: 'PENDING_REVIEW', label: 'Pending', count: counts.PENDING_REVIEW || 0 },
                { key: 'FAIL', label: 'Failing', count: counts.FAIL || 0 },
                { key: 'INCOMPLETE', label: 'Incomplete', count: counts.INCOMPLETE || 0 },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setStatusFilter(tab.key)}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-[12px] font-semibold transition-colors',
                    statusFilter === tab.key
                      ? 'border-hero-red-20 bg-hero-red-10 text-readable-red'
                      : 'border-[#E2E5EA] bg-white text-blueprint-blue hover:bg-page-bg'
                  )}
                >
                  {tab.label}
                  <span className="ml-2 font-mono text-[11px] text-heritage-grey">{tab.count}</span>
                </button>
              ))}
              {isReportDataLoading && (
                <div className="inline-flex items-center gap-2 rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-[12px] font-semibold text-heritage-grey">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading
                </div>
              )}
            </div>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 grid-cols-1">
            <div className="min-h-0 overflow-y-auto p-4 sm:p-6">
              {isReportDataLoading ? (
                <Card className="border-[#E2E5EA] bg-white">
                  <CardContent className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
                    <Loader2 className="h-9 w-9 animate-spin text-hero-red" />
                    <h3 className="mt-4 font-heading text-[16px] font-semibold text-blueprint-blue">Loading pipe mill reports</h3>
                    <p className="mt-2 max-w-md text-[13px] text-heritage-grey">
                      Fetching saved reports, metadata, project limits, and frequency rules for this pipe.
                    </p>
                  </CardContent>
                </Card>
              ) : filteredReports.length === 0 ? (
                <Card className="border-dashed border-[#E2E5EA] bg-white">
                  <CardContent className="flex min-h-[220px] flex-col items-center justify-center p-8 text-center">
                    <FileText className="h-8 w-8 text-heritage-grey" />
                    <h3 className="mt-3 font-heading text-[16px] font-semibold text-blueprint-blue">No pipe mill reports added</h3>
                    <p className="mt-2 max-w-md text-[13px] text-heritage-grey">
                      Add report metadata to create a report card for this heat. Cards will load from saved API data only.
                    </p>
                    <Button type="button" onClick={handleAddReport} className="mt-4 bg-hero-red text-white hover:bg-hero-red-60">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Report
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
                  {filteredReports.map((row) => {
                  const report = row.report;
                  const isSelected = selectedReportType === row.key;
                  const summary = getReportCardSummary(row.key, report, measurements);

                  return (
                    <Card
                      key={row.key}
                      onClick={() => openReportDetail(row.key)}
                      className={cn(
                        'cursor-pointer overflow-hidden border-[#E2E5EA] border-t-[4px] border-t-hero-red bg-white shadow-sm transition hover:border-hero-red-20 hover:shadow-md',
                        isSelected && 'ring-2 ring-hero-red-10'
                      )}
                    >
                      <CardContent className="p-0">
                        <div className="flex items-start justify-between gap-3 px-4 py-3">
                          <div>
                            <div className="font-mono text-[10px] uppercase tracking-wide text-heritage-grey">{row.key}</div>
                            <div className="mt-1 flex items-center gap-2 text-[13px] font-semibold text-blueprint-blue">
                              <FileText className="h-4 w-4 text-heritage-grey" />
                              {row.label}
                            </div>
                          </div>
                          <StatusBadge status={report.status} />
                        </div>

                        <div className="border-t border-[#EEF0F3] px-4 py-3">
                          <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px] text-heritage-grey">
                            <span className="font-semibold text-text-grey">{summary.eyebrow}</span>
                            <span className="font-mono">{row.freq.replace('per_', '')}</span>
                          {row.psl2Mandatory && (
                            <span className="rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[10px] font-semibold text-blueprint-blue">
                              PSL2 req
                            </span>
                          )}
                          </div>
                         
                          {(summary.note || report.note) && (
                            <div className={cn('mt-3', statusNoteClassName(report.status))}>
                              {summary.note || report.note}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between border-t border-[#EEF0F3] bg-page-bg px-4 py-2">
                          <span className="font-mono text-[10px] text-heritage-grey">Report {formatDisplayDate(report.updated)}</span>
                          <div className="flex items-center gap-2">
                            {/* {summary.chart && (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openReportDetail(row.key);
                                }}
                                className="rounded-md border border-[#E2E5EA] bg-white px-2 py-1 text-[10px] font-semibold text-blueprint-blue hover:bg-page-bg"
                              >
                                Chart
                              </button>
                            )} */}
                            <button
                              type="button"
                              onClick={openMaterialPdf}
                              disabled={!materialPdfDocument}
                              className="rounded-md border border-[#E2E5EA] bg-white px-2 py-1 text-[10px] font-semibold text-blueprint-blue hover:bg-page-bg disabled:cursor-not-allowed disabled:text-heritage-grey"
                              title={materialPdfDocument ? materialPdfDocument.fileName : 'No material PDF uploaded'}
                            >
                              PDF
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              )}
            </div>

           
          </div>
        </div>
      </DialogContent>
      </Dialog>

      <ReportDetailModal
        reportType={detailReportType}
        onOpenChange={(open) => {
          if (!open) setDetailReportType(null);
        }}
        pipeNumber={String(pipeNumber)}
        pipeNri={pipeNri}
        heatNumber={heatNumber}
        metadata={detailReportType ? reportMetadataByType[detailReportType] : undefined}
        frequency={detailReportType ? frequencyByReport[detailReportType] : undefined}
        projectLimitFields={detailReportType ? mergeProjectLimitFieldOptions(detailReportType, projectLimitFieldOptionsByReport[detailReportType]) : undefined}
        measurements={measurements.filter((item) => item.reportType === detailReportType)}
        deletedMeasurementIds={deletedMeasurementIds}
        onAddMeasurement={openAddMeasurement}
        onEditMeasurement={openEditMeasurement}
        onDeleteMeasurement={deleteMeasurement}
        selectedMaterial={selectedMaterial}
        reportStatus={detailReportType ? reportRows.find((row) => row.key === detailReportType)?.report.status : undefined}
        reportNote={detailReportType ? reportRows.find((row) => row.key === detailReportType)?.report.note : undefined}
        isSavingAdjudication={Boolean(saving && savingScope === 'reportStatus')}
        onAdjudicate={saveAdjudication}
        onImportMeasurements={importReportMeasurements}
      />

      <ProjectLimitsModal
        isOpen={isProjectLimitsModalOpen}
        onOpenChange={setIsProjectLimitsModalOpen}
        projectId={projectId || 0}
        selectedReportType="HYDROSTATIC"
        metadataByType={{}}
        selectedPipe={selectedPipe}
        details={details}
        selectedMaterial={selectedMaterial}
      />

      {materialPdfDocument && (
        <PdfDrawer
          target={pdfTarget}
          onClose={() => setPdfTarget(null)}
          formatDate={formatDisplayDate}
          widthPercent={pdfWidthPercent}
          onWidthChange={setPdfWidthPercent}
          fileUrl={materialPdfDocument.fileUrl}
        />
      )}

      <Dialog open={isReportMetadataModalOpen} onOpenChange={setIsReportMetadataModalOpen}>
        <DialogContent className="max-w-3xl p-0 gap-0 bg-page-bg [&>button]:hidden">
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 py-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue">
                  Add Report Metadata
                </DialogTitle>
                <p className="mt-1 text-[12px] text-heritage-grey">
                  Capture report-level metadata first. After saving, open the report card to add measured values.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setIsReportMetadataModalOpen(false)}>
                Close
              </Button>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
            <Field label="Report Type">
              <select
                value={reportMetadataForm.reportType}
                onChange={(event) => {
                  const reportType = event.target.value as FullReportType;
                  const frequency = frequencyByReport[reportType] || REPORT_TYPES.find((report) => report.key === reportType)?.freq || 'per_pipe';
                  const scope = getReportScope(frequency, heatNumber, lotNumber, pipeNumber);
                  setReportMetadataForm({
                    ...createReportMetadataForm(reportType, frequency),
                    heatNumber: scope.heatNumber,
                    lotNumber: scope.lotNumber,
                    pipeNumber: scope.pipeNumber,
                    extraDetails: { pipeNri, lotNumber: scope.lotNumber },
                  });
                }}
                className={inputClassName}
              >
                {REPORT_TYPES.map((reportType) => (
                  <option key={reportType.key} value={reportType.key}>{reportType.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Heat Number">
              <input value={reportMetadataForm.heatNumber || heatNumber || ''} readOnly className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
            </Field>
            {(reportMetadataForm.frequency === 'per_lot' || reportMetadataForm.frequency === 'per_batch' || reportMetadataForm.frequency === 'per_shift') && (
              <Field label={reportMetadataForm.frequency === 'per_batch' ? 'Batch Number' : reportMetadataForm.frequency === 'per_shift' ? 'Shift Number' : 'Lot Number'}>
                <select
                  value={reportMetadataForm.lotNumber || ''}
                  onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, lotNumber: event.target.value, pipeNumber: null }))}
                  className={inputClassName}
                >
                  <option value="">
                    {availableReportLotNumbers.length
                      ? `Select ${getReportScopeLabel(reportMetadataForm.frequency)} number`
                      : `No available ${getReportScopeLabel(reportMetadataForm.frequency)} numbers`}
                  </option>
                  {availableReportLotNumbers.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </Field>
            )}
            {reportMetadataForm.frequency === 'per_pipe' && (
              <Field label="Pipe Number">
                <input
                  value={reportMetadataForm.pipeNumber || pipeNumber || ''}
                  onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, pipeNumber: event.target.value }))}
                  className={inputClassName}
                />
              </Field>
            )}
            <Field label="Initial Status">
              <input value="Pending Review" readOnly className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
            </Field>
            <Field label="Report ID">
              <input value={reportMetadataForm.reportId} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, reportId: event.target.value }))} className={inputClassName} />
            </Field>
            <Field label="Performed At">
              <input type="datetime-local" value={reportMetadataForm.performedAt} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, performedAt: event.target.value }))} className={inputClassName} />
            </Field>
            <Field label="Source">
              <select value={reportMetadataForm.source} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, source: event.target.value }))} className={inputClassName}>
                <option value="MANUAL">Manual</option>
                <option value="PDF_DIGITAL">PDF digital</option>
                <option value="SPREADSHEET">Spreadsheet</option>
                <option value="API_FEED">API feed</option>
              </select>
            </Field>
            <Field label="Frequency">
              <select
                value={reportMetadataForm.frequency}
                onChange={(event) => {
                  const frequency = event.target.value;
                  const scope = getReportScope(frequency, heatNumber, lotNumber, pipeNumber);
                  setReportMetadataForm((previous) => ({
                    ...previous,
                    frequency,
                    heatNumber: scope.heatNumber,
                    lotNumber: scope.lotNumber,
                    pipeNumber: scope.pipeNumber,
                    extraDetails: { ...(previous.extraDetails || {}), lotNumber: scope.lotNumber },
                  }));
                }}
                className={inputClassName}
              >
                {FREQUENCY_OPTIONS.map((frequency) => (
                  <option key={frequency} value={frequency}>{formatFrequencyLabel(frequency)}</option>
                ))}
              </select>
            </Field>
            <Field label="Lab">
              <input value={reportMetadataForm.lab} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, lab: event.target.value }))} className={inputClassName} />
            </Field>
            <Field label="Technician">
              <input value={reportMetadataForm.technician} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, technician: event.target.value }))} className={inputClassName} />
            </Field>
            <Field label="Source File">
              <input value={reportMetadataForm.sourceFile} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, sourceFile: event.target.value }))} className={inputClassName} />
            </Field>
            <Field label="Note">
              <input value={reportMetadataForm.note} onChange={(event) => setReportMetadataForm((previous) => ({ ...previous, note: event.target.value }))} className={inputClassName} />
            </Field>
          </div>

          <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setIsReportMetadataModalOpen(false)}>Cancel</Button>
            <Button type="button" onClick={saveReportMetadata} className="bg-hero-red text-white hover:bg-hero-red-60">
              Add Report 
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isMeasurementModalOpen} onOpenChange={setIsMeasurementModalOpen}>
        <DialogContent className={cn('p-0 gap-0 bg-page-bg [&>button]:hidden', measurementForm.reportType === 'TENSILE' || measurementForm.reportType === 'CVN' || measurementForm.reportType === 'AUT' || measurementForm.reportType === 'HYDROSTATIC' || measurementForm.reportType === 'DIMENSIONAL' || measurementForm.reportType === 'FLATTENING' || measurementForm.reportType === 'BEND' || measurementForm.reportType === 'HARDNESS' || measurementForm.reportType === 'VISUAL' || measurementForm.reportType === 'RT' || measurementForm.reportType === 'MPT' || measurementForm.reportType === 'HEAT_TREAT' || measurementForm.reportType === 'PIPE_BODY_UT' || measurementForm.reportType === 'MFL_ET' || measurementForm.reportType === 'COATING' || measurementForm.reportType === 'THREAD' || measurementForm.reportType === 'WELD_MAP' ? 'max-w-5xl' : 'max-w-2xl')}>
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 py-4">
            <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue">
              {measurementForm.reportType === 'TENSILE'
                ? editingMeasurementId ? 'Edit Tensile Specimen' : 'Add Tensile Specimen'
                : measurementForm.reportType === 'CVN' ? editingMeasurementId ? 'Edit CVN Impact Specimen' : 'Add CVN Impact Specimen'
                : measurementForm.reportType === 'AUT' ? editingMeasurementId ? 'Edit AUT Indication' : 'Add AUT Indication'
                : measurementForm.reportType === 'HYDROSTATIC' ? editingMeasurementId ? 'Edit Hydrostatic Test' : 'Add Hydrostatic Test'
                : measurementForm.reportType === 'DIMENSIONAL' ? editingMeasurementId ? 'Edit Dimensional Inspection' : 'Add Dimensional Inspection'
                : measurementForm.reportType === 'FLATTENING' ? editingMeasurementId ? 'Edit Flattening Specimen' : 'Add Flattening Specimen'
                : measurementForm.reportType === 'BEND' ? editingMeasurementId ? 'Edit Bend Specimen' : 'Add Bend Specimen'
                : measurementForm.reportType === 'HARDNESS' ? editingMeasurementId ? 'Edit Hardness Reading' : 'Add Hardness Reading'
                : measurementForm.reportType === 'VISUAL' ? editingMeasurementId ? 'Edit Visual Defect' : 'Add Visual Defect'
                : measurementForm.reportType === 'RT' ? editingMeasurementId ? 'Edit RT Indication' : 'Add RT Indication'
                : measurementForm.reportType === 'MPT' ? editingMeasurementId ? 'Edit MPT Indication' : 'Add MPT Indication'
                : measurementForm.reportType === 'HEAT_TREAT' ? editingMeasurementId ? 'Edit Heat Treatment Cycle' : 'Add Heat Treatment Cycle'
                : measurementForm.reportType === 'PIPE_BODY_UT' ? editingMeasurementId ? 'Edit Pipe Body UT Indication' : 'Add Pipe Body UT Indication'
                : measurementForm.reportType === 'MFL_ET' ? editingMeasurementId ? 'Edit MFL / ET Indication' : 'Add MFL / ET Indication'
                : measurementForm.reportType === 'COATING' ? editingMeasurementId ? 'Edit Coating Result' : 'Add Coating Result'
                : measurementForm.reportType === 'THREAD' ? editingMeasurementId ? 'Edit Thread Gauge Result' : 'Add Thread Gauge Result'
                : measurementForm.reportType === 'WELD_MAP' ? editingMeasurementId ? 'Edit Weld Pass' : 'Add Weld Pass'
                : editingMeasurementId ? 'Edit Report Item' : 'Add Report Item'}
            </DialogTitle>
            <p className="mt-1 text-[12px] text-heritage-grey">
              {measurementForm.reportType === 'TENSILE'
                ? 'Enter the specimen row. YS/UTS and PASS/FAIL are calculated from project limits when saved.'
                : measurementForm.reportType === 'CVN' ? 'Enter the impact specimen row. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'AUT' ? 'Enter the AUT indication row. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'HYDROSTATIC' ? 'Enter the pressure test result. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'DIMENSIONAL' ? 'Enter dimensional inspection values. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'FLATTENING' ? 'Enter the flattening specimen result. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'BEND' ? 'Enter the bend specimen result. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'HARDNESS' ? 'Enter the HV10 indent reading. PASS/PENDING/FAIL is calculated from project and fallback limits when saved.'
                : measurementForm.reportType === 'VISUAL' ? 'Enter the visual inspection defect. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'RT' ? 'Enter the radiographic indication row. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'MPT' ? 'Enter the magnetic particle indication row. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'HEAT_TREAT' ? 'Enter the heat treatment stage results. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'PIPE_BODY_UT' ? 'Enter the pipe body UT indication. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'MFL_ET' ? 'Enter the MFL / ET illustrative indication. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'COATING' ? 'Enter the coating inspection values. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'THREAD' ? 'Enter the thread gauge measurements. PASS/FAIL is calculated from project limits when saved.'
                : measurementForm.reportType === 'WELD_MAP' ? 'Enter weld pass and traceability values. Heat input and PASS/FAIL are calculated when saved.'
                : measurementForm.reportType === 'CHEMICAL' ? 'Enter the measured chemical value. PASS/FAIL is calculated automatically from project limits when saved.'
                : 'Select a field from project limits, enter the measured value, and set the review status. Automatic calculation will be added later.'}
            </p>
          </DialogHeader>

          <div className="space-y-4 p-6">
            {measurementForm.reportType !== 'TENSILE' && measurementForm.reportType !== 'CHEMICAL' && measurementForm.reportType !== 'CVN' && measurementForm.reportType !== 'AUT' && measurementForm.reportType !== 'HYDROSTATIC' && measurementForm.reportType !== 'DIMENSIONAL' && measurementForm.reportType !== 'FLATTENING' && measurementForm.reportType !== 'BEND' && measurementForm.reportType !== 'HARDNESS' && measurementForm.reportType !== 'VISUAL' && measurementForm.reportType !== 'RT' && measurementForm.reportType !== 'MPT' && measurementForm.reportType !== 'HEAT_TREAT' && measurementForm.reportType !== 'PIPE_BODY_UT' && measurementForm.reportType !== 'MFL_ET' && measurementForm.reportType !== 'COATING' && measurementForm.reportType !== 'THREAD' && measurementForm.reportType !== 'WELD_MAP' && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Report Type">
                  <input value={measurementForm.reportType} disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                </Field>
                <Field label="Test Frequency">
                  <input value={measurementForm.frequency} disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                </Field>
              </div>
            )}

            {measurementForm.reportType !== 'TENSILE' && measurementForm.reportType !== 'CVN' && measurementForm.reportType !== 'AUT' && measurementForm.reportType !== 'HYDROSTATIC' && measurementForm.reportType !== 'DIMENSIONAL' && measurementForm.reportType !== 'FLATTENING' && measurementForm.reportType !== 'BEND' && measurementForm.reportType !== 'HARDNESS' && measurementForm.reportType !== 'VISUAL' && measurementForm.reportType !== 'RT' && measurementForm.reportType !== 'MPT' && measurementForm.reportType !== 'HEAT_TREAT' && measurementForm.reportType !== 'PIPE_BODY_UT' && measurementForm.reportType !== 'MFL_ET' && measurementForm.reportType !== 'COATING' && measurementForm.reportType !== 'THREAD' && measurementForm.reportType !== 'WELD_MAP' && (
              <Field label="Field From Project Limits">
                <div className="flex gap-2">
                  <select value={measurementForm.fieldPath} onChange={(event) => updateMeasurementField(event.target.value)} className={inputClassName}>
                    {getAvailableFieldOptions(measurementForm.reportType).map((field) => (
                      <option key={field.fieldPath} value={field.fieldPath}>{formatProjectFieldOption(field)}</option>
                    ))}
                  </select>
                  <Button type="button" variant="outline" onClick={openAddFieldOption} className="shrink-0">
                    <Plus className="mr-2 h-4 w-4" />
                    Add New Field
                  </Button>
                </div>
              </Field>
            )}

            {measurementForm.reportType === 'TENSILE' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Tensile Specimen Row</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Enter the full row values that will appear in the Tensile table.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="ID">
                    <input value={measurementForm.tensileSpecimenId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileSpecimenId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Location">
                    <select value={measurementForm.tensileLocation || 'pipe_body'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileLocation: event.target.value }))} className={inputClassName}>
                      <option value="pipe_body">Pipe body</option>
                      <option value="weld_seam">Weld seam</option>
                      <option value="haz">HAZ</option>
                      <option value="weld_metal">Weld metal</option>
                    </select>
                  </Field>
                  <Field label="Orient.">
                    <select value={measurementForm.tensileOrientation || 'transverse'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileOrientation: event.target.value }))} className={inputClassName}>
                      <option value="transverse">Transverse</option>
                      <option value="longitudinal">Longitudinal</option>
                    </select>
                  </Field>
                  <Field label="Area mm2">
                    <input value={measurementForm.tensileAreaMm2 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileAreaMm2: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="YS MPa">
                    <input value={measurementForm.tensileYsMpa || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileYsMpa: event.target.value, measuredValue: previous.fieldPath === 'ys_mpa' ? event.target.value : previous.measuredValue }))} className={inputClassName} />
                  </Field>
                  <Field label="UTS MPa">
                    <input value={measurementForm.tensileUtsMpa || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileUtsMpa: event.target.value, measuredValue: previous.fieldPath === 'uts_mpa' ? event.target.value : previous.measuredValue }))} className={inputClassName} />
                  </Field>
                  <Field label="Elong %">
                    <input value={measurementForm.tensileElongationPct || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, tensileElongationPct: event.target.value, measuredValue: previous.fieldPath === 'elongation_pct' ? event.target.value : previous.measuredValue }))} className={inputClassName} />
                  </Field>
                  <Field label="YS/UTS">
                    <input value={calculateTensileRatio(measurementForm)} disabled placeholder="Auto from YS / UTS" className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'TENSILE')}
                      </div>
                    </div>
                    <StatusBadge status={calculateTensileStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'CVN' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">CVN Impact Specimen Row</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one Charpy V-notch specimen. Location averages update in the CVN report table.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Specimen ID">
                    <input value={measurementForm.cvnSpecimenId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnSpecimenId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Location">
                    <select value={measurementForm.cvnLocation || 'pipe_body'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnLocation: event.target.value }))} className={inputClassName}>
                      <option value="pipe_body">Pipe body</option>
                      <option value="weld_seam">Weld seam</option>
                      <option value="haz">HAZ</option>
                    </select>
                  </Field>
                  <Field label="Notch Orient.">
                    <select value={measurementForm.cvnNotchOrientation || 'transverse'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnNotchOrientation: event.target.value }))} className={inputClassName}>
                      <option value="transverse">Transverse</option>
                      <option value="longitudinal">Longitudinal</option>
                    </select>
                  </Field>
                  <Field label="Specimen Size">
                    <select value={measurementForm.cvnSpecimenSize || 'full'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnSpecimenSize: event.target.value }))} className={inputClassName}>
                      <option value="full">Full 10.0 mm</option>
                      <option value="3/4">3/4 - 7.5 mm</option>
                      <option value="2/3">2/3 - 6.7 mm</option>
                      <option value="1/2">1/2 - 5.0 mm</option>
                    </select>
                  </Field>
                  <Field label="Energy J">
                    <input value={measurementForm.cvnEnergyJ || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnEnergyJ: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Shear %">
                    <input value={measurementForm.cvnShearPct || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnShearPct: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Lateral Expansion mm">
                    <input value={measurementForm.cvnLateralExpansionMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, cvnLateralExpansionMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Test Temperature">
                    <input value={getProjectLimit(projectLimitLookup, 'CVN', 'test_temperature') ? formatProjectLimitText(getProjectLimit(projectLimitLookup, 'CVN', 'test_temperature')!) : 'Project limit not configured'} disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'CVN')}
                      </div>
                    </div>
                    <StatusBadge status={calculateCvnStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'AUT' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">AUT Indication Row</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one AUT indication along the pipe seam.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="ID">
                    <input value={measurementForm.autIndicationId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autIndicationId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Position mm">
                    <input value={measurementForm.autPositionMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autPositionMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Clock">
                    <input value={measurementForm.autClock || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autClock: event.target.value }))} placeholder="3:00" className={inputClassName} />
                  </Field>
                  <Field label="Flaw Type">
                    <select value={measurementForm.autFlawType || 'inclusion'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autFlawType: event.target.value }))} className={inputClassName}>
                      <option value="inclusion">Inclusion</option>
                      <option value="porosity">Porosity</option>
                      <option value="slag line">Slag line</option>
                      <option value="lack of fusion">Lack of fusion</option>
                      <option value="crack">Crack</option>
                    </select>
                  </Field>
                  <Field label="Depth % WT">
                    <input value={measurementForm.autDepthPctWt || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autDepthPctWt: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Length mm">
                    <input value={measurementForm.autLengthMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autLengthMm: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.autDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, autDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="repair">Repair</option>
                      <option value="reject">Reject</option>
                      <option value="monitor">Monitor</option>
                    </select>
                  </Field>
                  <Field label="Method / Level">
                    <input value="AUT+TOFD · N2" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'AUT')}
                      </div>
                    </div>
                    <StatusBadge status={calculateAutStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'HYDROSTATIC' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Hydrostatic Pressure Test</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture the pressure hold result, pass conditions, and test setup.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Frequency Scope">
                    <input value={measurementForm.frequency || frequencyByReport.HYDROSTATIC || 'per_pipe'} disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                  <Field label="Test Pressure bar">
                    <input value={measurementForm.hydroTestPressureBar || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroTestPressureBar: event.target.value, measuredValue: event.target.value }))} placeholder={getProjectLimit(projectLimitLookup, 'HYDROSTATIC', 'test_pressure_bar') ? formatProjectLimitText(getProjectLimit(projectLimitLookup, 'HYDROSTATIC', 'test_pressure_bar')!) : '>= 257 bar'} className={inputClassName} />
                  </Field>
                  <Field label="Hold Duration s">
                    <input value={measurementForm.hydroHoldDurationS || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroHoldDurationS: event.target.value }))} placeholder={getProjectLimit(projectLimitLookup, 'HYDROSTATIC', 'hold_duration_s') ? formatProjectLimitText(getProjectLimit(projectLimitLookup, 'HYDROSTATIC', 'hold_duration_s')!) : '>= 30 s'} className={inputClassName} />
                  </Field>
                  <Field label="Pressure Drop Observed">
                    <select value={measurementForm.hydroPressureDropObserved || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroPressureDropObserved: event.target.value }))} className={inputClassName}>
                      <option value="no">No - required</option>
                      <option value="yes">Yes - fail</option>
                    </select>
                  </Field>
                  <Field label="Leak Observed">
                    <select value={measurementForm.hydroLeakObserved || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroLeakObserved: event.target.value }))} className={inputClassName}>
                      <option value="no">No - required</option>
                      <option value="yes">Yes - fail</option>
                    </select>
                  </Field>
                  <Field label="Medium">
                    <input value={measurementForm.hydroMedium || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroMedium: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Sealing">
                    <input value={measurementForm.hydroSealingMethod || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroSealingMethod: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Gauge Cal.">
                    <input value={measurementForm.hydroGaugeCalRef || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroGaugeCalRef: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Design Factor">
                    <input value={measurementForm.hydroDesignFactor || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hydroDesignFactor: event.target.value }))} className={inputClassName} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'HYDROSTATIC')}
                      </div>
                    </div>
                    <StatusBadge status={calculateHydrostaticStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'DIMENSIONAL' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Dimensional Inspection</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture OD, wall thickness points, and geometry checks for this pipe.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">Outside Diameter</div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                      <Field label="OD Avg mm">
                        <input value={measurementForm.dimOdAvgMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimOdAvgMm: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="OD Min mm">
                        <input value={measurementForm.dimOdMinMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimOdMinMm: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="OD Max mm">
                        <input value={measurementForm.dimOdMaxMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimOdMaxMm: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="Ovality %">
                        <input value={measurementForm.dimOvalityPct || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimOvalityPct: event.target.value }))} className={inputClassName} />
                      </Field>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">Wall Thickness - 8 Points</div>
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                      {([
                        ['dimWtEnd1N', 'End 1 - N'],
                        ['dimWtEnd1E', 'End 1 - E'],
                        ['dimWtEnd1S', 'End 1 - S'],
                        ['dimWtEnd1W', 'End 1 - W'],
                        ['dimWtEnd2N', 'End 2 - N'],
                        ['dimWtEnd2E', 'End 2 - E'],
                        ['dimWtEnd2S', 'End 2 - S'],
                        ['dimWtEnd2W', 'End 2 - W'],
                      ] as const).map(([key, label]) => (
                        <Field key={key} label={label}>
                          <input value={measurementForm[key] || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, [key]: event.target.value }))} className={inputClassName} />
                        </Field>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">Length / Geometry</div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                      <Field label="Length m">
                        <input value={measurementForm.dimLengthM || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimLengthM: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="Straightness mm">
                        <input value={measurementForm.dimStraightnessMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimStraightnessMm: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="Bevel Angle deg">
                        <input value={measurementForm.dimBevelAngleDeg || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimBevelAngleDeg: event.target.value }))} className={inputClassName} />
                      </Field>
                      <Field label="End Squareness mm">
                        <input value={measurementForm.dimEndSquarenessMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, dimEndSquarenessMm: event.target.value }))} className={inputClassName} />
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'DIMENSIONAL')}
                      </div>
                    </div>
                    <StatusBadge status={calculateDimensionalStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'FLATTENING' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Flattening Specimen</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture weld position, OD before test, H/D reached, and crack observation.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="Specimen ID">
                    <input value={measurementForm.flatteningSpecimenId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, flatteningSpecimenId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Weld Position">
                    <select
                      value={measurementForm.flatteningWeldPosition || '0deg'}
                      onChange={(event) => {
                        const weldPosition = event.target.value;
                        setMeasurementForm((previous) => ({
                          ...previous,
                          flatteningWeldPosition: weldPosition,
                        }));
                      }}
                      className={inputClassName}
                    >
                      <option value="0deg">0deg</option>
                      <option value="90deg">90deg</option>
                    </select>
                  </Field>
                  <Field label="OD Before mm">
                    <input value={measurementForm.flatteningOdBeforeMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, flatteningOdBeforeMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="H/D Reached">
                    <input value={measurementForm.flatteningHdReached || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, flatteningHdReached: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Cracks Observed">
                    <select value={measurementForm.flatteningCracksObserved || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, flatteningCracksObserved: event.target.value }))} className={inputClassName}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'FLATTENING')}
                      </div>
                    </div>
                    <StatusBadge status={calculateFlatteningStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'BEND' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Bend Specimen</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture face/root bend angle, crack length, fracture, and mandrel sizing.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Specimen ID">
                    <input value={measurementForm.bendSpecimenId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendSpecimenId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Bend Type">
                    <select value={measurementForm.bendType || 'face_bend'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendType: event.target.value }))} className={inputClassName}>
                      <option value="face_bend">Face bend</option>
                      <option value="root_bend">Root bend</option>
                    </select>
                  </Field>
                  <Field label="Bend Angle deg">
                    <input value={measurementForm.bendAngleDeg || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendAngleDeg: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Crack Length mm">
                    <input value={measurementForm.bendCrackLengthMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendCrackLengthMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Fracture">
                    <select value={measurementForm.bendFracture || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendFracture: event.target.value }))} className={inputClassName}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                  <Field label="Wall Thickness mm">
                    <input value={measurementForm.bendWallThicknessMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendWallThicknessMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Multiplier">
                    <input value={measurementForm.bendMultiplier || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendMultiplier: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Mill Former mm">
                    <input value={measurementForm.bendMillFormerMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, bendMillFormerMm: event.target.value }))} className={inputClassName} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'BEND')}
                      </div>
                    </div>
                    <StatusBadge status={calculateBendStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'HARDNESS' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">HV10 Hardness Reading</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one indent reading by weld cross-section zone.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Zone">
                    <select value={measurementForm.hardnessZone || 'weld cap'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessZone: event.target.value }))} className={inputClassName}>
                      <option value="weld cap">Weld cap</option>
                      <option value="weld root">Weld root</option>
                      <option value="haz outer">HAZ outer</option>
                      <option value="haz inner">HAZ inner</option>
                      <option value="base metal">Base metal</option>
                    </select>
                  </Field>
                  <Field label="Reading ID">
                    <input value={measurementForm.hardnessReadingId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessReadingId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Position">
                    <input value={measurementForm.hardnessPosition || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessPosition: event.target.value }))} placeholder="indent 1" className={inputClassName} />
                  </Field>
                  <Field label="Measured HV10">
                    <input value={measurementForm.hardnessValueHv10 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessValueHv10: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Project Max HV10">
                    <input value={measurementForm.hardnessProjectLimitHv10 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessProjectLimitHv10: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Fallback Max HV10">
                    <input value={measurementForm.hardnessFallbackLimitHv10 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, hardnessFallbackLimitHv10: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Method">
                    <input value="HV10" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                  <Field label="Scope">
                    <input value="weld cross-section" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Limit decision</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'HARDNESS')}
                      </div>
                    </div>
                    <StatusBadge status={calculateHardnessStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'VISUAL' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Visual Defect</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one catalogued visual indication and inspection lighting.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Defect ID">
                    <input value={measurementForm.visualDefectId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualDefectId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Type">
                    <select value={measurementForm.visualDefectType || 'scale'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualDefectType: event.target.value }))} className={inputClassName}>
                      <option value="scale">Scale</option>
                      <option value="groove">Groove</option>
                      <option value="pit">Pit</option>
                      <option value="arc_strike">Arc strike</option>
                      <option value="crack">Crack</option>
                    </select>
                  </Field>
                  <Field label="Position mm">
                    <input value={measurementForm.visualPositionMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualPositionMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Clock">
                    <input value={measurementForm.visualClock || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualClock: event.target.value }))} placeholder="5:00" className={inputClassName} />
                  </Field>
                  <Field label="Depth % WT">
                    <input value={measurementForm.visualDepthPctWt || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualDepthPctWt: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.visualDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="repaired">Repaired</option>
                      <option value="reject">Reject</option>
                      <option value="monitor">Monitor</option>
                    </select>
                  </Field>
                  <Field label="Lighting lux">
                    <input value={measurementForm.visualLightingLux || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, visualLightingLux: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Coverage">
                    <input value="both ends + full body" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'VISUAL')}
                      </div>
                      {getVisualStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getVisualStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateVisualStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'RT' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Radiographic Indication</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one film indication with exposure class and IQI sensitivity.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Indication ID">
                    <input value={measurementForm.rtIndicationId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtIndicationId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Position mm">
                    <input value={measurementForm.rtPositionMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtPositionMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Flaw Type">
                    <select value={measurementForm.rtFlawType || 'porosity'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtFlawType: event.target.value }))} className={inputClassName}>
                      <option value="porosity">Porosity</option>
                      <option value="slag">Slag</option>
                      <option value="lack_of_fusion">Lack of fusion</option>
                      <option value="crack">Crack</option>
                    </select>
                  </Field>
                  <Field label="Length mm">
                    <input value={measurementForm.rtLengthMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtLengthMm: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Area mm2">
                    <input value={measurementForm.rtAreaMm2 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtAreaMm2: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.rtDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="repair">Repair</option>
                      <option value="reject">Reject</option>
                    </select>
                  </Field>
                  <Field label="Exposure Class">
                    <select value={measurementForm.rtExposureClass || 'B'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtExposureClass: event.target.value }))} className={inputClassName}>
                      <option value="B">B</option>
                      <option value="A">A</option>
                    </select>
                  </Field>
                  <Field label="IQI Sensitivity %">
                    <input value={measurementForm.rtIqiSensitivityPct || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, rtIqiSensitivityPct: event.target.value }))} className={inputClassName} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'RT')}
                      </div>
                      {getRtStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getRtStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateRtStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'MPT' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Magnetic Particle Indication</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one end-face indication and the process parameters used for the MPT shot.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Indication ID">
                    <input value={measurementForm.mptIndicationId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptIndicationId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="End Face">
                    <select value={measurementForm.mptEnd || 'End 1 - PE1'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptEnd: event.target.value }))} className={inputClassName}>
                      <option value="End 1 - PE1">End 1 - PE1</option>
                      <option value="End 2 - PE2">End 2 - PE2</option>
                    </select>
                  </Field>
                  <Field label="Type">
                    <select value={measurementForm.mptType || 'rounded'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptType: event.target.value }))} className={inputClassName}>
                      <option value="rounded">Rounded</option>
                      <option value="linear">Linear</option>
                      <option value="non_relevant">Non relevant</option>
                    </select>
                  </Field>
                  <Field label="Length mm">
                    <input value={measurementForm.mptLengthMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptLengthMm: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.mptDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="repair">Repair</option>
                      <option value="reject">Reject</option>
                    </select>
                  </Field>
                  <Field label="Field Strength Oe">
                    <input value={measurementForm.mptFieldStrengthOe || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptFieldStrengthOe: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="UV-A uW/cm2">
                    <input value={measurementForm.mptUvIrradiance || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mptUvIrradiance: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Technique">
                    <input value="wet fluorescent - AC yoke" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'MPT')}
                      </div>
                      {getMptStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getMptStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateMptStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'HEAT_TREAT' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Heat Treatment Cycle</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture the furnace batch cycle stages used for this pipe batch.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Batch ID">
                    <input value={measurementForm.heatBatchId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatBatchId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Austenitising Temp C">
                    <input value={measurementForm.heatAustenitisingTempC || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatAustenitisingTempC: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Austenitising Hold min">
                    <input value={measurementForm.heatAustenitisingHoldMin || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatAustenitisingHoldMin: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Cooling Rate C/s">
                    <input value={measurementForm.heatCoolingRateCps || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatCoolingRateCps: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Tempering Temp C">
                    <input value={measurementForm.heatTemperingTempC || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatTemperingTempC: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Tempering Hold min">
                    <input value={measurementForm.heatTemperingHoldMin || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatTemperingHoldMin: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Pipes in Batch">
                    <input value={measurementForm.heatPipesInBatch || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, heatPipesInBatch: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Route">
                    <input value="TMCP-T" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'HEAT_TREAT')}
                      </div>
                      {getHeatTreatStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getHeatTreatStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateHeatTreatStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'PIPE_BODY_UT' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Pipe Body UT Indication</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one body scan lamination or inclusion against U2 acceptance limits.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Indication ID">
                    <input value={measurementForm.pbutIndicationId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutIndicationId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Type">
                    <select value={measurementForm.pbutIndicationType || 'lamination'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutIndicationType: event.target.value }))} className={inputClassName}>
                      <option value="lamination">Lamination</option>
                      <option value="inclusion">Inclusion</option>
                    </select>
                  </Field>
                  <Field label="Position mm">
                    <input value={measurementForm.pbutPositionMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutPositionMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Clock">
                    <input value={measurementForm.pbutClock || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutClock: event.target.value }))} placeholder="2:30" className={inputClassName} />
                  </Field>
                  <Field label="Area mm2">
                    <input value={measurementForm.pbutAreaMm2 || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutAreaMm2: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Depth % WT">
                    <input value={measurementForm.pbutDepthPctWt || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutDepthPctWt: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="End Zone">
                    <select value={measurementForm.pbutEndZone || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutEndZone: event.target.value }))} className={inputClassName}>
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.pbutDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, pbutDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="repair">Repair</option>
                      <option value="reject">Reject</option>
                    </select>
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'PIPE_BODY_UT')}
                      </div>
                      {getPipeBodyUtStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getPipeBodyUtStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculatePipeBodyUtStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'MFL_ET' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">MFL / ET Indication</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture an illustrative ERW/HFW inline electromagnetic indication.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Indication ID">
                    <input value={measurementForm.mflIndicationId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflIndicationId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Flaw Type">
                    <select value={measurementForm.mflFlawType || 'hook_crack'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflFlawType: event.target.value }))} className={inputClassName}>
                      <option value="hook_crack">Hook crack</option>
                      <option value="seam_crack">Seam crack</option>
                      <option value="lamination">Lamination</option>
                      <option value="wall_loss">Wall loss</option>
                    </select>
                  </Field>
                  <Field label="Signal % Ref">
                    <input value={measurementForm.mflSignalPctRef || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflSignalPctRef: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Disposition">
                    <select value={measurementForm.mflDisposition || 'accept'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflDisposition: event.target.value }))} className={inputClassName}>
                      <option value="accept">Accept</option>
                      <option value="confirmed_by_AUT">Confirmed by AUT</option>
                      <option value="repaired">Repaired</option>
                      <option value="reject">Reject</option>
                    </select>
                  </Field>
                  <Field label="Cal Notch % WT">
                    <input value={measurementForm.mflCalNotchPctWt || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflCalNotchPctWt: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Route">
                    <select value={measurementForm.mflRoute || 'ERW/HFW'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, mflRoute: event.target.value }))} className={inputClassName}>
                      <option value="ERW/HFW">ERW/HFW</option>
                      <option value="SAWL">SAWL</option>
                    </select>
                  </Field>
                  <Field label="Applicability">
                    <input value="SAWL replaced by AUT for this pipe" disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                  <Field label="Follow-up">
                    <input value={measurementForm.mflDisposition === 'confirmed_by_AUT' ? 'AUT record required' : 'Not required'} disabled className={cn(inputClassName, 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'MFL_ET')}
                      </div>
                      {getMflEtStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getMflEtStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateMflEtStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'COATING' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Coating Inspection Result</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture coating thickness, adhesion, holiday test, and surface preparation values.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Coating Type">
                    <select value={measurementForm.coatingType || '3LPE'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingType: event.target.value }))} className={inputClassName}>
                      <option value="3LPE">3LPE</option>
                      <option value="FBE">FBE</option>
                    </select>
                  </Field>
                  <Field label="DFT Min um">
                    <input value={measurementForm.coatingDftMinUm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingDftMinUm: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="DFT Avg um">
                    <input value={measurementForm.coatingDftAvgUm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingDftAvgUm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Adhesion N/cm">
                    <input value={measurementForm.coatingAdhesionNcm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingAdhesionNcm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="CD Radius mm">
                    <input value={measurementForm.coatingCdRadiusMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingCdRadiusMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Anchor Profile um">
                    <input value={measurementForm.coatingAnchorProfileUm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingAnchorProfileUm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Holiday Voltage kV">
                    <input value={measurementForm.coatingHolidayVoltageKv || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingHolidayVoltageKv: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Holidays Found">
                    <input value={measurementForm.coatingHolidaysFound || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingHolidaysFound: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Cutback Length mm">
                    <input value={measurementForm.coatingCutbackLengthMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingCutbackLengthMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Surface Prep">
                    <input value={measurementForm.coatingSurfacePrepGrade || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, coatingSurfacePrepGrade: event.target.value }))} className={inputClassName} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'COATING')}
                      </div>
                      {getCoatingStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getCoatingStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateCoatingStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'THREAD' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Thread Gauge Result</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture illustrative thread gauge measurements for API line-pipe threaded ends.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Thread Type">
                    <input value={measurementForm.threadType || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadType: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Standard">
                    <input value={measurementForm.threadStandard || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadStandard: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Gauge Set">
                    <input value={measurementForm.threadGaugeSetRef || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadGaugeSetRef: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="End Tested">
                    <select value={measurementForm.threadEndTested || 'both'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadEndTested: event.target.value }))} className={inputClassName}>
                      <option value="both">Both</option>
                      <option value="end_1">End 1</option>
                      <option value="end_2">End 2</option>
                    </select>
                  </Field>
                  <Field label="Taper mm/m">
                    <input value={measurementForm.threadTaperMmPerM || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadTaperMmPerM: event.target.value, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Lead mm cum">
                    <input value={measurementForm.threadLeadMmCum || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadLeadMmCum: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Height mm">
                    <input value={measurementForm.threadHeightMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadHeightMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Pipe End Diameter mm">
                    <input value={measurementForm.threadPipeEndDiameterMm || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadPipeEndDiameterMm: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Coupling Applied">
                    <select value={measurementForm.threadCouplingApplied || 'yes'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadCouplingApplied: event.target.value }))} className={inputClassName}>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </Field>
                  <Field label="Visual Damage">
                    <select value={measurementForm.threadVisualDamage || 'no'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, threadVisualDamage: event.target.value }))} className={inputClassName}>
                      <option value="no">None</option>
                      <option value="yes">Yes</option>
                    </select>
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'THREAD')}
                      </div>
                      {getThreadStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getThreadStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateThreadStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType === 'WELD_MAP' && (
              <div className="rounded-xl border border-[#E2E5EA] bg-white p-4">
                <div className="mb-3">
                  <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Weld Pass</h3>
                  <p className="mt-1 text-[12px] text-heritage-grey">
                    Capture one weld pass with traceability and heat-input values.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
                  <Field label="Pass">
                    <input value={measurementForm.weldPassName || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldPassName: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Process">
                    <select value={measurementForm.weldProcess || 'SAW'} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldProcess: event.target.value }))} className={inputClassName}>
                      <option value="GMAW">GMAW</option>
                      <option value="SAW">SAW</option>
                    </select>
                  </Field>
                  <Field label="Wire Heat">
                    <input value={measurementForm.weldWireHeatNo || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldWireHeatNo: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Flux Lot">
                    <input value={measurementForm.weldFluxLotNo || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldFluxLotNo: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Current A">
                    <input value={measurementForm.weldCurrentA || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldCurrentA: event.target.value, weldHeatInputKjMm: '' }))} className={inputClassName} />
                  </Field>
                  <Field label="Voltage V">
                    <input value={measurementForm.weldVoltageV || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldVoltageV: event.target.value, weldHeatInputKjMm: '' }))} className={inputClassName} />
                  </Field>
                  <Field label="Speed mm/min">
                    <input value={measurementForm.weldSpeedMmMin || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldSpeedMmMin: event.target.value, weldHeatInputKjMm: '' }))} className={inputClassName} />
                  </Field>
                  <Field label="Heat Input kJ/mm">
                    <input value={measurementForm.weldHeatInputKjMm || calculateWeldHeatInput(measurementForm)} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldHeatInputKjMm: event.target.value, measuredValue: event.target.value }))} placeholder="Auto from I x V x 60 / speed" className={inputClassName} />
                  </Field>
                  <Field label="Plate ID">
                    <input value={measurementForm.weldPlateId || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldPlateId: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Plate Heat">
                    <input value={measurementForm.weldPlateHeatNumber || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldPlateHeatNumber: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Pipe Heat">
                    <input value={measurementForm.weldPipeHeatNumber || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldPipeHeatNumber: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="WPS / PQR">
                    <input value={measurementForm.weldWpsRef || ''} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, weldWpsRef: event.target.value }))} className={inputClassName} />
                  </Field>
                </div>
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Project limits</div>
                      <div className="mt-1 text-[12px] text-text-grey">
                        {getReportProjectLimitText(projectLimitLookup, 'WELD_MAP')}
                      </div>
                      {getWeldMapStatusReason(measurementForm, projectLimitLookup) && (
                        <div className="mt-2 text-[12px] font-semibold text-readable-red">
                          {getWeldMapStatusReason(measurementForm, projectLimitLookup)}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={calculateWeldMapStatus(measurementForm, projectLimitLookup)} />
                  </div>
                </div>
              </div>
            )}

            {measurementForm.reportType !== 'TENSILE' && measurementForm.reportType !== 'CVN' && measurementForm.reportType !== 'AUT' && measurementForm.reportType !== 'HYDROSTATIC' && measurementForm.reportType !== 'DIMENSIONAL' && measurementForm.reportType !== 'FLATTENING' && measurementForm.reportType !== 'BEND' && measurementForm.reportType !== 'HARDNESS' && measurementForm.reportType !== 'VISUAL' && measurementForm.reportType !== 'RT' && measurementForm.reportType !== 'MPT' && measurementForm.reportType !== 'HEAT_TREAT' && measurementForm.reportType !== 'PIPE_BODY_UT' && measurementForm.reportType !== 'MFL_ET' && measurementForm.reportType !== 'COATING' && measurementForm.reportType !== 'THREAD' && measurementForm.reportType !== 'WELD_MAP' && (
              <>
                <div className={cn('grid grid-cols-1 gap-4', measurementForm.reportType === 'CHEMICAL' ? 'sm:grid-cols-2' : 'sm:grid-cols-3')}>
                  <Field label="Measured Value">
                    <input value={measurementForm.measuredValue} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, measuredValue: event.target.value }))} className={inputClassName} />
                  </Field>
                  <Field label="Unit">
                    <input value={measurementForm.unit} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, unit: event.target.value }))} className={inputClassName} />
                  </Field>
                  {measurementForm.reportType !== 'CHEMICAL' && (
                    <Field label="Status">
                      <select value={measurementForm.status} onChange={(event) => setMeasurementForm((previous) => ({ ...previous, status: event.target.value }))} className={inputClassName}>
                        <option value="PASS">PASS</option>
                        <option value="PENDING_REVIEW">PENDING_REVIEW</option>
                        <option value="FAIL">FAIL</option>
                        <option value="INCOMPLETE">INCOMPLETE</option>
                      </select>
                    </Field>
                  )}
                </div>

                <Field label="Limit / Expected">
                  <input
                    value={measurementForm.limit}
                    onChange={(event) => setMeasurementForm((previous) => ({ ...previous, limit: event.target.value }))}
                    disabled={measurementForm.reportType === 'CHEMICAL'}
                    className={cn(inputClassName, measurementForm.reportType === 'CHEMICAL' && 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')}
                  />
                </Field>
                <Field label="Source / Reference">
                  <input
                    value={measurementForm.reference}
                    onChange={(event) => setMeasurementForm((previous) => ({ ...previous, reference: event.target.value }))}
                    disabled={measurementForm.reportType === 'CHEMICAL'}
                    className={cn(inputClassName, measurementForm.reportType === 'CHEMICAL' && 'cursor-not-allowed bg-[#EEF0F3] text-heritage-grey')}
                  />
                </Field>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setIsMeasurementModalOpen(false)}>Cancel</Button>
            <Button type="button" onClick={saveMeasurement} className="bg-hero-red text-white hover:bg-hero-red-60">
              {editingMeasurementId ? 'Save Item' : 'Add Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isFieldOptionModalOpen} onOpenChange={setIsFieldOptionModalOpen}>
        <DialogContent className="max-w-xl p-0 gap-0 bg-page-bg [&>button]:hidden">
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 py-4">
            <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue">
              Add Project Limit Field
            </DialogTitle>
            <p className="mt-1 text-[12px] text-heritage-grey">
              Add a field to the {measurementForm.reportType} dropdown. Use symbol for chemistry fields, for example P - Phosphorus.
            </p>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
            <Field label="Symbol">
              <input
                value={fieldOptionForm.symbol || ''}
                onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, symbol: event.target.value }))}
                placeholder="Example: P"
                className={inputClassName}
              />
            </Field>
            <Field label="Field Name">
              <input
                value={fieldOptionForm.label}
                onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, label: event.target.value }))}
                placeholder="Example: Phosphorus"
                className={inputClassName}
              />
            </Field>
            <Field label="Field Path">
              <input
                value={fieldOptionForm.fieldPath}
                onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, fieldPath: event.target.value }))}
                placeholder="Auto: elements.P"
                className={inputClassName}
              />
            </Field>
            <Field label="Unit">
              <input
                value={fieldOptionForm.unit}
                onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, unit: event.target.value }))}
                placeholder="Example: %"
                className={inputClassName}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Limit / Expected">
                <input
                  value={fieldOptionForm.limit}
                  onChange={(event) => setFieldOptionForm((previous) => ({ ...previous, limit: event.target.value }))}
                  placeholder="Example: <= 0.020 %"
                  className={inputClassName}
                />
              </Field>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-[#E2E5EA] bg-white px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setIsFieldOptionModalOpen(false)}>Cancel</Button>
            <Button type="button" onClick={saveFieldOption} className="bg-hero-red text-white hover:bg-hero-red-60">
              Add Field
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmationDialog
        isOpen={Boolean(measurementToDelete)}
        onClose={() => setMeasurementToDelete(null)}
        onConfirm={confirmDeleteMeasurement}
        itemToDelete={measurementToDelete}
        itemType="Report Item"
        itemName={measurementToDelete ? `${measurementToDelete.reportType} - ${measurementToDelete.fieldLabel}` : ''}
        title="Delete Report Item"
        warningMessage="This report item will be removed from the pipe mill report and saved to the database."
      />
    </>
  );
};

const ReportDetailModal: React.FC<{
  reportType: FullReportType | null;
  onOpenChange: (open: boolean) => void;
  pipeNumber: string;
  pipeNri: string;
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  frequency?: string;
  projectLimitFields?: ProjectLimitFieldOption[];
  measurements: MeasurementItem[];
  deletedMeasurementIds: string[];
  onAddMeasurement: (reportType: FullReportType) => void;
  onEditMeasurement: (measurement: MeasurementItem) => void;
  onDeleteMeasurement: (measurement: MeasurementItem) => void;
  selectedMaterial: MaterialData;
  reportStatus?: string;
  reportNote?: string;
  isSavingAdjudication?: boolean;
  onAdjudicate: (reportType: FullReportType, request: { status: string; action: string; note?: string }) => void;
  onImportMeasurements: (reportType: FullReportType, measurements: MeasurementItem[]) => void;
}> = ({
  reportType,
  onOpenChange,
  pipeNumber,
  pipeNri,
  heatNumber,
  metadata,
  frequency,
  projectLimitFields,
  measurements,
  deletedMeasurementIds,
  onAddMeasurement,
  onEditMeasurement,
  onDeleteMeasurement,
  selectedMaterial,
  reportStatus,
  reportNote,
  isSavingAdjudication,
  onAdjudicate,
  onImportMeasurements,
}) => {
  const reportDefinition = REPORT_TYPES.find((report) => report.key === reportType);
  const report = reportType ? MOCK_REPORTS[reportType] : undefined;
  const materialDetails = selectedMaterial?.details || {};
  const pipeLengthM = useMemo(
    () => getMaterialPipeLengthM(materialDetails.pipeLength),
    [materialDetails.pipeLength],
  );
  const materialPdfDocument = useMemo(() => getFirstMaterialPdfDocument(selectedMaterial), [selectedMaterial]);
  const [pdfTarget, setPdfTarget] = useState<PdfTarget | null>(null);
  const [pdfWidthPercent, setPdfWidthPercent] = useState(55);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const openMaterialPdf = () => {
    if (!materialPdfDocument) return;
    setPdfTarget({
      reportId: Number(materialPdfDocument.docRef) || 0,
      weldId: pipeNumber,
      reportNo: materialPdfDocument.fileName,
      rev: null,
      reportDate: materialPdfDocument.uploadedAt || null,
    });
  };

  const downloadTemplate = () => {
    if (!reportType) return;
    downloadReportExcelTemplate(reportType, reportDefinition?.label || reportType, projectLimitFields, measurements);
  };

  const importExcelFile = async (file?: File | null) => {
    if (!file || !reportType) return;
    try {
      const importedItems = await parseReportExcelItems(file, reportType, metadata?.frequency || frequency, projectLimitFields);
      if (!importedItems.length) {
        window.alert('No report items found in the Excel file. Fill the Measured Value column and upload again.');
        return;
      }
      onImportMeasurements(reportType, importedItems);
    } catch (error) {
      window.alert('Unable to read the Excel file. Please download the template and upload the filled .xlsx file.');
    }
  };

  return (
    <>
      <Dialog open={Boolean(reportType)} onOpenChange={onOpenChange}>
        <DialogContent
          onInteractOutside={(event) => {
            if ((event.target as HTMLElement).closest('[data-pdf-drawer="true"]')) {
              event.preventDefault();
            }
          }}
          className="h-screen w-screen max-w-none max-h-none m-0 overflow-hidden rounded-none border-0 p-0 gap-0 bg-page-bg shadow-none [&>button]:hidden"
        >
          <DialogHeader className="border-b border-[#E2E5EA] bg-white px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-4">
                <Button onClick={() => onOpenChange(false)} variant="outline" className="h-9 w-9 shrink-0 rounded-lg border-[#E2E5EA] p-0">
                  <ArrowLeft className="h-5 w-5 text-text-grey" />
                </Button>
                <div>
                  <DialogTitle className="font-heading text-[20px] font-semibold text-blueprint-blue">
                    {reportDefinition?.label || 'Report Detail'}
                  </DialogTitle>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-heritage-grey">
                  <span className="font-mono text-blueprint-blue">{pipeNri}</span>
                  <span>-</span>
                  <span>Heat {heatNumber}</span>
                  <span>-</span>
                  <span className="font-mono">{reportType}</span>
                </div>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={openMaterialPdf}
                  disabled={!materialPdfDocument}
                  title={materialPdfDocument ? materialPdfDocument.fileName : 'No material PDF uploaded'}
                >
                  <FileText className="h-4 w-4" /> View PDF
                </Button>
                <Button type="button" onClick={downloadTemplate} disabled={!reportType}>
                  <Download className="h-4 w-4" /> Export 
                </Button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(event) => {
                    importExcelFile(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
                <Button
                  type="button"
                
                  onClick={() => uploadInputRef.current?.click()}
                  onDrop={(event) => {
                    event.preventDefault();
                    importExcelFile(event.dataTransfer.files?.[0]);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  title="Upload or drop the filled Excel template"
                >
                  <Upload className="h-4 w-4" /> import
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="h-[calc(100vh-105px)] overflow-y-auto">
            {reportType && (
              <>
                <ReportAdjudicationContext.Provider
                  value={{
                    reportType,
                    status: reportStatus || report?.status,
                    note: reportNote,
                    isSaving: isSavingAdjudication,
                    onAdjudicate: (request) => onAdjudicate(reportType, request),
                  }}
                >
                  <ReportDetail
                    reportType={reportType}
                    heatNumber={heatNumber}
                    pipeLengthM={pipeLengthM}
                    metadata={metadata}
                    frequency={frequency}
                    projectLimitFields={projectLimitFields}
                    measurements={measurements}
                    deletedMeasurementIds={deletedMeasurementIds}
                    onAddMeasurement={onAddMeasurement}
                    onEditMeasurement={onEditMeasurement}
                    onDeleteMeasurement={onDeleteMeasurement}
                    workflowPanel={(
                      <ReportWorkflowPanel
                        reportType={reportType}
                        metadata={metadata}
                        frequency={frequency}
                        measurements={measurements}
                        onAddMeasurement={onAddMeasurement}
                        onEditMeasurement={onEditMeasurement}
                        onDeleteMeasurement={onDeleteMeasurement}
                      />
                    )}
                  />
                </ReportAdjudicationContext.Provider>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {materialPdfDocument && (
        <PdfDrawer
          target={pdfTarget}
          onClose={() => setPdfTarget(null)}
          formatDate={formatDisplayDate}
          widthPercent={pdfWidthPercent}
          onWidthChange={setPdfWidthPercent}
          fileUrl={materialPdfDocument.fileUrl}
        />
      )}
    </>
  );
};

const ReportWorkflowPanel: React.FC<{
  reportType: FullReportType;
  metadata?: ReportMetadataEntry;
  frequency?: string;
  measurements: MeasurementItem[];
  onAddMeasurement: (reportType: FullReportType) => void;
  onEditMeasurement: (measurement: MeasurementItem) => void;
  onDeleteMeasurement: (measurement: MeasurementItem) => void;
}> = ({ reportType, metadata, frequency, measurements, onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => (
  <div className="border-t border-[#E2E5EA] bg-page-bg p-6">
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <Card className="border-[#E2E5EA]">
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b border-[#E2E5EA] px-4 py-3">
            <div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Report Items</h3>
              <p className="mt-1 text-[12px] text-heritage-grey">
                Add or edit measured items using fields from project limits. Frequency: {metadata?.frequency || frequency || 'per_pipe'}.
              </p>
            </div>
            <Button type="button" size="sm" onClick={() => onAddMeasurement(reportType)} className="bg-hero-red text-white hover:bg-hero-red-60">
              <Plus className="mr-2 h-4 w-4" />
              Add Item
            </Button>
          </div>

          {measurements.length === 0 ? (
            <div className="p-4 text-[13px] text-heritage-grey">
              No report items added yet. Click Add Item to capture a measured value for this report.
            </div>
          ) : (
            <table className="w-full table-fixed text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="w-[190px] px-4 py-3">Item / Field</th>
                  <th className="w-[140px] px-4 py-3 text-right">Measured</th>
                  <th className="w-[170px] px-4 py-3">Limit</th>
                  <th className="w-[130px] px-4 py-3">Status</th>
                  <th className="w-[90px] px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {measurements.map((measurement) => (
                  <tr key={measurement.id}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-blueprint-blue">
                        {measurement.fieldSymbol ? `${measurement.fieldSymbol} - ${measurement.fieldLabel}` : measurement.fieldLabel}
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-heritage-grey">{measurement.fieldPath}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {measurement.measuredValue}
                      {measurement.unit && <span className="ml-1 text-[11px] text-heritage-grey">{measurement.unit}</span>}
                    </td>
                    <td className="px-4 py-3 text-heritage-grey">{measurement.limit}</td>
                    <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button type="button" variant="outline" size="sm" title="Edit" onClick={() => onEditMeasurement(measurement)} className="h-8 w-8 p-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button type="button" variant="outline" size="sm" title="Delete" onClick={() => onDeleteMeasurement(measurement)} className="h-8 w-8 p-0 border-readable-red/25 text-readable-red hover:bg-[rgba(196,0,31,0.06)]">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="self-start border-[#E2E5EA]">
        <CardContent className="p-0">
          <div className="border-b border-[#E2E5EA] px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
            Report Metadata
          </div>
          <div className="p-4">
            <MetadataRow label="Report ID" value={metadata?.reportId || '-'} mono />
            <MetadataRow label="Frequency" value={metadata?.frequency || frequency || '-'} mono />
            <MetadataRow label="Performed" value={formatDisplayDate(metadata?.performedAt)} mono />
            <MetadataRow label="Source" value={metadata?.source || '-'} />
            <MetadataRow label="Lab" value={metadata?.lab || '-'} />
            <MetadataRow label="Technician" value={metadata?.technician || '-'} />
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const MetadataRow: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="grid grid-cols-[105px_minmax(0,1fr)] gap-3 border-b border-dashed border-[#E2E5EA] py-2 first:pt-0 last:border-b-0 last:pb-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
    <div className={cn('min-w-0 break-words text-right text-[12px] font-medium text-text-grey', mono && 'font-mono')}>
      {value}
    </div>
  </div>
);

const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;

